import React, {
  useState,
  useRef,
  useEffect,
  useMemo,
  useCallback,
} from "react";
import {
  User,
  Calendar,
  Keyboard,
  School,
  Heart,
  Plus,
  ChevronRight,
  ChevronLeft,
  Trash2,
  Upload,
  FileText,
  Camera,
  CheckSquare,
  AlertCircle,
  ArrowLeft,
  Clock,
  MapPin,
  Globe,
  ChevronDown,
  CheckCircle,
  Check,
} from "lucide-react";
import { Button, Card, Input, InputLabel, Modal, Select } from "./DesignSystem";
import { HebrewKeyboard, HebrewDatePicker } from "./HebrewWidgets";
import {
  buildAdmissionsIntakeAutofillDraft,
  shouldRenderAdmissionsIntakeAutofillFab,
} from "./intakeAutofill";
import {
  getApplicationsIntakeActionState,
  getApplicationsIntakeGradeDiscrepancyWarnings,
  getApplicationsIntakeSubmissionIssues,
  shouldShowFooterNavigation,
  type ApplicationsIntakeAdvanceIssue,
  type ApplicationsIntakeRequiredDocumentUploadLike,
  type ApplicationsIntakeStep,
  type ApplicationsIntakeValidationInput,
} from "./intakeValidation";
import {
  APPLICATION_GRADE_OPTIONS,
  CURRENT_GRADE_OPTIONS,
  getDefaultAdmissionsCalendarDate,
  isSameAdmissionsCalendarMonth,
  isSelectableAdmissionsScheduleDate,
  navigateAdmissionsCalendarMonth,
} from "./intakeSchedule";
import {
  domainRouterApi,
  useGetAdmissionsApplicationQuery,
  useGetApplicantApplicationsQuery,
  useGetParentAdmissionsDraftQuery,
  useUpsertParentAdmissionsDraftMutation,
  useDeleteParentAdmissionsDraftMutation,
} from "../../../state/domainRouterApi";
import { EngineeringSupportBanner } from "../../../components/overlays/EngineeringSupportBanner";
import { readDomainRouterErrorMessage } from "../../../state/domainRouterError";
import { useAppDispatch } from "../../../state/hooks";
import { useShellProfile } from "../../../state/useShellProfile";
import { isDevRuntimeBuild } from "../../../config/devRuntimeFlags";
import { ensurePersonaTokenClaim } from "../../../auth/personaProvisioningRuntime";
import { SubmissionError, useSubmitApplication } from "@triedge/bridge/hooks";
import { uploadData } from "aws-amplify/storage";
import { computeHebrewDate } from "@triedge/domain-core";
import {
  hasUnsavedFormData,
  useUnsavedBrowserNavigationGuard,
} from "../../../shared/forms/useUnsavedBrowserNavigationGuard";
import {
  getValidationIndicatorClassName,
  getValidationIndicatorState,
  isValidEmailAddress,
  isValidPhoneNumber,
  normalizePhoneForSubmission,
  normalizePhoneInput,
  pickFirstValidEmail,
  pickFirstValidPhone,
} from "../../shared/contactValidation";
import {
  buildAdmissionsIntakeRequiredUploads,
  getAdmissionsAgreementRows,
  getAdmissionsDocumentRequirementRows,
  isAdmissionsDocumentRowVisibleForStudent,
  resolveAdmissionsDocumentUploadFileName,
  type AdmissionDocumentRequirementRow,
} from "../../../settings/admissionsRequirements";
import {
  getPublicApplySettings,
  type PublicApplySettings,
} from "../../../settings/publicSettingsClient";
import {
  DEFAULT_PARENT_GUARDIAN_HOUSEHOLD_COMPOSITION,
  isDefaultParentGuardianHouseholdComposition,
  normalizeParentGuardianHouseholdComposition,
  resolveParentGuardianHouseholdComposition,
  type ParentGuardianFormKey,
  type ParentGuardianHouseholdComposition,
} from "../../../shared/parentGuardianHousehold";

// --- Constants ---
const USA_COUNTRY_LABEL = "United States of America";
type ParentSettingsLoadStatus = "loading" | "ready" | "error";

const US_STATE_OPTIONS = [
  { label: "Alabama", value: "Alabama", abbreviation: "AL" },
  { label: "Alaska", value: "Alaska", abbreviation: "AK" },
  { label: "Arizona", value: "Arizona", abbreviation: "AZ" },
  { label: "Arkansas", value: "Arkansas", abbreviation: "AR" },
  { label: "California", value: "California", abbreviation: "CA" },
  { label: "Colorado", value: "Colorado", abbreviation: "CO" },
  { label: "Connecticut", value: "Connecticut", abbreviation: "CT" },
  { label: "Delaware", value: "Delaware", abbreviation: "DE" },
  { label: "Florida", value: "Florida", abbreviation: "FL" },
  { label: "Georgia", value: "Georgia", abbreviation: "GA" },
  { label: "Hawaii", value: "Hawaii", abbreviation: "HI" },
  { label: "Idaho", value: "Idaho", abbreviation: "ID" },
  { label: "Illinois", value: "Illinois", abbreviation: "IL" },
  { label: "Indiana", value: "Indiana", abbreviation: "IN" },
  { label: "Iowa", value: "Iowa", abbreviation: "IA" },
  { label: "Kansas", value: "Kansas", abbreviation: "KS" },
  { label: "Kentucky", value: "Kentucky", abbreviation: "KY" },
  { label: "Louisiana", value: "Louisiana", abbreviation: "LA" },
  { label: "Maine", value: "Maine", abbreviation: "ME" },
  { label: "Maryland", value: "Maryland", abbreviation: "MD" },
  { label: "Massachusetts", value: "Massachusetts", abbreviation: "MA" },
  { label: "Michigan", value: "Michigan", abbreviation: "MI" },
  { label: "Minnesota", value: "Minnesota", abbreviation: "MN" },
  { label: "Mississippi", value: "Mississippi", abbreviation: "MS" },
  { label: "Missouri", value: "Missouri", abbreviation: "MO" },
  { label: "Montana", value: "Montana", abbreviation: "MT" },
  { label: "Nebraska", value: "Nebraska", abbreviation: "NE" },
  { label: "Nevada", value: "Nevada", abbreviation: "NV" },
  { label: "New Hampshire", value: "New Hampshire", abbreviation: "NH" },
  { label: "New Jersey", value: "New Jersey", abbreviation: "NJ" },
  { label: "New Mexico", value: "New Mexico", abbreviation: "NM" },
  { label: "New York", value: "New York", abbreviation: "NY" },
  { label: "North Carolina", value: "North Carolina", abbreviation: "NC" },
  { label: "North Dakota", value: "North Dakota", abbreviation: "ND" },
  { label: "Ohio", value: "Ohio", abbreviation: "OH" },
  { label: "Oklahoma", value: "Oklahoma", abbreviation: "OK" },
  { label: "Oregon", value: "Oregon", abbreviation: "OR" },
  { label: "Pennsylvania", value: "Pennsylvania", abbreviation: "PA" },
  { label: "Rhode Island", value: "Rhode Island", abbreviation: "RI" },
  { label: "South Carolina", value: "South Carolina", abbreviation: "SC" },
  { label: "South Dakota", value: "South Dakota", abbreviation: "SD" },
  { label: "Tennessee", value: "Tennessee", abbreviation: "TN" },
  { label: "Texas", value: "Texas", abbreviation: "TX" },
  { label: "Utah", value: "Utah", abbreviation: "UT" },
  { label: "Vermont", value: "Vermont", abbreviation: "VT" },
  { label: "Virginia", value: "Virginia", abbreviation: "VA" },
  { label: "Washington", value: "Washington", abbreviation: "WA" },
  { label: "West Virginia", value: "West Virginia", abbreviation: "WV" },
  { label: "Wisconsin", value: "Wisconsin", abbreviation: "WI" },
  { label: "Wyoming", value: "Wyoming", abbreviation: "WY" },
] as const;

const US_STATES = US_STATE_OPTIONS.map((state) => state.value);

const COUNTRY_FALLBACK_OPTIONS = [
  USA_COUNTRY_LABEL,
  "Canada",
  "Israel",
  "United Kingdom",
  "Australia",
  "France",
  "Mexico",
  "Brazil",
  "Argentina",
  "South Africa",
];

function buildCountryOptions(): string[] {
  const supportedValuesOf = (
    Intl as typeof Intl & {
      supportedValuesOf?: (key: string) => string[];
    }
  ).supportedValuesOf;

  if (
    typeof Intl === "undefined" ||
    typeof Intl.DisplayNames !== "function" ||
    typeof supportedValuesOf !== "function"
  ) {
    return COUNTRY_FALLBACK_OPTIONS;
  }

  try {
    const displayNames = new Intl.DisplayNames(["en"], { type: "region" });
    const countryNames = supportedValuesOf("region")
      .filter((code: string) => /^[A-Z]{2}$/.test(code))
      .map((code: string) => displayNames.of(code))
      .filter((name: string | undefined): name is string => Boolean(name))
      .map((name: string) =>
        name === "United States" ? USA_COUNTRY_LABEL : name,
      )
      .filter((name: string) => name !== "European Union")
      .filter(
        (name: string, index: number, allNames: string[]) =>
          allNames.indexOf(name) === index,
      )
      .sort((left: string, right: string) => left.localeCompare(right, "en"));

    return [
      USA_COUNTRY_LABEL,
      ...countryNames.filter((name: string) => name !== USA_COUNTRY_LABEL),
    ];
  } catch {
    return COUNTRY_FALLBACK_OPTIONS;
  }
}

const COUNTRY_OPTIONS = buildCountryOptions();

const GRANDPARENT_MARITAL_STATUS_OPTIONS = [
  "Married",
  "Divorced",
  "Separated",
  "Widowed",
  "Deceased",
];

// --- Types ---

interface StudentData {
  id: string;
  firstName: string;
  middleName: string;
  lastName: string;
  hebrewFirst: string;
  hebrewMiddle: string;
  hebrewLast: string;
  calledBy: string;
  englishBirthDate: string;
  hebrewBirthDate: string;
  birthCountry: string;
  birthState: string;
  birthCity: string;
  language: string;
  applyingYear: string;
  grade: string;
  gender: string;
  isAdopted: boolean | null;
  isStepParent: boolean | null;
  currentSchool: string;
  currentGrade: string;
  schoolPrincipal: string;
  schoolPhone: string;
  pediatrician: string;
  pediatricianPhone: string;
  dentist: string;
  dentistPhone: string;
}

interface ParentData {
  title: string;
  name: string;
  maidenName?: string; // Only for mother
  cellPhone: string;
  email: string;
  occupation: string;
  firmName: string;
  hasSeparateAddress: boolean;
  isDeceasedOrNoLongerLegalGuardian: boolean;
  sepHomePhone: string;
  sepAddress: string;
  sepCity: string;
  sepState: string;
  sepZip: string;
}

interface FamilyData {
  surname: string;
  homePhone: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  father: ParentData;
  mother: ParentData;
  maritalStatus: string;
  shulAffiliation: string;
  rav: string;
  householdComposition: ParentGuardianHouseholdComposition;
}

interface GrandparentData {
  lastName: string;
  grandfatherTitle: string;
  grandfatherName: string;
  grandmotherTitle: string;
  grandmotherName: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  maritalStatus: string;
  phone: string;
  grandfatherEmail: string;
  grandmotherEmail: string;
}

interface DocumentsData {
  fatherPolicyAware: boolean;
  motherPolicyAware: boolean;
}

type StudentTextField = {
  [K in keyof StudentData]: StudentData[K] extends string ? K : never;
}[keyof StudentData];

type StudentSectionPrefillState = {
  currentSchooling: boolean;
  medicalContacts: boolean;
};

type DocumentUploadCategory =
  | "transcript"
  | "birth_certificate"
  | "immunization"
  | "other";

const createEmptyStudent = (): StudentData => ({
  id: crypto.randomUUID(),
  firstName: "",
  middleName: "",
  lastName: "",
  hebrewFirst: "",
  hebrewMiddle: "",
  hebrewLast: "",
  calledBy: "",
  englishBirthDate: "",
  hebrewBirthDate: "",
  birthCountry: USA_COUNTRY_LABEL,
  birthState: "",
  birthCity: "",
  language: "",
  applyingYear: "2026-27",
  grade: "",
  gender: "",
  isAdopted: null,
  isStepParent: null,
  currentSchool: "",
  currentGrade: "",
  schoolPrincipal: "",
  schoolPhone: "",
  pediatrician: "",
  pediatricianPhone: "",
  dentist: "",
  dentistPhone: "",
});

const initialParentData: ParentData = {
  title: "",
  name: "",
  cellPhone: "",
  email: "",
  occupation: "",
  firmName: "",
  hasSeparateAddress: false,
  isDeceasedOrNoLongerLegalGuardian: false,
  sepHomePhone: "",
  sepAddress: "",
  sepCity: "",
  sepState: "",
  sepZip: "",
};

const initialFamilyData: FamilyData = {
  surname: "",
  homePhone: "",
  address: "",
  city: "",
  state: "",
  zip: "",
  father: { ...initialParentData },
  mother: { ...initialParentData, maidenName: "" },
  maritalStatus: "",
  shulAffiliation: "",
  rav: "",
  householdComposition: DEFAULT_PARENT_GUARDIAN_HOUSEHOLD_COMPOSITION,
};

const initialGrandparentData: GrandparentData = {
  lastName: "",
  grandfatherTitle: "",
  grandfatherName: "",
  grandmotherTitle: "",
  grandmotherName: "",
  address: "",
  city: "",
  state: "",
  zip: "",
  maritalStatus: "",
  phone: "",
  grandfatherEmail: "",
  grandmotherEmail: "",
};

type ScheduleFormData = {
  name: string;
  email: string;
  phone: string;
  notes: string;
};

type SubmissionCompletionMode = "scheduled" | "unscheduled";
type SubmissionFailurePhase = "application" | "schedule" | "documents";
type PendingSubmissionPhase = Exclude<SubmissionFailurePhase, "application">;

type SubmissionAlertTone = "error" | "warning";

type SubmissionAlertState = {
  tone: SubmissionAlertTone;
  title: string;
  detail: string;
  applicationId: string | null;
  applicationCode: string | null;
  retryPhase: PendingSubmissionPhase | null;
};

type PendingSubmissionState = {
  application: Record<string, unknown>;
  completionMode: SubmissionCompletionMode;
  scheduledSlots: number[];
  pendingPhase: PendingSubmissionPhase;
};

const createDefaultCalendarDate = (): Date => getDefaultAdmissionsCalendarDate();

const createInitialParentData = (): ParentData => ({
  ...initialParentData,
});

const createInitialFamilyData = (): FamilyData => ({
  ...initialFamilyData,
  father: createInitialParentData(),
  mother: { ...createInitialParentData(), maidenName: "" },
});

const createInitialGrandparentData = (): GrandparentData => ({
  ...initialGrandparentData,
});

const createInitialDocumentsData = (): DocumentsData => ({
  fatherPolicyAware: false,
  motherPolicyAware: false,
});

const createDefaultSiblingPrefillState = (): StudentSectionPrefillState => ({
  currentSchooling: true,
  medicalContacts: true,
});

const createInitialScheduleFormData = (): ScheduleFormData => ({
  name: "",
  email: "",
  phone: "",
  notes: "",
});

const asRecord = (value: unknown): Record<string, unknown> =>
  value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};

const sanitizePhoneInput = (value: string): string =>
  normalizePhoneInput(value);

const asRecordArray = (value: unknown): Record<string, unknown>[] =>
  Array.isArray(value)
    ? value
        .filter(
          (entry) =>
            entry && typeof entry === "object" && !Array.isArray(entry),
        )
        .map((entry) => entry as Record<string, unknown>)
    : [];

const readText = (value: unknown): string => String(value ?? "").trim();
const GENERIC_SUBMISSION_ERROR_MESSAGE = "Submission failed";
const PARENT_ADMISSIONS_DRAFT_UNAVAILABLE_MESSAGE =
  "Draft saving is not available in this environment yet. You can continue this application in this session.";

const isParentAdmissionsDraftCapabilityUnavailable = (error: unknown): boolean => {
  const domainMessage = readDomainRouterErrorMessage(error, "");
  let rawMessage = "";
  try {
    rawMessage = JSON.stringify(error);
  } catch {
    rawMessage = "";
  }
  const message = `${domainMessage} ${rawMessage}`.toLowerCase();
  return (
    message.includes("unsupported field") &&
    (
      message.includes("getparentadmissionsdraft") ||
      message.includes("upsertparentadmissionsdraft") ||
      message.includes("deleteparentadmissionsdraft")
    )
  );
};

const buildSubmittedApplicationPath = (applicationId: string): string =>
  `/parent/portal--new/applications?applicationId=${encodeURIComponent(applicationId)}`;

const readSubmissionFailureReason = (
  error: unknown,
  fallback: string,
): string => {
  const directMessage = readDomainRouterErrorMessage(error, "");
  const nestedMessage =
    error instanceof SubmissionError
      ? readDomainRouterErrorMessage(error.originalError, "")
      : "";
  const message = [nestedMessage, directMessage].find(
    (candidate) =>
      candidate &&
      candidate !== GENERIC_SUBMISSION_ERROR_MESSAGE &&
      candidate !== "Unknown error",
  );
  return message || fallback;
};

const extractSubmittedApplicationContext = (
  submittedApplication: unknown,
): {
  application: Record<string, unknown>;
  applicationId: string | null;
  applicationCode: string | null;
} => {
  const application = asRecord(submittedApplication);
  const applicationId = readText(application.id) || null;
  const applicationCode = readText(application.code) || null;
  return {
    application,
    applicationId,
    applicationCode,
  };
};

const ACADEMIC_YEAR_FALLBACK = "2026-2027";

const normalizeAcademicYearForSubmission = (value: unknown): string => {
  const raw = readText(value).replace(/\s+/g, "");
  if (/^\d{4}-\d{4}$/.test(raw)) return raw;
  const compactYearMatch = raw.match(/^(\d{4})-(\d{2})$/);
  if (compactYearMatch) {
    const start = Number(compactYearMatch[1]);
    const endSuffix = Number(compactYearMatch[2]);
    const century = Math.floor(start / 100) * 100;
    const candidate = century + endSuffix;
    const end = candidate < start ? candidate + 100 : candidate;
    return `${start}-${end}`;
  }
  return ACADEMIC_YEAR_FALLBACK;
};

const normalizeZipForDomain = (value: unknown): string => {
  const digits = readText(value).replace(/[^\d]/g, "");
  if (digits.length >= 5) return digits.slice(0, 5);
  return "00000";
};

const splitFullName = (
  value: unknown,
): { firstName: string; lastName: string } => {
  const normalized = readText(value);
  if (!normalized) return { firstName: "", lastName: "" };
  const parts = normalized.split(/\s+/).filter(Boolean);
  if (parts.length === 1) return { firstName: parts[0], lastName: "" };
  return {
    firstName: parts[0],
    lastName: parts.slice(1).join(" "),
  };
};

const normalizePhoneOptional = (value: unknown): string | undefined => {
  return normalizePhoneForSubmission(value);
};

const normalizePhoneRequired = (value: unknown, label: string): string => {
  const normalized = normalizePhoneOptional(value);
  if (!normalized) {
    throw new Error(`${label} must include a valid phone number.`);
  }
  return normalized;
};

const normalizeZipRequired = (value: unknown, label: string): string => {
  const digits = readText(value).replace(/[^\d]/g, "");
  if (digits.length < 5) {
    throw new Error(`${label} is required.`);
  }
  return digits.slice(0, 5);
};

const normalizeComparableText = (value: unknown): string =>
  readText(value).toLowerCase().replace(/\./g, "").replace(/\s+/g, " ").trim();

const COUNTRY_LOOKUP = COUNTRY_OPTIONS.reduce<Map<string, string>>(
  (lookup, country) => {
    lookup.set(normalizeComparableText(country), country);
    return lookup;
  },
  new Map(),
);

COUNTRY_LOOKUP.set("united states", USA_COUNTRY_LABEL);
COUNTRY_LOOKUP.set("united states of america", USA_COUNTRY_LABEL);
COUNTRY_LOOKUP.set("usa", USA_COUNTRY_LABEL);
COUNTRY_LOOKUP.set("us", USA_COUNTRY_LABEL);
COUNTRY_LOOKUP.set("u s", USA_COUNTRY_LABEL);
COUNTRY_LOOKUP.set("u s a", USA_COUNTRY_LABEL);

const US_STATE_LOOKUP = US_STATE_OPTIONS.reduce<Map<string, string>>(
  (lookup, state) => {
    lookup.set(normalizeComparableText(state.value), state.value);
    lookup.set(normalizeComparableText(state.abbreviation), state.value);
    return lookup;
  },
  new Map(),
);

const resolveCountryValue = (value: unknown): string =>
  COUNTRY_LOOKUP.get(normalizeComparableText(value)) ?? "";

const resolveUsStateValue = (value: unknown): string =>
  US_STATE_LOOKUP.get(normalizeComparableText(value)) ?? "";

const isUnitedStatesCountry = (value: unknown): boolean =>
  resolveCountryValue(value) === USA_COUNTRY_LABEL;

const parseBirthPlace = (
  value: unknown,
): Pick<StudentData, "birthCountry" | "birthState" | "birthCity"> => {
  const raw = readText(value);
  if (!raw) {
    return {
      birthCountry: USA_COUNTRY_LABEL,
      birthState: "",
      birthCity: "",
    };
  }

  const parts = raw
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean);
  if (!parts.length) {
    return {
      birthCountry: USA_COUNTRY_LABEL,
      birthState: "",
      birthCity: "",
    };
  }

  const trailingCountry = resolveCountryValue(parts[parts.length - 1]);
  if (trailingCountry) {
    const remainingParts = parts.slice(0, -1);
    if (trailingCountry !== USA_COUNTRY_LABEL) {
      return {
        birthCountry: trailingCountry,
        birthState: "",
        birthCity: remainingParts.join(", "),
      };
    }

    const trailingState = resolveUsStateValue(
      remainingParts[remainingParts.length - 1],
    );
    return {
      birthCountry: USA_COUNTRY_LABEL,
      birthState: trailingState,
      birthCity: trailingState
        ? remainingParts.slice(0, -1).join(", ")
        : remainingParts.join(", "),
    };
  }

  const trailingState = resolveUsStateValue(parts[parts.length - 1]);
  if (trailingState) {
    return {
      birthCountry: USA_COUNTRY_LABEL,
      birthState: trailingState,
      birthCity: parts.slice(0, -1).join(", "),
    };
  }

  return {
    birthCountry: USA_COUNTRY_LABEL,
    birthState: "",
    birthCity: parts.join(", "),
  };
};

const formatBirthPlaceForSubmission = (
  student: Pick<StudentData, "birthCountry" | "birthState" | "birthCity">,
): string | undefined => {
  const country =
    resolveCountryValue(student.birthCountry) || USA_COUNTRY_LABEL;
  const city = readText(student.birthCity);
  const state =
    country === USA_COUNTRY_LABEL
      ? resolveUsStateValue(student.birthState) || readText(student.birthState)
      : "";

  if (!city && !state && country === USA_COUNTRY_LABEL) {
    return undefined;
  }

  return [city, state, country].filter(Boolean).join(", ");
};

const buildGrandparentPayload = (
  data: GrandparentData,
):
  | {
      names?: string;
      location?: string;
      phone?: string;
      family?: string;
    }
  | undefined => {
  const grandfatherFull = [
    readText(data.grandfatherTitle),
    readText(data.grandfatherName),
  ]
    .filter(Boolean)
    .join(" ");
  const grandmotherFull = [
    readText(data.grandmotherTitle),
    readText(data.grandmotherName),
  ]
    .filter(Boolean)
    .join(" ");
  const names = [grandfatherFull, grandmotherFull].filter(Boolean).join(" & ");
  const city = readText(data.city);
  const state = readText(data.state);
  const zip = readText(data.zip);
  const location = [city, state, zip].filter(Boolean).join(", ");
  const phone = normalizePhoneOptional(data.phone);
  const family = readText(data.lastName) || undefined;
  if (!names && !location && !phone && !family) return undefined;
  return {
    names: names || undefined,
    location: location || undefined,
    phone,
    family,
  };
};

const toUnixSecondsFromDateInput = (value: unknown): number => {
  const normalized = readText(value);
  if (!normalized) return Math.floor(Date.now() / 1000);
  const date = new Date(normalized);
  if (Number.isNaN(date.getTime())) return Math.floor(Date.now() / 1000);
  return Math.floor(date.getTime() / 1000);
};

const resolveStudentGenderToken = (
  value: unknown,
): "male" | "female" | "other" | undefined => {
  const normalized = readText(value).toLowerCase();
  if (normalized === "male" || normalized === "m") return "male";
  if (normalized === "female" || normalized === "f") return "female";
  if (normalized === "other") return "other";
  return undefined;
};

const resolveGradeLevelToken = (value: unknown): string => {
  const normalized = readText(value).toLowerCase();
  if (!normalized) return "grade_1";
  if (["nursery", "nur"].includes(normalized)) return "nursery";
  if (["pre-k", "prek", "pre_k", "prekindergarten"].includes(normalized))
    return "pre_k";
  if (["kindergarten", "k", "kg"].includes(normalized)) return "kindergarten";
  if (["pre-1a", "pre1a", "pre1", "pre-1"].includes(normalized))
    return "grade_1";
  const explicit = normalized.match(/grade[_\s-]?([1-8])/);
  if (explicit) return `grade_${explicit[1]}`;
  const numberMatch = normalized.match(/([1-8])/);
  if (numberMatch) return `grade_${numberMatch[1]}`;
  return "grade_1";
};

const resolveSchoolDivisionToken = (
  gradeLevel: string,
  gender: "male" | "female" | "other" | undefined,
): "boys" | "girls" | "preschool" => {
  if (["nursery", "pre_k", "kindergarten"].includes(gradeLevel))
    return "preschool";
  if (gender === "female") return "girls";
  return "boys";
};

const toUnixSeconds = (value: unknown): number | null => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed <= 0) return null;
  return parsed;
};

const toDateInputValue = (value: unknown): string => {
  const unixSeconds = toUnixSeconds(value);
  if (unixSeconds == null) return "";
  const date = new Date(unixSeconds * 1000);
  if (Number.isNaN(date.getTime())) return "";
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, "0");
  const day = String(date.getUTCDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const normalizeDateValue = (value: string): string | null => {
  const normalized = value.trim();
  if (!normalized) return null;

  const isoDateMatch = normalized.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (isoDateMatch) {
    const [, year, month, day] = isoDateMatch;
    const yearValue = Number(year);
    const monthValue = Number(month);
    const dayValue = Number(day);
    if (
      !Number.isFinite(yearValue) ||
      !Number.isFinite(monthValue) ||
      !Number.isFinite(dayValue)
    )
      return null;

    const parsedDate = new Date(yearValue, monthValue - 1, dayValue);
    if (
      parsedDate.getFullYear() !== yearValue ||
      parsedDate.getMonth() + 1 !== monthValue ||
      parsedDate.getDate() !== dayValue
    ) {
      return null;
    }

    return `${year}-${String(monthValue).padStart(2, "0")}-${String(dayValue).padStart(2, "0")}`;
  }

  const englishDateMatch = normalized.match(
    /^(\d{1,2})[\/.-](\d{1,2})[\/.-](\d{4})$/,
  );
  if (!englishDateMatch) return null;
  const [, month, day, year] = englishDateMatch;
  const monthValue = Number(month);
  const dayValue = Number(day);
  const yearValue = Number(year);
  if (
    !Number.isFinite(monthValue) ||
    !Number.isFinite(dayValue) ||
    !Number.isFinite(yearValue)
  )
    return null;

  const parsedDate = new Date(yearValue, monthValue - 1, dayValue);
  if (
    parsedDate.getFullYear() !== yearValue ||
    parsedDate.getMonth() + 1 !== monthValue ||
    parsedDate.getDate() !== dayValue
  ) {
    return null;
  }

  return `${yearValue}-${String(monthValue).padStart(2, "0")}-${String(dayValue).padStart(2, "0")}`;
};

const parseEnglishBirthDateToHebrew = (value: string): string | null => {
  const isoDate = normalizeDateValue(value);
  if (!isoDate) return null;

  const [year, month, day] = isoDate.split("-");
  const yearValue = Number(year);
  const monthValue = Number(month);
  const dayValue = Number(day);
  if (
    !Number.isFinite(yearValue) ||
    !Number.isFinite(monthValue) ||
    !Number.isFinite(dayValue)
  )
    return null;

  try {
    const hebrewDate = computeHebrewDate(
      new Date(yearValue, monthValue - 1, dayValue),
      false,
    );
    return hebrewDate?.hebrewDateEnglish || null;
  } catch {
    return null;
  }
};

const normalizeAcademicYear = (value: unknown): string => {
  const raw = readText(value).replace(/\s+/g, "");
  if (!raw) return "2026-27";
  if (/^\d{4}-\d{4}$/.test(raw)) return `${raw.slice(0, 4)}-${raw.slice(7)}`;
  return raw;
};

const normalizeGender = (value: unknown): string => {
  const raw = readText(value).toLowerCase();
  if (raw === "male" || raw === "m") return "Male";
  if (raw === "female" || raw === "f") return "Female";
  return readText(value);
};

const toNullableBoolean = (value: unknown): boolean | null => {
  if (typeof value === "boolean") return value;
  const normalized = readText(value).toLowerCase();
  if (["yes", "true", "1"].includes(normalized)) return true;
  if (["no", "false", "0"].includes(normalized)) return false;
  return null;
};

const joinName = (...parts: unknown[]): string =>
  parts
    .map((part) => readText(part))
    .filter(Boolean)
    .join(" ")
    .trim();

const toRelationToken = (value: unknown): string =>
  readText(value)
    .toLowerCase()
    .replace(/[\s_-]+/g, "");

const pickParentRecord = (
  parentRecords: Record<string, unknown>[],
  relationToken: "father" | "mother",
): Record<string, unknown> => {
  const byRelation = parentRecords.find((parent) =>
    toRelationToken(parent.relation).includes(relationToken),
  );
  if (byRelation) return byRelation;
  const hasRelationshipMarkers = parentRecords.some((parent) =>
    Boolean(toRelationToken(parent.relation)),
  );
  if (hasRelationshipMarkers) return {};
  const fallbackIndex = relationToken === "father" ? 0 : 1;
  return parentRecords[fallbackIndex] ?? {};
};

const hasParentRecordInput = (parent: Record<string, unknown>): boolean =>
  [
    parent.title,
    parent.firstName,
    parent.lastName,
    parent.email,
    parent.phone,
    parent.cellPhone,
    parent.occupation,
    parent.firmName,
  ].some((value) => Boolean(readText(value)));

const displayRoleFromParentRecord = (
  parent: Record<string, unknown>,
  fallback: ParentGuardianFormKey,
): "father" | "mother" | "grandfather" | "grandmother" | "legal_guardian" => {
  const relation = toRelationToken(parent.relation);
  if (relation.includes("grandfather")) return "grandfather";
  if (relation.includes("grandmother")) return "grandmother";
  if (relation.includes("mother")) return "mother";
  if (relation.includes("father")) return "father";
  if (relation.includes("guardian")) return "legal_guardian";
  return fallback;
};

const resolveAdmissionsHouseholdCompositionFromApplicant = (
  applicant: Record<string, unknown>,
  fatherRecord: Record<string, unknown>,
  motherRecord: Record<string, unknown>,
): ParentGuardianHouseholdComposition => {
  const savedComposition = asRecord(applicant.householdComposition);
  if (readText(savedComposition.mode)) {
    return normalizeParentGuardianHouseholdComposition(savedComposition);
  }

  const hasFatherInput = hasParentRecordInput(fatherRecord);
  const hasMotherInput = hasParentRecordInput(motherRecord);
  if (hasFatherInput !== hasMotherInput) {
    const primaryKey: ParentGuardianFormKey = hasMotherInput ? "mother" : "father";
    const primaryRecord = primaryKey === "father" ? fatherRecord : motherRecord;
    return normalizeParentGuardianHouseholdComposition({
      mode: "single_parent_guardian",
      primaryKey,
      primaryRole: displayRoleFromParentRecord(primaryRecord, primaryKey),
    });
  }

  return DEFAULT_PARENT_GUARDIAN_HOUSEHOLD_COMPOSITION;
};

const mapParentRecordToData = (
  value: unknown,
  includeMaidenName: boolean,
): ParentData => {
  const record = asRecord(value);
  const sepHomePhone =
    readText(record.sepHomePhone) || readText(record.separateHomePhone);
  const sepAddress =
    readText(record.sepAddress) || readText(record.separateAddress);
  const sepCity = readText(record.sepCity) || readText(record.separateCity);
  const sepState = readText(record.sepState) || readText(record.separateState);
  const sepZip = readText(record.sepZip) || readText(record.separateZip);
  const hasSeparateAddress =
    toNullableBoolean(record.hasSeparateAddress) ??
    toNullableBoolean(record.livesAtSeparateAddress) ??
    Boolean(sepHomePhone || sepAddress || sepCity || sepState || sepZip);
  const isDeceasedOrNoLongerLegalGuardian =
    toNullableBoolean(record.isDeceasedOrNoLongerLegalGuardian) ??
    toNullableBoolean(record.deceasedOrNoLongerLegalGuardian) ??
    toNullableBoolean(record.noLongerLegalGuardian) ??
    false;

  return {
    title: readText(record.title),
    name: joinName(record.firstName, record.lastName),
    maidenName: includeMaidenName ? readText(record.maidenName) : undefined,
    cellPhone: readText(record.phone),
    email: readText(record.email),
    occupation: readText(record.occupation),
    firmName: readText(record.firm),
    hasSeparateAddress,
    isDeceasedOrNoLongerLegalGuardian,
    sepHomePhone,
    sepAddress,
    sepCity,
    sepState,
    sepZip,
  };
};

const splitGrandparentNames = (
  value: unknown,
): { grandfatherName: string; grandmotherName: string } => {
  const normalized = readText(value).replace(/\s+and\s+/gi, " & ");
  if (!normalized) return { grandfatherName: "", grandmotherName: "" };
  const parts = normalized
    .split(/\s*&\s*/)
    .map((part) => part.trim())
    .filter(Boolean);
  return {
    grandfatherName: parts[0] ?? "",
    grandmotherName: parts[1] ?? "",
  };
};

const splitLocation = (
  value: unknown,
): { address: string; city: string; state: string; zip: string } => {
  const raw = readText(value);
  if (!raw) return { address: "", city: "", state: "", zip: "" };

  const parts = raw
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean);
  if (parts.length >= 3) {
    return {
      address: parts[0],
      city: parts[1],
      state: parts[2],
      zip: readText(parts[3]),
    };
  }

  if (parts.length === 2) {
    const stateWithZip = parts[1];
    const withZip = stateWithZip.match(/^([A-Za-z]{2})\s+(\d{5}(?:-\d{4})?)$/);
    if (withZip) {
      return {
        address: "",
        city: parts[0],
        state: withZip[1],
        zip: withZip[2],
      };
    }
    return {
      address: "",
      city: parts[0],
      state: stateWithZip,
      zip: "",
    };
  }

  return {
    address: raw,
    city: "",
    state: "",
    zip: "",
  };
};

const mapGrandparentRecord = (value: unknown): GrandparentData => {
  const record = asRecord(value);
  const names = splitGrandparentNames(record.names);
  const location = splitLocation(record.location);
  return {
    lastName: readText(record.family),
    grandfatherTitle: "",
    grandfatherName: names.grandfatherName,
    grandmotherTitle: "",
    grandmotherName: names.grandmotherName,
    address: location.address,
    city: location.city,
    state: location.state,
    zip: location.zip,
    maritalStatus: readText(record.maritalStatus),
    phone: readText(record.phone),
    grandfatherEmail: "",
    grandmotherEmail: "",
  };
};

type IntakeHydratedState = {
  students: StudentData[];
  hebrewBirthDateOverrides: Record<string, boolean>;
  hasSibling: boolean;
  family: FamilyData;
  paternalGrandparents: GrandparentData;
  maternalGrandparents: GrandparentData;
  scheduleForm: ScheduleFormData;
  interviewSlotsByStudentId: Record<string, number>;
  selectedDate: number | null;
  selectedTime: string | null;
  scheduleView: "calendar" | "form" | "success";
  currentCalendarDate: Date;
};

const FULL_APPLICATION_STEPS = [
  "Students",
  "Parents",
  "Extended Family",
  "Documents",
  "Review",
  "Schedule",
] as const;
const SIBLING_APPLICATION_STEPS = [
  "Students",
  "Documents",
  "Review",
  "Schedule",
] as const;

const getApplicationSteps = (
  hasSibling: boolean | null,
  familySelfSchedulingEnabled: boolean,
): string[] => {
  const baseSteps = hasSibling
    ? [...SIBLING_APPLICATION_STEPS]
    : [...FULL_APPLICATION_STEPS];
  return familySelfSchedulingEnabled
    ? baseSteps
    : baseSteps.filter((step) => step !== "Schedule");
};

type ParentAdmissionsDraftFormState = {
  students: StudentData[];
  hebrewBirthDateOverrides: Record<string, boolean>;
  studentSectionPrefill: Record<string, StudentSectionPrefillState>;
  hasSibling: boolean | null;
  family: FamilyData;
  paternalGrandparents: GrandparentData;
  maternalGrandparents: GrandparentData;
  documents: DocumentsData;
  documentUploads: Record<string, string>;
  consentChecked: boolean;
  scheduleView: "calendar" | "form" | "success";
  selectedDate: number | null;
  selectedTime: string | null;
  scheduleForm: ScheduleFormData;
  scheduleStudentIndex: number;
  interviewSlotsByStudentId: Record<string, number>;
  currentCalendarDate: string;
  selectedTimezone: string;
};

type ParentAdmissionsDraftScheduleState = Pick<
  ParentAdmissionsDraftFormState,
  | "scheduleView"
  | "selectedDate"
  | "selectedTime"
  | "scheduleForm"
  | "scheduleStudentIndex"
  | "interviewSlotsByStudentId"
  | "currentCalendarDate"
  | "selectedTimezone"
>;

type DraftSaveReason = "autosave" | "manual" | "submit" | "discard";
type DraftFeedbackState = {
  state: "idle" | "hydrated" | "saving" | "saved" | "error" | "discarding";
  message: string;
};

const clampDraftStep = (
  value: unknown,
  hasSibling: boolean | null,
  familySelfSchedulingEnabled = true,
): number => {
  const numeric = Number(value);
  const maxStep = getApplicationSteps(
    hasSibling,
    familySelfSchedulingEnabled,
  ).length - 1;
  if (!Number.isFinite(numeric)) {
    return 0;
  }
  return Math.max(0, Math.min(maxStep, Math.trunc(numeric)));
};

const readStringRecord = (value: unknown): Record<string, string> => {
  const record = asRecord(value);
  return Object.fromEntries(
    Object.entries(record)
      .map(([key, candidate]) => [key, readText(candidate)] as const)
      .filter(([, candidate]) => Boolean(candidate)),
  );
};

const readNumberRecord = (value: unknown): Record<string, number> => {
  const record = asRecord(value);
  return Object.fromEntries(
    Object.entries(record)
      .map(([key, candidate]) => [key, Number(candidate)] as const)
      .filter(([, candidate]) => Number.isFinite(candidate)),
  );
};

const readBooleanRecord = (value: unknown): Record<string, boolean> => {
  const record = asRecord(value);
  return Object.fromEntries(
    Object.entries(record)
      .filter(([, candidate]) => typeof candidate === "boolean")
      .map(([key, candidate]) => [key, candidate as boolean]),
  );
};

const readSiblingPrefillRecord = (
  value: unknown,
): Record<string, StudentSectionPrefillState> => {
  const record = asRecord(value);
  return Object.fromEntries(
    Object.entries(record).map(([studentId, candidate]) => {
      const prefill = asRecord(candidate);
      return [
        studentId,
        {
          currentSchooling: prefill.currentSchooling !== false,
          medicalContacts: prefill.medicalContacts !== false,
        },
      ];
    }),
  );
};

const readDraftStudents = (value: unknown): StudentData[] => {
  const entries = Array.isArray(value) ? value : [];
  const students = entries.map((candidate) => ({
    ...createEmptyStudent(),
    ...asRecord(candidate),
    id: readText(asRecord(candidate).id) || crypto.randomUUID(),
  })) as StudentData[];
  return students.length ? students : [createEmptyStudent()];
};

const readDateFromDraft = (value: unknown): Date => {
  const candidate = new Date(readText(value));
  return Number.isNaN(candidate.getTime())
    ? createDefaultCalendarDate()
    : candidate;
};

const readParentAdmissionsDraftFormState = (
  value: unknown,
): ParentAdmissionsDraftFormState => {
  const record = asRecord(value);
  const familyRecord = asRecord(record.family);
  const documentsRecord = asRecord(record.documents);
  const hasSibling =
    typeof record.hasSibling === "boolean" ? record.hasSibling : false;
  return {
    students: readDraftStudents(record.students),
    hebrewBirthDateOverrides: readBooleanRecord(
      record.hebrewBirthDateOverrides,
    ),
    studentSectionPrefill: readSiblingPrefillRecord(
      record.studentSectionPrefill,
    ),
    hasSibling,
    family: {
      ...createInitialFamilyData(),
      ...familyRecord,
      father: {
        ...createInitialParentData(),
        ...asRecord(familyRecord.father),
      },
      mother: {
        ...createInitialParentData(),
        maidenName: "",
        ...asRecord(familyRecord.mother),
      },
    },
    paternalGrandparents: {
      ...createInitialGrandparentData(),
      ...asRecord(record.paternalGrandparents),
    },
    maternalGrandparents: {
      ...createInitialGrandparentData(),
      ...asRecord(record.maternalGrandparents),
    },
    documents: {
      fatherPolicyAware: documentsRecord.fatherPolicyAware === true,
      motherPolicyAware: documentsRecord.motherPolicyAware === true,
    },
    documentUploads: readStringRecord(record.documentUploads),
    consentChecked: record.consentChecked === true,
    scheduleView:
      record.scheduleView === "form" || record.scheduleView === "success"
        ? record.scheduleView
        : "calendar",
    selectedDate:
      typeof record.selectedDate === "number" ? record.selectedDate : null,
    selectedTime: readText(record.selectedTime) || null,
    scheduleForm: {
      ...createInitialScheduleFormData(),
      ...asRecord(record.scheduleForm),
    },
    scheduleStudentIndex: Number.isFinite(Number(record.scheduleStudentIndex))
      ? Number(record.scheduleStudentIndex)
      : 0,
    interviewSlotsByStudentId: readNumberRecord(
      record.interviewSlotsByStudentId,
    ),
    currentCalendarDate: readDateFromDraft(record.currentCalendarDate)
      .toISOString(),
    selectedTimezone: readText(record.selectedTimezone) || "Eastern Time",
  };
};

const readParentAdmissionsDraftScheduleState = (
  formState: ParentAdmissionsDraftFormState,
): ParentAdmissionsDraftScheduleState => ({
  scheduleView: formState.scheduleView,
  selectedDate: formState.selectedDate,
  selectedTime: formState.selectedTime,
  scheduleForm: formState.scheduleForm,
  scheduleStudentIndex: formState.scheduleStudentIndex,
  interviewSlotsByStudentId: formState.interviewSlotsByStudentId,
  currentCalendarDate: formState.currentCalendarDate,
  selectedTimezone: formState.selectedTimezone,
});

const toScheduleTime = (date: Date): string =>
  date
    .toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    })
    .replace(/\s+/g, "")
    .toLowerCase();

const buildScheduledUnix = (
  day: number | null,
  time: string | null,
  calendarDate: Date,
): number | null => {
  if (day == null) return null;
  const date = new Date(
    calendarDate.getFullYear(),
    calendarDate.getMonth(),
    day,
  );
  if (Number.isNaN(date.getTime())) return null;

  const normalizedTime = readText(time).toLowerCase();
  if (!normalizedTime) {
    return Math.floor(date.getTime() / 1000);
  }

  const match = normalizedTime.match(/^(\d{1,2}):(\d{2})\s*(am|pm)?$/i);
  if (!match) return null;

  let hours = Number.parseInt(match[1], 10);
  const minutes = Number.parseInt(match[2], 10);
  const meridiem = readText(match[3]).toLowerCase();
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return null;
  if (meridiem === "pm" && hours < 12) hours += 12;
  if (meridiem === "am" && hours === 12) hours = 0;
  if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) return null;

  date.setHours(hours, minutes, 0, 0);
  return Math.floor(date.getTime() / 1000);
};

const mapAdmissionsApplicationToIntakeState = (
  value: unknown,
): IntakeHydratedState => {
  const application = asRecord(value);
  const applicant = asRecord(application.applicant);
  const primaryContact = asRecord(applicant.primaryContact);
  const parentRecords = asRecordArray(applicant.parents);
  const fatherRecord = pickParentRecord(parentRecords, "father");
  const motherRecord = pickParentRecord(parentRecords, "mother");
  const householdComposition = resolveAdmissionsHouseholdCompositionFromApplicant(
    applicant,
    fatherRecord,
    motherRecord,
  );

  const academicYear = normalizeAcademicYear(application.academicYear);
  const adoptedFlag = toNullableBoolean(applicant.adoptedChild);
  const stepParentFlag = toNullableBoolean(applicant.stepParent);
  const children = asRecordArray(application.children);

  const students: StudentData[] = [];
  const hebrewBirthDateOverrides: Record<string, boolean> = {};
  children.forEach((child, index) => {
    const childInfo = asRecord(child.childInfo);
    const healthInfo = asRecord(child.healthInfo);
    const id = readText(child.id) || `child-${index + 1}`;
    const englishBirthDate = toDateInputValue(childInfo.dateOfBirth);
    const sourceHebrewDate = readText(childInfo.hebrewDateOfBirth);
    const derivedHebrewDate = parseEnglishBirthDateToHebrew(englishBirthDate);
    const hebrewBirthDate = sourceHebrewDate || derivedHebrewDate || "";
    const birthPlace = parseBirthPlace(childInfo.placeOfBirth);

    hebrewBirthDateOverrides[id] = Boolean(sourceHebrewDate);
    students.push({
      id,
      firstName: readText(childInfo.firstName),
      middleName: readText(childInfo.middleName),
      lastName: readText(childInfo.lastName),
      hebrewFirst: readText(childInfo.hebrewFirstName),
      hebrewMiddle: readText(childInfo.hebrewMiddleName),
      hebrewLast: readText(childInfo.hebrewLastName),
      calledBy: readText(childInfo.calledBy),
      englishBirthDate,
      hebrewBirthDate,
      birthCountry: birthPlace.birthCountry,
      birthState: birthPlace.birthState,
      birthCity: birthPlace.birthCity,
      language: readText(childInfo.languageAtHome),
      applyingYear: academicYear,
      grade:
        readText(child.gradeApplyingFor) || readText(childInfo.currentGrade),
      gender: normalizeGender(childInfo.gender),
      isAdopted: adoptedFlag,
      isStepParent: stepParentFlag,
      currentSchool:
        readText(childInfo.currentSchool) || readText(child.previousSchool),
      currentGrade: readText(childInfo.currentGrade),
      schoolPrincipal: readText(childInfo.principalName),
      schoolPhone: readText(childInfo.principalPhone),
      pediatrician: readText(healthInfo.physicianName),
      pediatricianPhone: readText(healthInfo.physicianPhone),
      dentist: readText(healthInfo.dentistName),
      dentistPhone: readText(healthInfo.dentistPhone),
    });
  });

  const fallbackStudent = students[0] ?? createEmptyStudent();

  const interviewDate = toUnixSeconds(application.interviewDate);
  const visitDate = toUnixSeconds(application.visitDate);
  const scheduledUnix = interviewDate ?? visitDate;
  const scheduledDate =
    scheduledUnix == null ? null : new Date(scheduledUnix * 1000);
  const selectedDate =
    scheduledDate && !Number.isNaN(scheduledDate.getTime())
      ? scheduledDate.getDate()
      : null;
  const selectedTime =
    scheduledDate && !Number.isNaN(scheduledDate.getTime())
      ? toScheduleTime(scheduledDate)
      : null;
  const currentCalendarDate =
    scheduledDate && !Number.isNaN(scheduledDate.getTime())
      ? new Date(scheduledDate.getFullYear(), scheduledDate.getMonth(), 1)
      : createDefaultCalendarDate();
  const firstStudentId = (students[0] ?? fallbackStudent).id;
  const interviewSlotsByStudentId =
    firstStudentId && scheduledUnix != null
      ? { [firstStudentId]: scheduledUnix }
      : {};

  return {
    students: students.length ? students : [fallbackStudent],
    hebrewBirthDateOverrides: students.length ? hebrewBirthDateOverrides : {},
    hasSibling: students.length > 1,
    family: {
      surname: readText(applicant.familyName),
      homePhone: readText(applicant.homePhone),
      address: readText(asRecord(primaryContact.address).street),
      city: readText(asRecord(primaryContact.address).city),
      state: readText(asRecord(primaryContact.address).state),
      zip: readText(asRecord(primaryContact.address).zipCode),
      father: mapParentRecordToData(fatherRecord, false),
      mother: mapParentRecordToData(motherRecord, true),
      maritalStatus: readText(applicant.maritalStatus),
      shulAffiliation: readText(applicant.synagogue),
      rav: readText(applicant.rav),
      householdComposition,
    },
    paternalGrandparents: mapGrandparentRecord(applicant.paternalGrandparents),
    maternalGrandparents: mapGrandparentRecord(applicant.maternalGrandparents),
    scheduleForm: {
      name:
        joinName(primaryContact.firstName, primaryContact.lastName) ||
        joinName(fallbackStudent.firstName, fallbackStudent.lastName),
      email: readText(primaryContact.email),
      phone: readText(primaryContact.phone) || readText(applicant.homePhone),
      notes: "",
    },
    interviewSlotsByStudentId,
    selectedDate,
    selectedTime,
    scheduleView: selectedDate ? "form" : "calendar",
    currentCalendarDate,
  };
};

const DOCUMENT_CATEGORY_BY_SLOT: Record<string, DocumentUploadCategory> = {
  "previous-report-card": "transcript",
  "birth-certificate": "birth_certificate",
  "immunization-records": "immunization",
  "occupational-history": "other",
  "recent-photo": "other",
  "iep-504-plan": "other",
};

const buildDocumentSlotKey = (slotId: string, studentId?: string): string =>
  studentId ? `${studentId}::${slotId}` : slotId;

const parseDocumentSlotKey = (
  slotKey: string,
): { studentId: string | null; baseSlotId: string } => {
  const [studentId, baseSlotId] = slotKey.split("::");
  if (baseSlotId) {
    return {
      studentId: readText(studentId) || null,
      baseSlotId: readText(baseSlotId),
    };
  }
  return { studentId: null, baseSlotId: readText(slotKey) };
};

const buildDocumentValidationKey = (slotKey: string): string =>
  `documents.requiredUploads.${slotKey}`;

const getRequiredDocumentUploadLabel = (
  slot: AdmissionDocumentRequirementRow,
  targetStudent: StudentData | null,
  targetIndex: number,
): string => {
  if (!targetStudent) return slot.label;
  const studentName =
    joinName(targetStudent.firstName, targetStudent.lastName) ||
    `Student ${targetIndex + 1}`;
  return `${studentName}: ${slot.label}`;
};

const CURRENT_SCHOOLING_FIELDS: StudentTextField[] = [
  "currentSchool",
  "currentGrade",
  "schoolPrincipal",
  "schoolPhone",
];

const MEDICAL_CONTACT_FIELDS: StudentTextField[] = [
  "pediatrician",
  "pediatricianPhone",
  "dentist",
  "dentistPhone",
];

const sanitizeStorageToken = (value: unknown): string =>
  readText(value)
    .replace(/[^a-zA-Z0-9._-]+/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 120);

const buildAdmissionsUploadPath = (
  applicationId: string,
  ownerId: string,
  slotId: string,
  fileName: string,
): string => {
  const safeOwner = sanitizeStorageToken(ownerId) || "unknown";
  const safeSlot = sanitizeStorageToken(slotId) || "other";
  const timestamp = Date.now();
  const extension = fileName.includes(".")
    ? `.${fileName.split(".").pop()}`
    : "";
  const safeBaseName =
    sanitizeStorageToken(fileName.replace(/\.[^.]+$/, "")) || "document";
  return `uploads/admissions/${applicationId}/${safeOwner}/${safeSlot}/${timestamp}-${safeBaseName}${extension}`;
};

const ProgressBar = ({
  currentStep,
  onStepClick,
  steps,
}: {
  currentStep: number;
  onStepClick: (step: number) => void;
  steps: string[];
}) => {
  const progress = Math.round(((currentStep + 1) / steps.length) * 100);
  const currentStepName = steps[currentStep] || "Complete";

  return (
    <div className="mb-10">
      {/* Header: Registration Progress title and % */}
      <div className="flex justify-between items-end mb-3">
        <span className="text-[10px] font-bold text-irongrey/40 tracking-widest uppercase">
          REGISTRATION PROGRESS
        </span>
        <span className="text-[10px] font-bold text-irongrey/40 tracking-widest">
          {Math.min(progress, 100)}%
        </span>
      </div>

      {/* Progress Bar Line */}
      <div className="h-1.5 w-full bg-gray-100 rounded-full mb-4 overflow-hidden">
        <div
          className="h-full bg-primary rounded-full transition-all duration-300 ease-in-out"
          style={{ width: `${Math.min(progress, 100)}%` }}
        ></div>
      </div>

      {/* Desktop: Step list with word labels */}
      <div className="hidden sm:flex justify-between relative">
        {steps.map((step, idx) => (
          <button
            key={step}
            onClick={() => (idx <= currentStep ? onStepClick(idx) : null)}
            disabled={idx > currentStep}
            className={`text-[10px] font-bold tracking-wide text-left transition-colors ${
              idx === currentStep
                ? "text-[#2697a2]"
                : idx < currentStep
                  ? "text-[#2697a2]/70 cursor-pointer hover:text-[#2697a2]"
                  : "text-[#2697a2]/30 cursor-not-allowed"
            }`}
          >
            {idx + 1}. {step}
          </button>
        ))}
      </div>

      {/* Mobile: Centered current step text (Match reference image) */}
      <div className="sm:hidden flex justify-center mt-2">
        <span className="text-sm font-bold text-primary tracking-tight">
          Step {currentStep + 1}: {currentStepName}
        </span>
      </div>
    </div>
  );
};

const SectionHeader: React.FC<{
  icon?: React.ElementType;
  title: string;
  rightText?: string;
  rightContent?: React.ReactNode;
}> = ({ icon: Icon, title, rightText, rightContent }) => (
  <div className="flex items-center justify-between mb-6">
    <div className="flex items-center gap-3">
      {Icon && (
        <Icon
          size={16}
          className="text-irongrey/40 transition-colors duration-500 group-hover:text-irongrey/90"
        />
      )}
      <h3 className="text-[11px] font-bold text-irongrey/40 uppercase tracking-widest transition-colors duration-500 group-hover:text-irongrey/90">
        {title}
      </h3>
    </div>
    {rightContent ? rightContent : null}
    {!rightContent && rightText ? (
      <span className="text-[11px] font-bold text-irongrey/40 uppercase tracking-widest transition-colors duration-500 group-hover:text-irongrey/90">
        {rightText}
      </span>
    ) : null}
  </div>
);

const StepHeader: React.FC<{ title: string; description: string }> = ({
  title,
  description,
}) => (
  <div className="mb-8 animate-in slide-in-from-bottom-2 duration-500">
    <h1 className="text-2xl font-bold text-irongrey mb-1">{title}</h1>
    <p className="text-sm text-irongrey/60">{description}</p>
  </div>
);

const StyledRadioOption: React.FC<{
  label: string;
  name: string;
  checked: boolean;
  onSelect: () => void;
}> = ({ label, name, checked, onSelect }) => (
  <label className="inline-flex items-center gap-2.5 cursor-pointer select-none">
    <input
      type="radio"
      name={name}
      checked={checked}
      onChange={onSelect}
      className="sr-only peer"
    />
    <span
      className="h-4 w-4 rounded-full border border-[#9AA5A4] bg-white inline-flex items-center justify-center transition-colors peer-checked:border-primary"
      aria-hidden="true"
    >
      <span
        className={`h-2 w-2 rounded-full bg-primary transition-opacity ${checked ? "opacity-100" : "opacity-0"}`}
      />
    </span>
    <span className="text-sm font-semibold text-irongrey">{label}</span>
  </label>
);

const StudentForm = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const { profile } = useShellProfile();
  const dispatch = useAppDispatch();
  const submitApplication = useSubmitApplication();

  // --- State ---
  const [students, setStudents] = useState<StudentData[]>([
    createEmptyStudent(),
  ]);
  const [hebrewBirthDateOverrides, setHebrewBirthDateOverrides] = useState<
    Record<string, boolean>
  >({});
  const [studentSectionPrefill, setStudentSectionPrefill] = useState<
    Record<string, StudentSectionPrefillState>
  >({});
  const [hasSibling, setHasSibling] = useState<boolean | null>(false);
  const [family, setFamily] = useState<FamilyData>(() =>
    createInitialFamilyData(),
  );
  const [paternalGrandparents, setPaternalGrandparents] =
    useState<GrandparentData>(() => createInitialGrandparentData());
  const [maternalGrandparents, setMaternalGrandparents] =
    useState<GrandparentData>(() => createInitialGrandparentData());
  const [documents, setDocuments] = useState<DocumentsData>(() =>
    createInitialDocumentsData(),
  );
  const [documentUploads, setDocumentUploads] = useState<
    Record<string, string>
  >({});
  const [documentFiles, setDocumentFiles] = useState<
    Record<string, File | undefined>
  >({});
  const fileInputRefs = useRef<Record<string, HTMLInputElement | null>>({});
  const [consentChecked, setConsentChecked] = useState(false);
  const [gradeDiscrepancyAcknowledged, setGradeDiscrepancyAcknowledged] =
    useState(false);

  // Calendar & Schedule State
  const [scheduleView, setScheduleView] = useState<
    "calendar" | "form" | "success"
  >("calendar");
  const [selectedDate, setSelectedDate] = useState<number | null>(null);
  const [selectedTime, setSelectedTime] = useState<string | null>(null);
  const [scheduleForm, setScheduleForm] = useState<ScheduleFormData>(() =>
    createInitialScheduleFormData(),
  );
  const [scheduleStudentIndex, setScheduleStudentIndex] = useState(0);
  const [interviewSlotsByStudentId, setInterviewSlotsByStudentId] = useState<
    Record<string, number>
  >({});
  const [touchedFields, setTouchedFields] = useState<Record<string, boolean>>(
    {},
  );
  const [attemptedSteps, setAttemptedSteps] = useState<
    Partial<Record<ApplicationsIntakeStep, boolean>>
  >({});

  const [currentCalendarDate, setCurrentCalendarDate] = useState(
    () => createDefaultCalendarDate(),
  );
  const [timezoneOpen, setTimezoneOpen] = useState(false);
  const [selectedTimezone, setSelectedTimezone] = useState("Eastern Time");
  const timezoneRef = useRef<HTMLDivElement>(null);

  // Track active widget state: { studentId, fieldName }
  const [activeKeyboard, setActiveKeyboard] = useState<{
    id: string;
    field: keyof StudentData;
  } | null>(null);
  const [activeDatePicker, setActiveDatePicker] = useState<{
    id: string;
    field: keyof StudentData;
  } | null>(null);

  const [hydratedApplicationId, setHydratedApplicationId] = useState<
    string | null
  >(null);
  const [draftFeedback, setDraftFeedback] = useState<DraftFeedbackState>({
    state: "idle",
    message: "Automatic saving enabled",
  });
  const [parentAdmissionsDraftCapability, setParentAdmissionsDraftCapability] =
    useState<"available" | "unavailable">("available");
  const [submissionAlert, setSubmissionAlert] =
    useState<SubmissionAlertState | null>(null);
  const [submissionCompletionMode, setSubmissionCompletionMode] =
    useState<SubmissionCompletionMode | null>(null);
  const [pendingSubmission, setPendingSubmission] =
    useState<PendingSubmissionState | null>(null);
  const [isRetryingSubmission, setIsRetryingSubmission] = useState(false);
  const initialBrowserBackDraftRef = useRef({
    students: [createEmptyStudent()],
    hebrewBirthDateOverrides: {},
    studentSectionPrefill: {},
    hasSibling: false,
    family: createInitialFamilyData(),
    paternalGrandparents: createInitialGrandparentData(),
    maternalGrandparents: createInitialGrandparentData(),
    documents: createInitialDocumentsData(),
    documentUploads: {},
    documentFiles: {},
    consentChecked: false,
    gradeDiscrepancyAcknowledged: false,
    scheduleView: "calendar" as const,
    selectedDate: null,
    selectedTime: null,
    scheduleForm: createInitialScheduleFormData(),
    interviewSlotsByStudentId: {},
  });

  const searchParams = useMemo(
    () =>
      new URLSearchParams(
        typeof window === "undefined" ? "" : window.location.search,
      ),
    [],
  );
  const selectedApplicationId = readText(searchParams.get("applicationId"));
  const selectedApplicationCodeParam = readText(
    searchParams.get("applicationCode"),
  );
  const applicantId = readText(profile?.userSub);
  const selectedApplicationQuery = useGetAdmissionsApplicationQuery(
    selectedApplicationId,
    { skip: !selectedApplicationId },
  );
  const parentAdmissionsDraftQuery = useGetParentAdmissionsDraftQuery(
    {},
    {
      skip:
        !applicantId ||
        Boolean(selectedApplicationId) ||
        parentAdmissionsDraftCapability === "unavailable",
    },
  );
  const [
    upsertParentAdmissionsDraft,
    { isLoading: isSavingParentAdmissionsDraft },
  ] = useUpsertParentAdmissionsDraftMutation();
  const [
    deleteParentAdmissionsDraft,
    { isLoading: isDeletingParentAdmissionsDraft },
  ] = useDeleteParentAdmissionsDraftMutation();
  const applicantApplicationsQuery = useGetApplicantApplicationsQuery(
    { applicantId, limit: 25 },
    { skip: !applicantId || Boolean(selectedApplicationId) },
  );
  const [parentApplySettings, setParentApplySettings] =
    useState<PublicApplySettings | null>(null);
  const [parentSettingsLoadStatus, setParentSettingsLoadStatus] =
    useState<ParentSettingsLoadStatus>("loading");
  const [parentSettingsLoadError, setParentSettingsLoadError] = useState("");
  const loadParentApplySettings = useCallback(async () => {
    setParentSettingsLoadStatus("loading");
    setParentSettingsLoadError("");
    try {
      const settings = await getPublicApplySettings();
      setParentApplySettings(settings);
      setParentSettingsLoadStatus("ready");
    } catch (error) {
      setParentSettingsLoadStatus("error");
      setParentSettingsLoadError(
        error instanceof Error
          ? error.message
          : "Unable to load application requirements.",
      );
    }
  }, []);

  useEffect(() => {
    void loadParentApplySettings();
  }, [loadParentApplySettings]);

  const parentAdmissionConfig = parentApplySettings?.admissionConfig;
  const familySelfSchedulingEnabled =
    parentAdmissionConfig?.interviewScheduling?.familySelfSchedulingEnabled ===
    true;
  const familySelfSchedulingVisible =
    parentSettingsLoadStatus === "ready" && familySelfSchedulingEnabled;
  const shouldSuppressFamilyScheduling = !familySelfSchedulingVisible;
  const admissionsRequirementsReady =
    parentSettingsLoadStatus === "ready" && parentApplySettings != null;
  const admissionsRequirementsLoading = parentSettingsLoadStatus === "loading";
  const admissionsRequirementsErrorMessage = parentSettingsLoadError;
  const retryAdmissionsRequirements = loadParentApplySettings;
  const admissionsDocumentRows = useMemo(
    () => getAdmissionsDocumentRequirementRows(parentAdmissionConfig),
    [parentAdmissionConfig],
  );
  const admissionsAgreementRows = useMemo(
    () => getAdmissionsAgreementRows(parentAdmissionConfig),
    [parentAdmissionConfig],
  );
  const tuitionPolicyAgreement = useMemo(
    () =>
      admissionsAgreementRows.find((row) => row.id === "tuition_policy") ??
      null,
    [admissionsAgreementRows],
  );
  const certificationAgreement = useMemo(
    () =>
      admissionsAgreementRows.find((row) => row.id === "certification") ??
      null,
    [admissionsAgreementRows],
  );
  const selectedApplicationRecord = useMemo(
    () =>
      selectedApplicationQuery.data
        ? asRecord(selectedApplicationQuery.data)
        : {},
    [selectedApplicationQuery.data],
  );
  const selectedApplicationCode =
    readText(selectedApplicationRecord.code) || selectedApplicationCodeParam;
  const latestApplicantApplicationRecord = useMemo(() => {
    const items = Array.isArray(applicantApplicationsQuery.data?.items)
      ? applicantApplicationsQuery.data.items
      : [];
    if (!items.length) return {};
    const sorted = [...items].sort((left, right) => {
      const leftRecord = asRecord(left);
      const rightRecord = asRecord(right);
      const leftTimestamp = Number(
        leftRecord.updatedAt ??
          leftRecord.submittedAt ??
          leftRecord.createdAt ??
          0,
      );
      const rightTimestamp = Number(
        rightRecord.updatedAt ??
          rightRecord.submittedAt ??
          rightRecord.createdAt ??
          0,
      );
      return rightTimestamp - leftTimestamp;
    });
    return asRecord(sorted[0]);
  }, [applicantApplicationsQuery.data]);
  const siblingInheritanceHydratedRef = useRef(false);
  const parentAdmissionsDraftHydratedRef = useRef(false);
  const parentAdmissionsDraftLastSavedRef = useRef("");
  const parentAdmissionsSuppressedScheduleRef =
    useRef<ParentAdmissionsDraftScheduleState | null>(null);
  const parentAdmissionsDraftAutosaveTimeoutRef = useRef<number | null>(null);
  const parentAdmissionsDraftSaveQueueRef = useRef<Promise<void>>(Promise.resolve());
  const submissionInFlightRef = useRef(false);

  // Dynamic Steps Logic
  const steps = useMemo(
    () => getApplicationSteps(hasSibling, familySelfSchedulingVisible),
    [familySelfSchedulingVisible, hasSibling],
  );
  const isSubmittingApplication =
    submitApplication.submitting || isRetryingSubmission;
  const schedulableStudents = useMemo(() => {
    const filtered = students.filter((student) => {
      const hasAnyName = Boolean(
        readText(student.firstName) ||
          readText(student.lastName) ||
          readText(student.calledBy),
      );
      const hasAnyGrade = Boolean(
        readText(student.grade) || readText(student.currentGrade),
      );
      return hasAnyName || hasAnyGrade;
    });
    return filtered.length ? filtered : students.slice(0, 1);
  }, [students]);
  const activeScheduleStudent =
    schedulableStudents[scheduleStudentIndex] ?? schedulableStudents[0] ?? null;
  const activeScheduleStudentName = activeScheduleStudent
    ? readText(activeScheduleStudent.firstName) ||
      readText(activeScheduleStudent.calledBy) ||
      `Child ${scheduleStudentIndex + 1}`
    : "Student";
  const schedulableStudentIds = useMemo(
    () => schedulableStudents.map((student) => student.id),
    [schedulableStudents],
  );
  const currentStepName = (
    currentStep < steps.length
      ? steps[currentStep]
      : familySelfSchedulingVisible
        ? "Schedule"
        : "Review"
  ) as ApplicationsIntakeStep;
  const markFieldTouched = useCallback((fieldKey: string) => {
    setTouchedFields((previous) =>
      previous[fieldKey] ? previous : { ...previous, [fieldKey]: true },
    );
  }, []);
  const markStepAttempted = useCallback((step: ApplicationsIntakeStep) => {
    setAttemptedSteps((previous) =>
      previous[step] ? previous : { ...previous, [step]: true },
    );
  }, []);
  const shouldShowFieldValidation = useCallback(
    (fieldKey: string, step: ApplicationsIntakeStep) =>
      Boolean(touchedFields[fieldKey] || attemptedSteps[step]),
    [attemptedSteps, touchedFields],
  );
  const getFieldIndicatorClassName = useCallback(
    (
      fieldKey: string,
      step: ApplicationsIntakeStep,
      value: unknown,
      options?: {
        required?: boolean;
        validator?: (candidate: unknown) => boolean;
      },
    ) =>
      getValidationIndicatorClassName(
        getValidationIndicatorState({
          show: shouldShowFieldValidation(fieldKey, step),
          value,
          required: options?.required ?? true,
          validator: options?.validator,
        }),
      ),
    [shouldShowFieldValidation],
  );
  const getGroupedFieldIndicatorClassName = useCallback(
    (
      fieldKey: string,
      step: ApplicationsIntakeStep,
      value: unknown,
      groupRequiredMissing: boolean,
      validator?: (candidate: unknown) => boolean,
    ) => {
      if (!shouldShowFieldValidation(fieldKey, step)) {
        return "";
      }
      const normalized = readText(value);
      if (!normalized) {
        return groupRequiredMissing
          ? getValidationIndicatorClassName("missing")
          : "";
      }
      if (validator && !validator(normalized)) {
        return getValidationIndicatorClassName("invalid");
      }
      return "";
    },
    [shouldShowFieldValidation],
  );
  const getCheckboxIndicatorClassName = useCallback(
    (step: ApplicationsIntakeStep, checked: boolean) =>
      attemptedSteps[step] && !checked ? "border-amber-700 bg-amber-50" : "",
    [attemptedSteps],
  );
  const fieldKey = useCallback(
    (...parts: Array<string | number>) => parts.join("."),
    [],
  );
  const requiredDocumentUploads = useMemo<
    ApplicationsIntakeRequiredDocumentUploadLike[]
  >(() => {
    return buildAdmissionsIntakeRequiredUploads({
      rows: admissionsDocumentRows,
      students,
      documentUploads,
    });
  }, [admissionsDocumentRows, documentUploads, students]);
  const documentUploadTargets = useMemo(() => {
    if (students.length > 1) {
      return students.map((student, index) => ({
        key: student.id || `student_${index}`,
        labelIndex: index,
        showStudentHeader: true,
        student,
        uploadStudentId: student.id || `student_${index}`,
      }));
    }
    return [
      {
        key: "single-0",
        labelIndex: 0,
        showStudentHeader: false,
        student: students[0] ?? null,
        uploadStudentId: students[0]?.id,
      },
    ];
  }, [students]);

  const documentValidationData = useMemo(
    () => ({
      ...documents,
      policyAcknowledgementRequired:
        tuitionPolicyAgreement?.visible === true &&
        tuitionPolicyAgreement.required,
      requirementsReady: admissionsRequirementsReady,
      requiredUploads: requiredDocumentUploads,
    }),
    [
      admissionsRequirementsReady,
      documents,
      requiredDocumentUploads,
      tuitionPolicyAgreement,
    ],
  );

  const gradeDiscrepancyWarnings = useMemo(
    () => getApplicationsIntakeGradeDiscrepancyWarnings(students),
    [students],
  );
  const gradeDiscrepancyWarningSignature = useMemo(
    () =>
      gradeDiscrepancyWarnings
        .map((warning) => `${warning.key}:${warning.applyingGrade}:${warning.currentGrade}`)
        .join('|'),
    [gradeDiscrepancyWarnings],
  );

  useEffect(() => {
    setGradeDiscrepancyAcknowledged(false);
  }, [gradeDiscrepancyWarningSignature]);

  const intakeValidationInput = useMemo<ApplicationsIntakeValidationInput>(
    () => ({
      step: currentStepName,
      students,
      family,
      documents: documentValidationData,
      consentChecked,
      certificationRequired:
        certificationAgreement?.visible === true &&
        certificationAgreement.required,
      gradeDiscrepancyAcknowledged,
      scheduleForm,
      selectedDate,
      selectedTime,
      interviewSlotsByStudentId,
      schedulableStudentIds,
    }),
    [
      consentChecked,
      certificationAgreement,
      currentStepName,
      documentValidationData,
      family,
      gradeDiscrepancyAcknowledged,
      interviewSlotsByStudentId,
      scheduleForm,
      schedulableStudentIds,
      selectedDate,
      selectedTime,
      students,
    ],
  );

  const intakeActionState = useMemo(
    () => getApplicationsIntakeActionState(intakeValidationInput),
    [intakeValidationInput],
  );

  const submissionBlockingIssues = useMemo(
    () => getApplicationsIntakeSubmissionIssues(intakeValidationInput),
    [intakeValidationInput],
  );
  const showFooterNavigation = shouldShowFooterNavigation(
    currentStep < steps.length ? currentStepName : null,
    scheduleView,
  );
  const currentAdvanceIssues = intakeActionState.advanceIssues;
  const showStepValidationAlert =
    currentStep < steps.length &&
    Boolean(attemptedSteps[currentStepName]) &&
    currentAdvanceIssues.length > 0;
  const visibleAdvanceIssues = currentAdvanceIssues.slice(0, 3);
  const hiddenAdvanceIssueCount = Math.max(
    0,
    currentAdvanceIssues.length - visibleAdvanceIssues.length,
  );
  const saveAndContinueBlocked = !intakeActionState.canAdvanceStep;
  const isDisabledSchedulingReviewSubmit =
    !familySelfSchedulingVisible && steps[currentStep] === "Review";
  const primaryActionBlocked = isDisabledSchedulingReviewSubmit
    ? !intakeActionState.canSubmitWithoutScheduling
    : saveAndContinueBlocked;
  const fatherPolicyAcknowledgementDisabled =
    family.father.isDeceasedOrNoLongerLegalGuardian;
  const motherPolicyAcknowledgementDisabled =
    family.mother.isDeceasedOrNoLongerLegalGuardian;
  const firstSubmissionBlockingIssue = submissionBlockingIssues[0] ?? null;

  const focusAdvanceIssue = useCallback(
    (issue: ApplicationsIntakeAdvanceIssue) => {
      const resolveIssueTargetSelector = (issueKey: string): string => {
        switch (issueKey) {
          case "parents.group.parentName":
            return '[data-validation-key="family.father.name"]';
          case "parents.group.phone":
            return '[data-validation-key="family.homePhone"]';
          case "parents.group.email":
            return '[data-validation-key="family.father.email"]';
          case "students.__count__":
            return '[data-testid="parent-portal-intake-students-step"]';
          default:
            return `[data-validation-key="${issueKey.replace(/\\/g, "\\\\").replace(/"/g, '\\"')}"]`;
        }
      };

      window.requestAnimationFrame(() => {
        const target = document.querySelector(
          resolveIssueTargetSelector(issue.key),
        ) as HTMLElement | null;
        if (!target) {
          return;
        }

        target.scrollIntoView({ behavior: "smooth", block: "center" });

        const focusableSelector =
          'input, button, textarea, select, [href], [data-select-trigger="true"], [tabindex]';
        const isRenderedFocusableTarget = (element: HTMLElement): boolean => {
          const computedStyle = window.getComputedStyle(element);
          return (
            computedStyle.display !== "none" &&
            computedStyle.visibility !== "hidden" &&
            computedStyle.pointerEvents !== "none" &&
            element.getClientRects().length > 0
          );
        };
        const visibleFocusableTarget = Array.from(
          target.querySelectorAll<HTMLElement>(focusableSelector),
        ).find((candidate) => isRenderedFocusableTarget(candidate));
        const focusableTarget =
          target.matches(focusableSelector) && isRenderedFocusableTarget(target)
            ? target
            : (visibleFocusableTarget ?? target);

        if (!focusableTarget.matches(focusableSelector)) {
          focusableTarget.setAttribute("tabindex", "-1");
        }

        focusableTarget.focus({ preventScroll: true });
      });
    },
    [],
  );

  // --- Helpers ---
  const scrollToTop = () => {
    const root = document.getElementById("root")?.parentElement;
    if (root) root.scrollTo({ top: 0, behavior: "smooth" });
  };

  const clearSubmissionFeedback = () => {
    setSubmissionAlert(null);
    setPendingSubmission(null);
  };

  const applyAutofillDraft = () => {
    const draft = buildAdmissionsIntakeAutofillDraft();
    clearSubmissionFeedback();
    setSubmissionCompletionMode(null);
    setStudentSectionPrefill(draft.studentSectionPrefill);
    setStudents(draft.students);
    setHasSibling(draft.hasSibling);
    setFamily(draft.family);
    setPaternalGrandparents(draft.paternalGrandparents);
    setMaternalGrandparents(draft.maternalGrandparents);
    setDocuments(draft.documents);
    setDocumentUploads(
      Object.fromEntries(
        (draft.documents.requiredUploads ?? []).map((upload) => [
          upload.key,
          upload.fileName ?? "",
        ]),
      ),
    );
    setDocumentFiles({});
    setConsentChecked(draft.consentChecked);
    setGradeDiscrepancyAcknowledged(false);
    setScheduleView(shouldSuppressFamilyScheduling ? "calendar" : draft.scheduleView);
    setSelectedDate(shouldSuppressFamilyScheduling ? null : draft.selectedDate);
    setSelectedTime(shouldSuppressFamilyScheduling ? null : draft.selectedTime);
    setScheduleForm(
      shouldSuppressFamilyScheduling
        ? createInitialScheduleFormData()
        : draft.scheduleForm,
    );
    setScheduleStudentIndex(
      shouldSuppressFamilyScheduling ? 0 : draft.scheduleStudentIndex,
    );
    setInterviewSlotsByStudentId(
      shouldSuppressFamilyScheduling ? {} : draft.interviewSlotsByStudentId,
    );
    setCurrentCalendarDate(
      shouldSuppressFamilyScheduling
        ? createDefaultCalendarDate()
        : new Date(draft.currentCalendarDate),
    );
    setTimezoneOpen(false);
    setActiveKeyboard(null);
    setActiveDatePicker(null);
  };

  const resetParentAdmissionsDraftState = useCallback(() => {
    setCurrentStep(0);
    setStudents([createEmptyStudent()]);
    setHebrewBirthDateOverrides({});
    setStudentSectionPrefill({});
    setHasSibling(false);
    setFamily(createInitialFamilyData());
    setPaternalGrandparents(createInitialGrandparentData());
    setMaternalGrandparents(createInitialGrandparentData());
    setDocuments(createInitialDocumentsData());
    setDocumentUploads({});
    setDocumentFiles({});
    setConsentChecked(false);
    setScheduleView("calendar");
    setSelectedDate(null);
    setSelectedTime(null);
    setScheduleForm(createInitialScheduleFormData());
    setScheduleStudentIndex(0);
    setInterviewSlotsByStudentId({});
    setCurrentCalendarDate(createDefaultCalendarDate());
    setTimezoneOpen(false);
    setSelectedTimezone("Eastern Time");
    setActiveKeyboard(null);
    setActiveDatePicker(null);
    parentAdmissionsSuppressedScheduleRef.current = null;
    clearSubmissionFeedback();
    setSubmissionCompletionMode(null);
  }, []);

  const buildParentAdmissionsDraftFormState =
    useCallback((): ParentAdmissionsDraftFormState => ({
      students,
      hebrewBirthDateOverrides,
      studentSectionPrefill,
      hasSibling,
      family,
      paternalGrandparents,
      maternalGrandparents,
      documents,
      documentUploads,
      consentChecked,
      scheduleView,
      selectedDate,
      selectedTime,
      scheduleForm,
      scheduleStudentIndex,
      interviewSlotsByStudentId,
      currentCalendarDate: currentCalendarDate.toISOString(),
      selectedTimezone,
    }), [
      consentChecked,
      currentCalendarDate,
      documentUploads,
      documents,
      family,
      hasSibling,
      hebrewBirthDateOverrides,
      interviewSlotsByStudentId,
      maternalGrandparents,
      paternalGrandparents,
      scheduleForm,
      scheduleStudentIndex,
      scheduleView,
      selectedDate,
      selectedTime,
      selectedTimezone,
      studentSectionPrefill,
      students,
    ]);

  const restoreSuppressedScheduleState = useCallback(
    (
      formState: ParentAdmissionsDraftFormState,
    ): ParentAdmissionsDraftFormState =>
      shouldSuppressFamilyScheduling &&
      parentAdmissionsSuppressedScheduleRef.current
        ? {
            ...formState,
            ...parentAdmissionsSuppressedScheduleRef.current,
          }
        : formState,
    [shouldSuppressFamilyScheduling],
  );

  const clearPendingParentAdmissionsDraftAutosave = useCallback(() => {
    if (parentAdmissionsDraftAutosaveTimeoutRef.current === null) {
      return;
    }
    window.clearTimeout(parentAdmissionsDraftAutosaveTimeoutRef.current);
    parentAdmissionsDraftAutosaveTimeoutRef.current = null;
  }, []);

  const applyParentAdmissionsDraft = useCallback(
    (draft: {
      currentStep?: number;
      formState?: Record<string, unknown> | null;
    }) => {
      const formState = readParentAdmissionsDraftFormState(draft.formState);
      parentAdmissionsSuppressedScheduleRef.current =
        shouldSuppressFamilyScheduling
          ? readParentAdmissionsDraftScheduleState(formState)
          : null;
      const scheduleSafeFormState: ParentAdmissionsDraftFormState =
        shouldSuppressFamilyScheduling
          ? {
              ...formState,
              scheduleView: "calendar",
              selectedDate: null,
              selectedTime: null,
              scheduleForm: createInitialScheduleFormData(),
              scheduleStudentIndex: 0,
              interviewSlotsByStudentId: {},
              currentCalendarDate: createDefaultCalendarDate().toISOString(),
            }
          : formState;
      clearSubmissionFeedback();
      setSubmissionCompletionMode(null);
      setStudentSectionPrefill(scheduleSafeFormState.studentSectionPrefill);
      setStudents(scheduleSafeFormState.students);
      setHebrewBirthDateOverrides(scheduleSafeFormState.hebrewBirthDateOverrides);
      setHasSibling(scheduleSafeFormState.hasSibling);
      setFamily(scheduleSafeFormState.family);
      setPaternalGrandparents(scheduleSafeFormState.paternalGrandparents);
      setMaternalGrandparents(scheduleSafeFormState.maternalGrandparents);
      setDocuments(scheduleSafeFormState.documents);
      setDocumentUploads(scheduleSafeFormState.documentUploads);
      setDocumentFiles({});
      setConsentChecked(scheduleSafeFormState.consentChecked);
      setScheduleView(scheduleSafeFormState.scheduleView);
      setSelectedDate(scheduleSafeFormState.selectedDate);
      setSelectedTime(scheduleSafeFormState.selectedTime);
      setScheduleForm(scheduleSafeFormState.scheduleForm);
      setScheduleStudentIndex(scheduleSafeFormState.scheduleStudentIndex);
      setInterviewSlotsByStudentId(scheduleSafeFormState.interviewSlotsByStudentId);
      setCurrentCalendarDate(readDateFromDraft(scheduleSafeFormState.currentCalendarDate));
      setSelectedTimezone(scheduleSafeFormState.selectedTimezone);
      setTimezoneOpen(false);
      setActiveKeyboard(null);
      setActiveDatePicker(null);
      const restoredStep = clampDraftStep(
        draft.currentStep,
        scheduleSafeFormState.hasSibling,
        !shouldSuppressFamilyScheduling,
      );
      setCurrentStep(restoredStep);
      parentAdmissionsDraftLastSavedRef.current = JSON.stringify({
        currentStep: restoredStep,
        formState: restoreSuppressedScheduleState(scheduleSafeFormState),
      });
      setDraftFeedback({
        state: "hydrated",
        message: "Draft restored from your account",
      });
    },
    [
      clearSubmissionFeedback,
      restoreSuppressedScheduleState,
      shouldSuppressFamilyScheduling,
    ],
  );

  const saveParentAdmissionsDraft = useCallback(
    async (
      reason: DraftSaveReason,
      options?: { currentStepOverride?: number },
    ): Promise<boolean> => {
      if (!applicantId || selectedApplicationId || currentStep >= steps.length) {
        return true;
      }
      if (parentAdmissionsDraftCapability === "unavailable") {
        if (reason === "manual") {
          setDraftFeedback({
            state: "error",
            message: PARENT_ADMISSIONS_DRAFT_UNAVAILABLE_MESSAGE,
          });
        }
        return true;
      }
      const formState = restoreSuppressedScheduleState(
        buildParentAdmissionsDraftFormState(),
      );
      const payload = {
        currentStep: clampDraftStep(
          options?.currentStepOverride ?? currentStep,
          hasSibling,
          familySelfSchedulingVisible,
        ),
        formState,
      };
      const serialized = JSON.stringify(payload);
      if (
        reason === "autosave" &&
        parentAdmissionsDraftLastSavedRef.current === serialized
      ) {
        return true;
      }

      const executeSave = async (): Promise<boolean> => {
        setDraftFeedback({
          state: "saving",
          message: reason === "autosave" ? "Autosaving..." : "Saving draft...",
        });
        await upsertParentAdmissionsDraft(payload).unwrap();
        parentAdmissionsDraftLastSavedRef.current = serialized;
        setDraftFeedback({
          state: "saved",
          message:
            reason === "autosave"
              ? "Draft autosaved"
              : "Draft saved to your account",
        });
        return true;
      };

      const queuedSave = parentAdmissionsDraftSaveQueueRef.current
        .catch(() => undefined)
        .then(executeSave);
      parentAdmissionsDraftSaveQueueRef.current = queuedSave.then(
        () => undefined,
        () => undefined,
      );

      try {
        return await queuedSave;
      } catch (error) {
        if (isParentAdmissionsDraftCapabilityUnavailable(error)) {
          setParentAdmissionsDraftCapability("unavailable");
          setDraftFeedback({
            state: "error",
            message: PARENT_ADMISSIONS_DRAFT_UNAVAILABLE_MESSAGE,
          });
          return true;
        }
        setDraftFeedback({
          state: "error",
          message: readDomainRouterErrorMessage(
            error,
            "We could not save this draft right now.",
          ),
        });
        return false;
      }
    },
    [
      applicantId,
      buildParentAdmissionsDraftFormState,
      currentStep,
      familySelfSchedulingVisible,
      hasSibling,
      parentAdmissionsDraftCapability,
      restoreSuppressedScheduleState,
      selectedApplicationId,
      steps.length,
      upsertParentAdmissionsDraft,
    ],
  );

  const deleteActiveParentAdmissionsDraft = useCallback(
    async (reason: DraftSaveReason): Promise<boolean> => {
      if (!applicantId || selectedApplicationId) {
        return true;
      }
      if (parentAdmissionsDraftCapability === "unavailable") {
        if (reason === "discard") {
          setDraftFeedback({
            state: "idle",
            message: "No saved draft to discard in this environment",
          });
        }
        return true;
      }
      if (reason === "discard") {
        setDraftFeedback({
          state: "discarding",
          message: "Discarding draft...",
        });
      }
      try {
        await deleteParentAdmissionsDraft({}).unwrap();
        parentAdmissionsDraftLastSavedRef.current = "";
        parentAdmissionsSuppressedScheduleRef.current = null;
        if (reason === "discard") {
          setDraftFeedback({
            state: "idle",
            message: "Draft discarded",
          });
        }
        return true;
      } catch (error) {
        if (isParentAdmissionsDraftCapabilityUnavailable(error)) {
          setParentAdmissionsDraftCapability("unavailable");
          if (reason === "discard") {
            setDraftFeedback({
              state: "idle",
              message: "No saved draft to discard in this environment",
            });
          }
          return true;
        }
        setDraftFeedback({
          state: "error",
          message: readDomainRouterErrorMessage(
            error,
            "We could not discard this draft right now.",
          ),
        });
        return false;
      }
    },
    [
      applicantId,
      deleteParentAdmissionsDraft,
      parentAdmissionsDraftCapability,
      selectedApplicationId,
    ],
  );

  const discardParentAdmissionsDraft = useCallback(async () => {
    const discarded = await deleteActiveParentAdmissionsDraft("discard");
    if (!discarded) {
      return;
    }
    resetParentAdmissionsDraftState();
    window.location.assign("/parent/portal/applications");
  }, [deleteActiveParentAdmissionsDraft, resetParentAdmissionsDraftState]);

  const buildSubmitApplicationInput = () => {
    const inheritedApplicant = asRecord(selectedApplicationRecord.applicant);
    const fallbackApplicant = asRecord(
      latestApplicantApplicationRecord.applicant,
    );
    const parentRows = [
      { relation: "father" as const, source: family.father },
      { relation: "mother" as const, source: family.mother },
    ];
    const parentGuardianHouseholdComposition =
      resolveParentGuardianHouseholdComposition(family);
    const activeParentKeys: ParentGuardianFormKey[] =
      parentGuardianHouseholdComposition.mode === "single_parent_guardian"
        ? [parentGuardianHouseholdComposition.primaryKey]
        : ["father", "mother"];
    const activeParentRows = parentRows.filter((entry) =>
      activeParentKeys.includes(entry.relation),
    );
    const isSingleParentGuardianMode =
      parentGuardianHouseholdComposition.mode === "single_parent_guardian";
    const activeParentNameParts = activeParentRows.map((entry) =>
      splitFullName(entry.source.name),
    );
    const familySurname =
      readText(family.surname) ||
      activeParentNameParts
        .map((parts) => parts.lastName)
        .find((lastName) => Boolean(lastName)) ||
      (!isSingleParentGuardianMode
        ? splitFullName(scheduleForm.name).lastName ||
          readText(inheritedApplicant.familyName) ||
          readText(fallbackApplicant.familyName)
        : "");
    if (!familySurname) {
      throw new Error(
        "Family surname is required before submitting the application.",
      );
    }

    const primaryActiveParentName = activeParentRows
      .map((entry) => readText(entry.source.name))
      .find((name) => Boolean(name));
    const contactName =
      primaryActiveParentName ||
      (!isSingleParentGuardianMode ? readText(scheduleForm.name) : "");
    const contactParts = splitFullName(contactName);
    const contactFirstName = contactParts.firstName;
    const contactLastName = contactParts.lastName || familySurname;
    if (!contactFirstName || !contactLastName) {
      throw new Error("Primary contact first and last name are required.");
    }
    const contactEmail = pickFirstValidEmail([
      profile?.email,
      ...activeParentRows.map((entry) => entry.source.email),
      ...(!isSingleParentGuardianMode ? [scheduleForm.email] : []),
    ]);
    if (!contactEmail) {
      throw new Error(
        "A valid contact email is required before submitting the application.",
      );
    }
    const contactPhoneCandidate = pickFirstValidPhone([
      family.homePhone,
      ...activeParentRows.map((entry) => entry.source.cellPhone),
      ...(!isSingleParentGuardianMode ? [
        scheduleForm.phone,
        asRecord(inheritedApplicant.primaryContact).phone,
        inheritedApplicant.homePhone,
        asRecord(fallbackApplicant.primaryContact).phone,
        fallbackApplicant.homePhone,
      ] : []),
    ]);
    if (!contactPhoneCandidate) {
      throw new Error(
        "A parent or home phone number must include a valid phone number.",
      );
    }
    const contactPhone = contactPhoneCandidate;

    const submittedParentRows = activeParentRows
      .map((entry) => {
        if (!activeParentKeys.includes(entry.relation)) return null;
        const title = readText(entry.source.title);
        const split = splitFullName(entry.source.name);
        const emailCandidate = readText(entry.source.email);
        const phoneCandidate = readText(entry.source.cellPhone);
        const occupation = readText(entry.source.occupation);
        const firmName = readText(entry.source.firmName);
        const hasExplicitParentIdentity =
          Boolean(split.firstName || split.lastName);
        const hasExplicitParentContactOrWork =
          Boolean(title || emailCandidate || phoneCandidate || occupation || firmName);
        if (!hasExplicitParentIdentity && entry.source.isDeceasedOrNoLongerLegalGuardian) return null;
        if (!hasExplicitParentIdentity && !hasExplicitParentContactOrWork) return null;
        const firstName = split.firstName || contactFirstName;
        const lastName = split.lastName || contactLastName || familySurname;
        if (!firstName || !lastName) {
          throw new Error(
            `${entry.relation === "father" ? "Father's" : "Mother's"} first and last name are required.`,
          );
        }
        return {
          title: title || undefined,
          firstName,
          lastName,
          maidenName:
            entry.relation === "mother"
              ? readText(entry.source.maidenName) || undefined
              : undefined,
          relation: entry.relation,
          email: isValidEmailAddress(emailCandidate)
            ? emailCandidate
            : undefined,
          phone: normalizePhoneOptional(phoneCandidate) ?? contactPhone,
          occupation: occupation || undefined,
          firm: firmName || undefined,
          hasSeparateAddress: entry.source.hasSeparateAddress || undefined,
          isDeceasedOrNoLongerLegalGuardian:
            entry.source.isDeceasedOrNoLongerLegalGuardian || undefined,
          sepHomePhone: readText(entry.source.sepHomePhone) || undefined,
          sepAddress: readText(entry.source.sepAddress) || undefined,
          sepCity: readText(entry.source.sepCity) || undefined,
          sepState: readText(entry.source.sepState) || undefined,
          sepZip: readText(entry.source.sepZip) || undefined,
        };
      })
      .filter((parent): parent is NonNullable<typeof parent> =>
        Boolean(parent),
      );

    const sourceStudents = students.filter((student) => {
      const hasAnyName = Boolean(
        readText(student.firstName) ||
          readText(student.lastName) ||
          readText(student.calledBy),
      );
      const hasAnyGrade = Boolean(
        readText(student.grade) || readText(student.currentGrade),
      );
      return hasAnyName || hasAnyGrade;
    });
    if (!sourceStudents.length) {
      throw new Error(
        "At least one student with a name and grade is required.",
      );
    }
    const children = sourceStudents.map((student) => {
      const firstName =
        readText(student.firstName) || readText(student.calledBy);
      const lastName = readText(student.lastName) || familySurname;
      if (!firstName || !lastName) {
        throw new Error(
          "Each student must include first and last name before submitting.",
        );
      }
      const gradeLevel = resolveGradeLevelToken(
        student.grade || student.currentGrade,
      );
      const currentGrade = resolveGradeLevelToken(
        student.currentGrade || student.grade,
      );
      const gender = resolveStudentGenderToken(student.gender);
      const principalPhoneRaw = readText(student.schoolPhone);
      const physicianPhoneRaw = readText(student.pediatricianPhone);
      const dentistPhoneRaw = readText(student.dentistPhone);
      return {
        childInfo: {
          firstName,
          middleName: readText(student.middleName) || undefined,
          lastName,
          hebrewFirstName: readText(student.hebrewFirst) || undefined,
          hebrewMiddleName: readText(student.hebrewMiddle) || undefined,
          hebrewLastName: readText(student.hebrewLast) || undefined,
          calledBy: readText(student.calledBy) || undefined,
          dateOfBirth: toUnixSecondsFromDateInput(student.englishBirthDate),
          hebrewDateOfBirth: readText(student.hebrewBirthDate) || undefined,
          gender,
          currentGrade,
          placeOfBirth: formatBirthPlaceForSubmission(student),
          languageAtHome: readText(student.language) || undefined,
          currentSchool: readText(student.currentSchool) || undefined,
          principalName: readText(student.schoolPrincipal) || undefined,
          principalPhone: normalizePhoneOptional(principalPhoneRaw),
        },
        gradeApplyingFor: gradeLevel,
        schoolDivision: resolveSchoolDivisionToken(gradeLevel, gender),
        previousSchool: readText(student.currentSchool) || undefined,
        healthInfo: {
          physicianName: readText(student.pediatrician) || undefined,
          physicianPhone: normalizePhoneOptional(physicianPhoneRaw),
          dentistName: readText(student.dentist) || undefined,
          dentistPhone: normalizePhoneOptional(dentistPhoneRaw),
        },
      };
    });

    const academicYear = normalizeAcademicYearForSubmission(
      sourceStudents[0]?.applyingYear || ACADEMIC_YEAR_FALLBACK,
    );
    const inheritedPrimaryContact = asRecord(inheritedApplicant.primaryContact);
    const fallbackPrimaryContact = asRecord(fallbackApplicant.primaryContact);
    const inheritedAddress = asRecord(inheritedPrimaryContact.address);
    const fallbackAddress = asRecord(fallbackPrimaryContact.address);
    const street =
      readText(family.address) ||
      readText(inheritedAddress.street) ||
      readText(fallbackAddress.street);
    const city =
      readText(family.city) ||
      readText(inheritedAddress.city) ||
      readText(fallbackAddress.city);
    const state =
      readText(family.state) ||
      readText(inheritedAddress.state) ||
      readText(fallbackAddress.state);
    if (!street || !city || !state) {
      throw new Error(
        "Family address, city, and state are required before submitting.",
      );
    }
    const zipCandidate =
      readText(family.zip) ||
      readText(inheritedAddress.zipCode) ||
      readText(fallbackAddress.zipCode);
    return {
      applicant: {
        familyName: familySurname,
        primaryContact: {
          firstName: contactFirstName,
          lastName: contactLastName,
          email: contactEmail,
          phone: contactPhone,
          address: {
            street,
            city,
            state,
            zipCode: normalizeZipRequired(zipCandidate, "Family ZIP code"),
          },
        },
        homePhone: contactPhone,
        parents: submittedParentRows,
        householdComposition: parentGuardianHouseholdComposition,
        synagogue: readText(family.shulAffiliation) || undefined,
        rav: readText(family.rav) || undefined,
        maritalStatus: readText(family.maritalStatus) || undefined,
        adoptedChild: sourceStudents.some(
          (student) => student.isAdopted === true,
        )
          ? "yes"
          : "no",
        stepParent: sourceStudents.some(
          (student) => student.isStepParent === true,
        )
          ? "yes"
          : "no",
        paternalGrandparents: buildGrandparentPayload(paternalGrandparents),
        maternalGrandparents: buildGrandparentPayload(maternalGrandparents),
      },
      academicYear,
      children,
    };
  };

  const persistUploadedDocuments = async (submittedApplication: unknown) => {
    const applicationRecord = asRecord(submittedApplication);
    const applicationId = readText(applicationRecord.id);
    if (!applicationId) return;

    const uploads = Object.entries(documentFiles).filter(
      ([, file]) => file instanceof File,
    );
    if (!uploads.length) return;

    const ownerId = readText(profile?.userSub) || "unknown";
    const childApplicationIds = asRecordArray(applicationRecord.children)
      .map((child) => readText(child.id))
      .filter(Boolean);
    const submittedStudentIds = students
      .filter((student) => {
        const hasAnyName = Boolean(
          readText(student.firstName) ||
            readText(student.lastName) ||
            readText(student.calledBy),
        );
        const hasAnyGrade = Boolean(
          readText(student.grade) || readText(student.currentGrade),
        );
        return hasAnyName || hasAnyGrade;
      })
      .map((student) => student.id);
    const childApplicationIdByStudentId = new Map<string, string>();
    submittedStudentIds.forEach((studentId, index) => {
      const childApplicationId = childApplicationIds[index];
      if (childApplicationId) {
        childApplicationIdByStudentId.set(studentId, childApplicationId);
      }
    });

    for (const [slotKey, file] of uploads) {
      if (!(file instanceof File)) continue;
      const { studentId, baseSlotId } = parseDocumentSlotKey(slotKey);
      const category = DOCUMENT_CATEGORY_BY_SLOT[baseSlotId] ?? "other";
      const storageSlotToken = studentId
        ? `${baseSlotId}-${sanitizeStorageToken(studentId)}`
        : baseSlotId;
      const storagePath = buildAdmissionsUploadPath(
        applicationId,
        ownerId,
        storageSlotToken,
        file.name,
      );
      const childIdForUpload = studentId
        ? childApplicationIdByStudentId.get(studentId)
        : undefined;

      await uploadData({
        path: storagePath,
        data: file,
        options: {
          contentType: readText(file.type) || undefined,
        },
      }).result;

      await dispatch(
        domainRouterApi.endpoints.addAdmissionsDocument.initiate({
          applicationId,
          document: {
            resourceId: storagePath,
            category,
            fileName: file.name,
            contentType: readText(file.type) || undefined,
            sizeBytes: Number.isFinite(file.size) ? file.size : undefined,
            childApplicationIds: childIdForUpload
              ? [childIdForUpload]
              : childApplicationIds.length
                ? childApplicationIds
                : undefined,
            storageProvider: "s3",
            storageKey: storagePath,
          },
        }),
      ).unwrap();

      setDocumentFiles((previous) => {
        if (!(slotKey in previous)) return previous;
        const next = { ...previous };
        delete next[slotKey];
        return next;
      });
      setDocumentUploads((previous) => {
        if (!(slotKey in previous)) return previous;
        const next = { ...previous };
        delete next[slotKey];
        return next;
      });
    }
  };

  const requestInterviewSchedule = async (
    submittedApplication: unknown,
    scheduledSlots: number[],
  ) => {
    if (!familySelfSchedulingVisible) return;
    if (!scheduledSlots.length) return;
    const applicationRecord = asRecord(submittedApplication);
    const applicationId = readText(applicationRecord.id);
    if (!applicationId) return;
    const notes = readText(scheduleForm.notes);

    await dispatch(
      domainRouterApi.endpoints.requestAdmissionsInterviewSchedule.initiate({
        applicationId,
        preferredSlots: scheduledSlots,
        ...(notes ? { notes } : {}),
      }),
    ).unwrap();
  };

  const finalizeSubmissionSuccess = (mode: SubmissionCompletionMode) => {
    clearSubmissionFeedback();
    setDocumentFiles({});
    setDocumentUploads({});
    setSubmissionCompletionMode(mode);
    setCurrentStep(steps.length);
    scrollToTop();
  };

  const reportSubmissionFailure = (
    phase: SubmissionFailurePhase,
    error: unknown,
    options?: {
      applicationId?: string | null;
      applicationCode?: string | null;
      pendingPhase?: PendingSubmissionPhase | null;
      completionMode?: SubmissionCompletionMode;
      scheduledSlots?: number[];
      application?: Record<string, unknown>;
    },
  ) => {
    const fallbackReason =
      phase === "application"
        ? "We could not submit your application right now."
        : phase === "schedule"
          ? "Your application was created, but we could not save the requested interview schedule."
          : "Your application was created, but one or more documents could not be attached.";
    const detail = readSubmissionFailureReason(error, fallbackReason);
    const title =
      phase === "application"
        ? "Application submit failed."
        : phase === "schedule"
          ? "Application submitted, but interview scheduling failed."
          : "Application submitted, but document upload failed.";
    const pendingPhase = options?.pendingPhase ?? null;
    if (pendingPhase && options?.application && options?.completionMode) {
      setPendingSubmission({
        application: options.application,
        completionMode: options.completionMode,
        scheduledSlots: options.scheduledSlots ?? [],
        pendingPhase,
      });
      setSubmissionCompletionMode(options.completionMode);
      setCurrentStep(steps.length);
    } else {
      setPendingSubmission(null);
    }
    setSubmissionAlert({
      tone: phase === "application" ? "error" : "warning",
      title,
      detail,
      applicationId: options?.applicationId ?? null,
      applicationCode: options?.applicationCode ?? null,
      retryPhase: pendingPhase,
    });
    scrollToTop();
  };

  const continuePostSubmitFlow = async (
    submittedApplication: unknown,
    mode: SubmissionCompletionMode,
    scheduledSlots: number[],
    startingPhase: PendingSubmissionPhase = mode === "scheduled"
      ? "schedule"
      : "documents",
  ): Promise<boolean> => {
    const { application, applicationId, applicationCode } =
      extractSubmittedApplicationContext(submittedApplication);

    if (mode === "scheduled" && startingPhase === "schedule") {
      try {
        await requestInterviewSchedule(application, scheduledSlots);
      } catch (error) {
        reportSubmissionFailure("schedule", error, {
          application,
          applicationId,
          applicationCode,
          pendingPhase: "schedule",
          completionMode: mode,
          scheduledSlots,
        });
        return false;
      }
    }

    try {
      await persistUploadedDocuments(application);
    } catch (error) {
      reportSubmissionFailure("documents", error, {
        application,
        applicationId,
        applicationCode,
        pendingPhase: "documents",
        completionMode: mode,
        scheduledSlots,
      });
      return false;
    }

    finalizeSubmissionSuccess(mode);
    return true;
  };

  const submitCurrentApplication = async (mode: SubmissionCompletionMode) => {
    if (submissionInFlightRef.current) {
      return;
    }
    const submissionMode: SubmissionCompletionMode =
      mode === "scheduled" && !familySelfSchedulingVisible
        ? "unscheduled"
        : mode;
    const canSubmit =
      submissionMode === "scheduled"
        ? intakeActionState.canFinishScheduledSubmission
        : intakeActionState.canSubmitWithoutScheduling;

    if (!canSubmit) {
      markStepAttempted("Documents");
      markStepAttempted("Review");
      if (submissionMode === "scheduled") {
        markStepAttempted("Schedule");
      }
      if (firstSubmissionBlockingIssue) {
        focusAdvanceIssue(firstSubmissionBlockingIssue);
      }
      setSubmissionAlert({
        tone: "warning",
        title: "Required information missing",
        detail: firstSubmissionBlockingIssue
          ? submissionMode === "scheduled"
            ? `${firstSubmissionBlockingIssue.label} is required before you can submit this application or schedule an interview.`
            : `${firstSubmissionBlockingIssue.label} is required before you can submit this application.`
          : submissionMode === "scheduled"
            ? "Complete all required application and scheduling details before submitting."
            : "Complete all required application details before submitting.",
        applicationId: null,
        applicationCode: null,
        retryPhase: null,
      });
      return;
    }

    submissionInFlightRef.current = true;
    clearSubmissionFeedback();
    try {
      const personaClaim = await ensurePersonaTokenClaim("parent", {
        maxAttempts: 6,
        baseDelayMs: 250,
      });
      console.info("[auth] persona_submit_preflight", {
        targetPersona: personaClaim.targetPersona,
        ready: personaClaim.ready,
        attempts: personaClaim.attempts,
        groups: personaClaim.groups,
        reason: personaClaim.reason,
      });
      if (!personaClaim.ready) {
        throw new Error(
          "Account persona is still provisioning. Please wait a few seconds and try again.",
        );
      }

      const scheduledSlots: number[] = [];
      if (submissionMode === "scheduled") {
        scheduledSlots.push(
          ...schedulableStudents
            .map((student) => interviewSlotsByStudentId[student.id])
            .filter((slot): slot is number => Number.isFinite(slot)),
        );
        const fallbackScheduledUnix =
          selectedDate != null
            ? buildScheduledUnix(
                selectedDate,
                selectedTime,
                currentCalendarDate,
              )
            : null;
        if (!scheduledSlots.length && fallbackScheduledUnix != null) {
          scheduledSlots.push(fallbackScheduledUnix);
        }
      }
      const submittedApplication = await submitApplication.submit(
        buildSubmitApplicationInput(),
      );
      await deleteActiveParentAdmissionsDraft("submit");
      dispatch(
        domainRouterApi.util.invalidateTags([
          { type: "AdmissionsApplications" },
        ]),
      );
      await continuePostSubmitFlow(
        submittedApplication,
        submissionMode,
        scheduledSlots,
      );
    } catch (error) {
      reportSubmissionFailure("application", error);
    } finally {
      submissionInFlightRef.current = false;
    }
  };

  const retryPendingSubmission = async () => {
    if (!pendingSubmission) return;
    setIsRetryingSubmission(true);
    try {
      setSubmissionAlert(null);
      await continuePostSubmitFlow(
        pendingSubmission.application,
        pendingSubmission.completionMode,
        pendingSubmission.scheduledSlots,
        pendingSubmission.pendingPhase,
      );
    } finally {
      setIsRetryingSubmission(false);
    }
  };

  const nextStep = async () => {
    if (
      familySelfSchedulingVisible &&
      steps[currentStep] === "Schedule" &&
      currentStep === steps.length - 1 &&
      scheduleView === "success"
    ) {
      await submitCurrentApplication("scheduled");
      return;
    }
    if (!familySelfSchedulingVisible && steps[currentStep] === "Review") {
      await submitCurrentApplication("unscheduled");
      return;
    }
    if (currentStep < steps.length) {
      if (!intakeActionState.canAdvanceStep) {
        markStepAttempted(currentStepName);
        const firstAdvanceIssue = intakeActionState.advanceIssues[0];
        if (firstAdvanceIssue) {
          focusAdvanceIssue(firstAdvanceIssue);
        }
        return;
      }
      clearSubmissionFeedback();
      const nextStepIndex = currentStep + 1;
      clearPendingParentAdmissionsDraftAutosave();
      const saved = await saveParentAdmissionsDraft("manual", {
        currentStepOverride: nextStepIndex,
      });
      if (!saved) {
        return;
      }
      setCurrentStep(nextStepIndex);
      scrollToTop();
    }
  };

  const submitWithoutScheduling = async () => {
    await submitCurrentApplication("unscheduled");
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep((prev) => prev - 1);
      scrollToTop();
    }
  };

  const openSchedulerForStudent = (index: number) => {
    if (!familySelfSchedulingVisible) return;
    const target = schedulableStudents[index];
    if (!target) return;
    setScheduleStudentIndex(index);
    setTimezoneOpen(false);

    const scheduledUnix = interviewSlotsByStudentId[target.id];
    if (Number.isFinite(scheduledUnix)) {
      const scheduledDate = new Date(scheduledUnix * 1000);
      if (!Number.isNaN(scheduledDate.getTime())) {
        setCurrentCalendarDate(
          new Date(scheduledDate.getFullYear(), scheduledDate.getMonth(), 1),
        );
        setSelectedDate(scheduledDate.getDate());
        setSelectedTime(toScheduleTime(scheduledDate));
        setScheduleView("form");
        return;
      }
    }

    setCurrentCalendarDate(createDefaultCalendarDate());
    setSelectedDate(null);
    setSelectedTime(null);
    setScheduleView("calendar");
  };

  const handleScheduleEvent = () => {
    if (!familySelfSchedulingVisible) return;
    if (!activeScheduleStudent || !intakeActionState.canScheduleInterview) {
      markStepAttempted("Schedule");
      const firstScheduleIssue = intakeActionState.advanceIssues[0];
      if (firstSubmissionBlockingIssue) {
        markStepAttempted("Documents");
        markStepAttempted("Review");
        focusAdvanceIssue(firstSubmissionBlockingIssue);
      } else if (firstScheduleIssue) {
        focusAdvanceIssue(firstScheduleIssue);
      }
      return;
    }
    const scheduledUnix = buildScheduledUnix(
      selectedDate,
      selectedTime,
      currentCalendarDate,
    );
    if (scheduledUnix == null) return;

    const nextSlots = {
      ...interviewSlotsByStudentId,
      [activeScheduleStudent.id]: scheduledUnix,
    };
    setInterviewSlotsByStudentId(nextSlots);
    setTimezoneOpen(false);

    const nextUnscheduledIndex = schedulableStudents.findIndex(
      (student) => !nextSlots[student.id],
    );
    if (nextUnscheduledIndex >= 0) {
      setScheduleStudentIndex(nextUnscheduledIndex);
      setCurrentCalendarDate(createDefaultCalendarDate());
      setSelectedDate(null);
      setSelectedTime(null);
      setScheduleView("calendar");
      return;
    }

    setScheduleView("success");
  };

  const applyFieldsFromSource = (
    target: StudentData,
    source: StudentData,
    fields: StudentTextField[],
  ): StudentData => {
    const next = { ...target };
    fields.forEach((field) => {
      next[field] = source[field];
    });
    return next;
  };

  const clearStudentFields = (
    target: StudentData,
    fields: StudentTextField[],
  ): StudentData => {
    const next = { ...target };
    fields.forEach((field) => {
      next[field] = "";
    });
    return next;
  };

  const setStudentSectionPrefillState = (
    studentId: string,
    section: keyof StudentSectionPrefillState,
    enabled: boolean,
  ) => {
    setStudentSectionPrefill((previous) => ({
      ...previous,
      [studentId]: {
        ...createDefaultSiblingPrefillState(),
        ...(previous[studentId] ?? {}),
        [section]: enabled,
      },
    }));
    setStudents((previous) => {
      const source = previous[0];
      return previous.map((entry, index) => {
        if (entry.id !== studentId) return entry;
        if (index === 0) return entry;
        if (section === "currentSchooling") {
          if (enabled && source)
            return applyFieldsFromSource(
              entry,
              source,
              CURRENT_SCHOOLING_FIELDS,
            );
          return clearStudentFields(entry, CURRENT_SCHOOLING_FIELDS);
        }
        if (enabled && source)
          return applyFieldsFromSource(entry, source, MEDICAL_CONTACT_FIELDS);
        return clearStudentFields(entry, MEDICAL_CONTACT_FIELDS);
      });
    });
  };

  const updateStudent = (id: string, field: keyof StudentData, value: any) => {
    setStudents((prev) =>
      prev.map((s) => (s.id === id ? { ...s, [field]: value } : s)),
    );
  };

  const updateBirthCountry = (studentId: string, value: string) => {
    setStudents((previous) =>
      previous.map((student) => {
        if (student.id !== studentId) return student;
        const birthCountry = resolveCountryValue(value) || USA_COUNTRY_LABEL;
        return {
          ...student,
          birthCountry,
          birthState:
            birthCountry === USA_COUNTRY_LABEL ? student.birthState : "",
        };
      }),
    );
  };

  const clearHebrewBirthDateOverride = (studentId: string) => {
    setHebrewBirthDateOverrides((previous) => {
      if (!previous[studentId]) return previous;
      const next = { ...previous };
      delete next[studentId];
      return next;
    });
  };

  const setHebrewBirthDateOverride = (
    studentId: string,
    overridden: boolean,
  ) => {
    if (!overridden) {
      clearHebrewBirthDateOverride(studentId);
      return;
    }

    setHebrewBirthDateOverrides((previous) => {
      if (previous[studentId]) return previous;
      return { ...previous, [studentId]: true };
    });
  };

  const updateEnglishBirthDate = (studentId: string, value: string) => {
    const isOverridden = hebrewBirthDateOverrides[studentId] === true;
    updateStudent(studentId, "englishBirthDate", value);

    if (!value) {
      updateStudent(studentId, "hebrewBirthDate", "");
      clearHebrewBirthDateOverride(studentId);
      return;
    }

    if (isOverridden) {
      return;
    }

    const derivedHebrewDate = parseEnglishBirthDateToHebrew(value);
    if (derivedHebrewDate) {
      updateStudent(studentId, "hebrewBirthDate", derivedHebrewDate);
    }
  };

  const updateHebrewBirthDate = (studentId: string, value: string) => {
    setHebrewBirthDateOverride(studentId, true);
    updateStudent(studentId, "hebrewBirthDate", value);
  };

  const updateHebrewBirthDateFromPicker = (
    studentId: string,
    value: string,
  ) => {
    setHebrewBirthDateOverride(studentId, true);
    updateStudent(studentId, "hebrewBirthDate", value);
  };

  const addStudent = () => {
    const nextStudent = createEmptyStudent();
    setHebrewBirthDateOverrides((previous) => {
      const next = { ...previous };
      delete next[nextStudent.id];
      return next;
    });
    setStudents((previous) => {
      const source = previous[0] ?? createEmptyStudent();
      let withSchooling = applyFieldsFromSource(
        nextStudent,
        source,
        CURRENT_SCHOOLING_FIELDS,
      );
      withSchooling = applyFieldsFromSource(
        withSchooling,
        source,
        MEDICAL_CONTACT_FIELDS,
      );
      return [...previous, withSchooling];
    });
    setStudentSectionPrefill((previous) => ({
      ...previous,
      [nextStudent.id]: createDefaultSiblingPrefillState(),
    }));
  };

  const removeStudent = (id: string) => {
    if (students.length > 1) {
      setStudents((prev) => prev.filter((s) => s.id !== id));
      setStudentSectionPrefill((previous) => {
        const next = { ...previous };
        delete next[id];
        return next;
      });
      setHebrewBirthDateOverrides((previous) => {
        const next = { ...previous };
        delete next[id];
        return next;
      });
    }
  };

  const handleHebrewChar = (char: string) => {
    if (activeKeyboard) {
      const student = students.find((s) => s.id === activeKeyboard.id);
      if (student) {
        const currentVal = (student[activeKeyboard.field] as string) || "";
        updateStudent(
          activeKeyboard.id,
          activeKeyboard.field,
          currentVal + char,
        );
      }
    }
  };

  const updateFamily = (field: keyof FamilyData, value: any) =>
    setFamily((prev) => ({ ...prev, [field]: value }));
  const updateParent = (
    parent: "father" | "mother",
    field: keyof ParentData,
    value: any,
  ) =>
    setFamily((prev) => ({
      ...prev,
      [parent]: {
        ...prev[parent],
        [field]:
          field === "hasSeparateAddress" ||
          field === "isDeceasedOrNoLongerLegalGuardian"
            ? value === "true" || value === true
            : value,
      },
    }));

  const applyParentGuardianHouseholdComposition = (
    mode: "two_parent" | "single_parent_guardian",
    primaryKey: ParentGuardianFormKey,
  ) => {
    updateFamily('householdComposition', normalizeParentGuardianHouseholdComposition({
      mode,
      primaryKey,
      primaryRole: primaryKey,
    }));
  };

  const setDocumentUpload = (slotId: string, fileName: string, file?: File) => {
    setDocumentUploads((previous) => ({
      ...previous,
      [slotId]: fileName,
    }));
    setDocumentFiles((previous) => ({
      ...previous,
      [slotId]: file,
    }));
  };

  const handleDocumentFiles = (
    slotId: string,
    files: FileList | File[] | null,
  ) => {
    if (!files || files.length === 0) return;
    const first = files[0];
    if (!first?.name) return;
    setDocumentUpload(slotId, first.name, first);
  };

  const openDocumentPicker = (slotId: string) => {
    const target = fileInputRefs.current[slotId];
    if (!target) return;
    const pickerTarget = target as HTMLInputElement & {
      showPicker?: () => void;
    };
    if (typeof pickerTarget.showPicker === "function") {
      try {
        pickerTarget.showPicker();
        return;
      } catch {
        // Fall through to click() when showPicker is blocked or unsupported.
      }
    }
    target.click();
  };

  const updateGrandparents = (
    type: "paternal" | "maternal",
    field: keyof GrandparentData,
    value: string,
  ) => {
    if (type === "paternal")
      setPaternalGrandparents((prev) => ({ ...prev, [field]: value }));
    else setMaternalGrandparents((prev) => ({ ...prev, [field]: value }));
  };

  useEffect(() => {
    if (selectedApplicationId) {
      parentAdmissionsDraftHydratedRef.current = false;
      return;
    }
    if (!applicantId) return;
    if (parentAdmissionsDraftHydratedRef.current) return;
    if (
      parentAdmissionsDraftQuery.isLoading ||
      parentAdmissionsDraftQuery.isFetching
    ) {
      return;
    }

    parentAdmissionsDraftHydratedRef.current = true;
    const draft = parentAdmissionsDraftQuery.data?.draft ?? null;
    if (draft) {
      applyParentAdmissionsDraft(draft);
      return;
    }
    setDraftFeedback({
      state: "idle",
      message: "Automatic saving enabled",
    });
  }, [
    applicantId,
    applyParentAdmissionsDraft,
    parentAdmissionsDraftQuery.data,
    parentAdmissionsDraftQuery.isFetching,
    parentAdmissionsDraftQuery.isLoading,
    selectedApplicationId,
  ]);

  useEffect(() => {
    if (!familySelfSchedulingVisible) return;
    const suppressedScheduleState =
      parentAdmissionsSuppressedScheduleRef.current;
    if (!suppressedScheduleState) return;

    setScheduleView(suppressedScheduleState.scheduleView);
    setSelectedDate(suppressedScheduleState.selectedDate);
    setSelectedTime(suppressedScheduleState.selectedTime);
    setScheduleForm(suppressedScheduleState.scheduleForm);
    setScheduleStudentIndex(suppressedScheduleState.scheduleStudentIndex);
    setInterviewSlotsByStudentId(
      suppressedScheduleState.interviewSlotsByStudentId,
    );
    setCurrentCalendarDate(
      readDateFromDraft(suppressedScheduleState.currentCalendarDate),
    );
    setSelectedTimezone(suppressedScheduleState.selectedTimezone);
    parentAdmissionsSuppressedScheduleRef.current = null;
  }, [familySelfSchedulingVisible]);

  useEffect(() => {
    if (!applicantId || selectedApplicationId) return;
    if (!parentAdmissionsDraftHydratedRef.current) return;
    if (currentStep >= steps.length) return;
    const timeout = window.setTimeout(() => {
      parentAdmissionsDraftAutosaveTimeoutRef.current = null;
      void saveParentAdmissionsDraft("autosave");
    }, 1200);
    parentAdmissionsDraftAutosaveTimeoutRef.current = timeout;
    return () => {
      window.clearTimeout(timeout);
      if (parentAdmissionsDraftAutosaveTimeoutRef.current === timeout) {
        parentAdmissionsDraftAutosaveTimeoutRef.current = null;
      }
    };
  }, [
    applicantId,
    currentStep,
    saveParentAdmissionsDraft,
    selectedApplicationId,
    steps.length,
  ]);

  useEffect(() => {
    if (!shouldSuppressFamilyScheduling) return;
    if (submissionCompletionMode || pendingSubmission) return;
    if (currentStep < steps.length) return;
    setCurrentStep(Math.max(steps.length - 1, 0));
  }, [
    currentStep,
    pendingSubmission,
    shouldSuppressFamilyScheduling,
    steps.length,
    submissionCompletionMode,
  ]);

  useEffect(() => {
    if (!selectedApplicationId && hydratedApplicationId) {
      resetParentAdmissionsDraftState();
      setHydratedApplicationId(null);
      siblingInheritanceHydratedRef.current = false;
      parentAdmissionsDraftHydratedRef.current = false;
    }
  }, [
    selectedApplicationId,
    hydratedApplicationId,
    resetParentAdmissionsDraftState,
  ]);

  useEffect(() => {
    if (
      selectedApplicationId &&
      hydratedApplicationId &&
      selectedApplicationId !== hydratedApplicationId
    ) {
      setHydratedApplicationId(null);
    }
  }, [selectedApplicationId, hydratedApplicationId]);

  useEffect(() => {
    if (!hasSibling) {
      siblingInheritanceHydratedRef.current = false;
    }
  }, [hasSibling]);

  useEffect(() => {
    setStudentSectionPrefill((previous) => {
      const next: Record<string, StudentSectionPrefillState> = {};
      students.forEach((student, index) => {
        if (index === 0) return;
        next[student.id] =
          previous[student.id] ?? createDefaultSiblingPrefillState();
      });
      return next;
    });
  }, [students]);

  useEffect(() => {
    setScheduleStudentIndex((previous) => {
      const maxIndex = Math.max(schedulableStudents.length - 1, 0);
      return previous > maxIndex ? maxIndex : previous;
    });
    setInterviewSlotsByStudentId((previous) => {
      const activeIds = new Set(
        schedulableStudents.map((student) => student.id),
      );
      let changed = false;
      const next: Record<string, number> = {};
      Object.entries(previous).forEach(([studentId, slot]) => {
        if (activeIds.has(studentId)) {
          next[studentId] = slot;
        } else {
          changed = true;
        }
      });
      return changed ? next : previous;
    });
  }, [schedulableStudents]);

  useEffect(() => {
    if (!selectedApplicationId) return;
    if (selectedApplicationQuery.isLoading) return;
    if (selectedApplicationQuery.error) return;
    if (!selectedApplicationQuery.data) return;
    if (hydratedApplicationId === selectedApplicationId) return;

    const mapped = mapAdmissionsApplicationToIntakeState(
      selectedApplicationQuery.data,
    );
    setCurrentStep(0);
    setStudents(mapped.students);
    setHebrewBirthDateOverrides(mapped.hebrewBirthDateOverrides);
    setStudentSectionPrefill({});
    setHasSibling(mapped.hasSibling);
    setFamily(mapped.family);
    setPaternalGrandparents(mapped.paternalGrandparents);
    setMaternalGrandparents(mapped.maternalGrandparents);
    setDocuments(createInitialDocumentsData());
    setDocumentUploads({});
    setDocumentFiles({});
    setConsentChecked(false);
    setScheduleView(mapped.scheduleView);
    setSelectedDate(mapped.selectedDate);
    setSelectedTime(mapped.selectedTime);
    setScheduleForm(mapped.scheduleForm);
    setScheduleStudentIndex(0);
    setInterviewSlotsByStudentId(mapped.interviewSlotsByStudentId);
    setCurrentCalendarDate(mapped.currentCalendarDate);
    setTimezoneOpen(false);
    setSelectedTimezone("Eastern Time");
    setActiveKeyboard(null);
    setActiveDatePicker(null);
    setHydratedApplicationId(selectedApplicationId);
    clearSubmissionFeedback();
    setSubmissionCompletionMode(null);
  }, [
    selectedApplicationId,
    selectedApplicationQuery.isLoading,
    selectedApplicationQuery.error,
    selectedApplicationQuery.data,
    hydratedApplicationId,
  ]);

  useEffect(() => {
    if (!hasSibling) return;
    if (selectedApplicationId) return;
    if (siblingInheritanceHydratedRef.current) return;
    if (selectedApplicationQuery.isLoading || selectedApplicationQuery.error)
      return;
    if (
      applicantApplicationsQuery.isLoading ||
      applicantApplicationsQuery.error
    )
      return;
    if (!Object.keys(latestApplicantApplicationRecord).length) return;

    const mapped = mapAdmissionsApplicationToIntakeState(
      latestApplicantApplicationRecord,
    );
    setFamily((previous) => ({
      ...previous,
      surname: previous.surname || mapped.family.surname,
      homePhone: previous.homePhone || mapped.family.homePhone,
      address: previous.address || mapped.family.address,
      city: previous.city || mapped.family.city,
      state: previous.state || mapped.family.state,
      zip: previous.zip || mapped.family.zip,
      householdComposition: isDefaultParentGuardianHouseholdComposition(
        previous.householdComposition,
      )
        ? mapped.family.householdComposition
        : previous.householdComposition,
      father: {
        ...previous.father,
        title: previous.father.title || mapped.family.father.title,
        name: previous.father.name || mapped.family.father.name,
        maidenName:
          previous.father.maidenName || mapped.family.father.maidenName,
        cellPhone: previous.father.cellPhone || mapped.family.father.cellPhone,
        email: previous.father.email || mapped.family.father.email,
        occupation:
          previous.father.occupation || mapped.family.father.occupation,
        firmName: previous.father.firmName || mapped.family.father.firmName,
        hasSeparateAddress:
          previous.father.hasSeparateAddress ||
          mapped.family.father.hasSeparateAddress,
        isDeceasedOrNoLongerLegalGuardian:
          previous.father.isDeceasedOrNoLongerLegalGuardian ||
          mapped.family.father.isDeceasedOrNoLongerLegalGuardian,
        sepHomePhone:
          previous.father.sepHomePhone || mapped.family.father.sepHomePhone,
        sepAddress:
          previous.father.sepAddress || mapped.family.father.sepAddress,
        sepCity: previous.father.sepCity || mapped.family.father.sepCity,
        sepState: previous.father.sepState || mapped.family.father.sepState,
        sepZip: previous.father.sepZip || mapped.family.father.sepZip,
      },
      mother: {
        ...previous.mother,
        title: previous.mother.title || mapped.family.mother.title,
        name: previous.mother.name || mapped.family.mother.name,
        maidenName:
          previous.mother.maidenName || mapped.family.mother.maidenName,
        cellPhone: previous.mother.cellPhone || mapped.family.mother.cellPhone,
        email: previous.mother.email || mapped.family.mother.email,
        occupation:
          previous.mother.occupation || mapped.family.mother.occupation,
        firmName: previous.mother.firmName || mapped.family.mother.firmName,
        hasSeparateAddress:
          previous.mother.hasSeparateAddress ||
          mapped.family.mother.hasSeparateAddress,
        isDeceasedOrNoLongerLegalGuardian:
          previous.mother.isDeceasedOrNoLongerLegalGuardian ||
          mapped.family.mother.isDeceasedOrNoLongerLegalGuardian,
        sepHomePhone:
          previous.mother.sepHomePhone || mapped.family.mother.sepHomePhone,
        sepAddress:
          previous.mother.sepAddress || mapped.family.mother.sepAddress,
        sepCity: previous.mother.sepCity || mapped.family.mother.sepCity,
        sepState: previous.mother.sepState || mapped.family.mother.sepState,
        sepZip: previous.mother.sepZip || mapped.family.mother.sepZip,
      },
      maritalStatus: previous.maritalStatus || mapped.family.maritalStatus,
      shulAffiliation:
        previous.shulAffiliation || mapped.family.shulAffiliation,
      rav: previous.rav || mapped.family.rav,
    }));
    setPaternalGrandparents((previous) => ({
      ...previous,
      ...mapped.paternalGrandparents,
      lastName: previous.lastName || mapped.paternalGrandparents.lastName,
      grandfatherName:
        previous.grandfatherName || mapped.paternalGrandparents.grandfatherName,
      grandmotherName:
        previous.grandmotherName || mapped.paternalGrandparents.grandmotherName,
      city: previous.city || mapped.paternalGrandparents.city,
      state: previous.state || mapped.paternalGrandparents.state,
      zip: previous.zip || mapped.paternalGrandparents.zip,
      maritalStatus:
        previous.maritalStatus || mapped.paternalGrandparents.maritalStatus,
      phone: previous.phone || mapped.paternalGrandparents.phone,
    }));
    setMaternalGrandparents((previous) => ({
      ...previous,
      ...mapped.maternalGrandparents,
      lastName: previous.lastName || mapped.maternalGrandparents.lastName,
      grandfatherName:
        previous.grandfatherName || mapped.maternalGrandparents.grandfatherName,
      grandmotherName:
        previous.grandmotherName || mapped.maternalGrandparents.grandmotherName,
      city: previous.city || mapped.maternalGrandparents.city,
      state: previous.state || mapped.maternalGrandparents.state,
      zip: previous.zip || mapped.maternalGrandparents.zip,
      maritalStatus:
        previous.maritalStatus || mapped.maternalGrandparents.maritalStatus,
      phone: previous.phone || mapped.maternalGrandparents.phone,
    }));
    setScheduleForm((previous) => ({
      ...previous,
      name: previous.name || mapped.scheduleForm.name,
      email: previous.email || mapped.scheduleForm.email,
      phone: previous.phone || mapped.scheduleForm.phone,
    }));
    siblingInheritanceHydratedRef.current = true;
  }, [
    hasSibling,
    selectedApplicationId,
    selectedApplicationQuery.isLoading,
    selectedApplicationQuery.error,
    applicantApplicationsQuery.isLoading,
    applicantApplicationsQuery.error,
    latestApplicantApplicationRecord,
  ]);

  // Close timezone dropdown on click outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        timezoneRef.current &&
        !timezoneRef.current.contains(event.target as Node)
      ) {
        setTimezoneOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const navigateMonth = (direction: number) => {
    const newDate = navigateAdmissionsCalendarMonth(
      currentCalendarDate,
      direction,
    );
    if (isSameAdmissionsCalendarMonth(newDate, currentCalendarDate)) {
      return;
    }
    setCurrentCalendarDate(newDate);
    setSelectedDate(null); // Reset selection on month change
  };

  const selectedApplicationRequested = Boolean(selectedApplicationId);
  const selectedApplicationHydrated =
    selectedApplicationRequested &&
    hydratedApplicationId === selectedApplicationId;
  const selectedApplicationNotFound =
    selectedApplicationRequested &&
    !selectedApplicationQuery.isLoading &&
    !selectedApplicationQuery.error &&
    !selectedApplicationQuery.data;
  const selectedApplicationLoading =
    selectedApplicationRequested &&
    !selectedApplicationHydrated &&
    !selectedApplicationNotFound &&
    !selectedApplicationQuery.error;
  const selectedApplicationReadOnly =
    selectedApplicationHydrated && !selectedApplicationNotFound;
  const showAutofillFab = shouldRenderAdmissionsIntakeAutofillFab({
    isDevBuild: isDevRuntimeBuild(),
    isReadOnly: selectedApplicationReadOnly,
    currentStep,
    totalSteps: steps.length,
  });
  const selectedApplicationErrorMessage = selectedApplicationNotFound
    ? "We could not find that application. Please return to your applications list and try again."
    : selectedApplicationQuery.error
      ? "Unable to load the selected application right now. Please try again."
      : "";
  const currentBrowserBackDraft = useMemo(() => ({
    students,
    hebrewBirthDateOverrides,
    studentSectionPrefill,
    hasSibling,
    family,
    paternalGrandparents,
    maternalGrandparents,
    documents,
    documentUploads,
    documentFiles,
    consentChecked,
    gradeDiscrepancyAcknowledged,
    scheduleView,
    selectedDate,
    selectedTime,
    scheduleForm,
    interviewSlotsByStudentId,
  }), [
    consentChecked,
    documentFiles,
    documentUploads,
    documents,
    family,
    gradeDiscrepancyAcknowledged,
    hasSibling,
    hebrewBirthDateOverrides,
    interviewSlotsByStudentId,
    maternalGrandparents,
    paternalGrandparents,
    scheduleForm,
    scheduleView,
    selectedDate,
    selectedTime,
    studentSectionPrefill,
    students,
  ]);
  const shouldWarnBrowserBack = !selectedApplicationReadOnly
    && !submissionCompletionMode
    && currentStep < steps.length
    && hasUnsavedFormData(currentBrowserBackDraft, initialBrowserBackDraftRef.current);
  const browserBackGuard = useUnsavedBrowserNavigationGuard({
    enabled: shouldWarnBrowserBack,
  });

  if (selectedApplicationRequested && selectedApplicationLoading) {
    return (
      <div className="max-w-5xl mx-auto p-0 md:px-6 md:pt-10 md:pb-32">
        <div
          className="bg-white border border-gray-100 rounded-2xl shadow-card px-6 py-8"
          data-testid="intake-selected-application-loading"
        >
          <h2 className="text-xl font-bold text-irongrey mb-2">
            Loading application
          </h2>
          <p className="text-sm text-irongrey/60">
            {selectedApplicationCode
              ? `Opening application ${selectedApplicationCode}...`
              : "Opening selected application..."}
          </p>
        </div>
      </div>
    );
  }

  if (selectedApplicationRequested && selectedApplicationErrorMessage) {
    return (
      <div className="max-w-5xl mx-auto p-0 md:px-6 md:pt-10 md:pb-32">
        <div
          className="bg-white border border-strawberry/30 rounded-2xl shadow-card px-6 py-8"
          data-testid="intake-selected-application-error"
        >
          <h2 className="text-xl font-bold text-irongrey mb-2">
            Application unavailable
          </h2>
          <EngineeringSupportBanner className="mb-5" />
          <p className="text-sm text-irongrey/70 mb-6">
            {selectedApplicationErrorMessage}
          </p>
          <a
            href="/parent/portal/applications"
            className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-primary text-white font-bold text-sm hover:opacity-90 transition-opacity"
          >
            <ArrowLeft size={16} />
            Back to Applications
          </a>
        </div>
      </div>
    );
  }

  // --- Render Steps ---

  const renderStudentsStep = () => (
    <div data-testid="parent-portal-intake-students-step">
      <StepHeader
        title="Student Information"
        description="Please enter details for each child applying."
      />
      <div className="mb-6 rounded-xl border border-primary/10 bg-primary/5 px-4 py-3">
        <p className="text-xs font-semibold text-irongrey/70">
          Fields marked <span className="text-primary">*</span> are required
          before you can continue.
        </p>
      </div>

      {/* Sibling Lookup */}
      <div className="bg-white border border-gray-200 rounded-2xl p-6 mb-8 shadow-card group">
        <InputLabel>SIBLING LOOKUP</InputLabel>
        <p className="text-sm text-irongrey/70 mb-4 font-medium transition-colors duration-500 group-hover:text-irongrey/90">
          Is this applicant a sibling of an existing student?
        </p>
        <div className="flex bg-gray-100 p-1 rounded-xl w-fit">
          <button
            onClick={() => setHasSibling(false)}
            className={`px-8 py-2 rounded-lg text-sm font-bold transition-all duration-200 ${
              hasSibling === false
                ? "bg-white text-primary shadow-sm"
                : "text-irongrey/40 hover:text-irongrey/60"
            }`}
          >
            No
          </button>
          <button
            onClick={() => setHasSibling(true)}
            className={`px-8 py-2 rounded-lg text-sm font-bold transition-all duration-200 ${
              hasSibling === true
                ? "bg-white text-primary shadow-sm"
                : "text-irongrey/40 hover:text-irongrey/60"
            }`}
          >
            Yes
          </button>
        </div>
      </div>

      {/* Students List */}
      <div className="space-y-8 mb-8">
        {students.map((student, index) => (
          <Card
            key={student.id}
            className="relative overflow-visible animate-in fade-in slide-in-from-bottom-4 duration-300"
          >
            {students.length > 1 && (
              <button
                onClick={() => removeStudent(student.id)}
                className="absolute top-6 right-6 text-irongrey/20 hover:text-strawberry transition-colors"
              >
                <Trash2 size={20} />
              </button>
            )}

            <SectionHeader icon={CheckSquare} title="THIS APPLICATION" />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
              <div>
                <InputLabel>Applying for school year</InputLabel>
                <Select
                  options={["2025-26", "2026-27", "2027-28"]}
                  value={student.applyingYear}
                  onChange={(val) =>
                    updateStudent(student.id, "applyingYear", val)
                  }
                />
              </div>
              <div>
                <InputLabel required>Applying for grade</InputLabel>
                <div
                  data-testid={index === 0 ? 'intake-student-applying-grade-select' : undefined}
                  data-validation-key={fieldKey("students", index, "grade")}
                >
                  <Select
                    options={[...APPLICATION_GRADE_OPTIONS]}
                    value={student.grade}
                    onChange={(val) => updateStudent(student.id, "grade", val)}
                    onBlur={() =>
                      markFieldTouched(
                        fieldKey("students", student.id, "grade"),
                      )
                    }
                    triggerClassName={getFieldIndicatorClassName(
                      fieldKey("students", student.id, "grade"),
                      "Students",
                      student.grade,
                    )}
                  />
                </div>
              </div>
            </div>

            <div className="border-t border-gray-100 my-8"></div>

            <SectionHeader icon={User} title="LEGAL NAME OF CHILD" />

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
              <div>
                <InputLabel required>First</InputLabel>
                <Input
                  data-validation-key={fieldKey("students", index, "firstName")}
                  value={student.firstName}
                  onChange={(e) =>
                    updateStudent(student.id, "firstName", e.target.value)
                  }
                  onBlur={() =>
                    markFieldTouched(
                      fieldKey("students", student.id, "firstName"),
                    )
                  }
                  className={getFieldIndicatorClassName(
                    fieldKey("students", student.id, "firstName"),
                    "Students",
                    student.firstName,
                  )}
                  data-testid={
                    index === 0 ? "intake-student-first-name-input" : undefined
                  }
                />
              </div>
              <div>
                <InputLabel>Middle</InputLabel>
                <Input
                  value={student.middleName}
                  onChange={(e) =>
                    updateStudent(student.id, "middleName", e.target.value)
                  }
                />
              </div>
              <div>
                <InputLabel required>Last</InputLabel>
                <Input
                  data-validation-key={fieldKey("students", index, "lastName")}
                  value={student.lastName}
                  onChange={(e) =>
                    updateStudent(student.id, "lastName", e.target.value)
                  }
                  onBlur={() =>
                    markFieldTouched(
                      fieldKey("students", student.id, "lastName"),
                    )
                  }
                  className={getFieldIndicatorClassName(
                    fieldKey("students", student.id, "lastName"),
                    "Students",
                    student.lastName,
                  )}
                  data-testid={index === 0 ? 'intake-student-last-name-input' : undefined}
                />
              </div>
            </div>

            <div className="mb-8">
              <InputLabel>HEBREW NAME (PLEASE WRITE IN HEBREW)</InputLabel>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-2">
                {["hebrewFirst", "hebrewMiddle", "hebrewLast"].map((field) => (
                  <div className="relative" key={field}>
                    <InputLabel>{field.replace("hebrew", "")}</InputLabel>
                    <div className="relative">
                      <Input
                        value={(student as any)[field]}
                        onChange={(e) =>
                          updateStudent(
                            student.id,
                            field as keyof StudentData,
                            e.target.value,
                          )
                        }
                      />
                      <button
                        onClick={() =>
                          setActiveKeyboard({
                            id: student.id,
                            field: field as keyof StudentData,
                          })
                        }
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-irongrey/30 hover:text-primary transition-colors"
                      >
                        <Keyboard size={16} />
                      </button>
                      {activeKeyboard?.id === student.id &&
                        activeKeyboard?.field === field && (
                          <HebrewKeyboard
                            onCharClick={handleHebrewChar}
                            onClose={() => setActiveKeyboard(null)}
                          />
                        )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="mb-8">
              <InputLabel>Name Child is Called By</InputLabel>
              <Input
                value={student.calledBy}
                onChange={(e) =>
                  updateStudent(student.id, "calledBy", e.target.value)
                }
              />
            </div>

            <div className="border-t border-gray-100 my-8"></div>
            <SectionHeader icon={User} title="BIRTH & IDENTITY" />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <InputLabel>English Birth Date</InputLabel>
                <Input
                  type="date"
                  value={student.englishBirthDate}
                  onChange={(e) =>
                    updateEnglishBirthDate(student.id, e.target.value)
                  }
                />
              </div>
              <div>
                <InputLabel>Hebrew Birth Date</InputLabel>
                <div className="relative">
                  <Input
                    placeholder="e.g. 15 Sivan 5776"
                    value={student.hebrewBirthDate}
                    onChange={(e) =>
                      updateHebrewBirthDate(student.id, e.target.value)
                    }
                  />
                  <button
                    onClick={() =>
                      setActiveDatePicker({
                        id: student.id,
                        field: "hebrewBirthDate",
                      })
                    }
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-irongrey/30 hover:text-primary transition-colors"
                  >
                    <Calendar size={16} />
                  </button>
                  {activeDatePicker?.id === student.id &&
                    activeDatePicker?.field === "hebrewBirthDate" && (
                      <HebrewDatePicker
                        value={student.hebrewBirthDate}
                        onSelect={(date) =>
                          updateHebrewBirthDateFromPicker(student.id, date)
                        }
                        onClose={() => setActiveDatePicker(null)}
                      />
                    )}
                </div>
              </div>
            </div>

            <div
              className={`grid grid-cols-1 ${isUnitedStatesCountry(student.birthCountry) ? "md:grid-cols-3" : "md:grid-cols-2"} gap-4 mb-4`}
            >
              <div>
                <InputLabel>Country</InputLabel>
                <Select
                  options={COUNTRY_OPTIONS}
                  value={student.birthCountry}
                  onChange={(value) => updateBirthCountry(student.id, value)}
                  searchable
                />
              </div>
              {isUnitedStatesCountry(student.birthCountry) ? (
                <div>
                  <InputLabel>State</InputLabel>
                  <Select
                    options={US_STATES}
                    value={student.birthState}
                    onChange={(value) =>
                      updateStudent(student.id, "birthState", value)
                    }
                    searchable
                  />
                </div>
              ) : null}
              <div>
                <InputLabel>City</InputLabel>
                <Input
                  value={student.birthCity}
                  onChange={(e) =>
                    updateStudent(student.id, "birthCity", e.target.value)
                  }
                />
              </div>
              <div>
                <InputLabel>Language Spoken at Home</InputLabel>
                <Input
                  value={student.language}
                  onChange={(e) =>
                    updateStudent(student.id, "language", e.target.value)
                  }
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
              <div>
                <InputLabel>Gender</InputLabel>
                <Select
                  options={["Male", "Female"]}
                  value={student.gender}
                  onChange={(val) => updateStudent(student.id, "gender", val)}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              <div className="flex flex-col justify-center">
                <span className="text-sm font-semibold text-irongrey mb-2">
                  Is child adopted?
                </span>
                <div className="flex gap-4">
                  <StyledRadioOption
                    name={`adopted-${student.id}`}
                    label="Yes"
                    checked={student.isAdopted === true}
                    onSelect={() =>
                      updateStudent(student.id, "isAdopted", true)
                    }
                  />
                  <StyledRadioOption
                    name={`adopted-${student.id}`}
                    label="No"
                    checked={student.isAdopted === false}
                    onSelect={() =>
                      updateStudent(student.id, "isAdopted", false)
                    }
                  />
                </div>
              </div>
              <div className="flex flex-col justify-center">
                <span className="text-sm font-semibold text-irongrey mb-2">
                  Is either parent a step-parent?
                </span>
                <div className="flex gap-4">
                  <StyledRadioOption
                    name={`step-${student.id}`}
                    label="Yes"
                    checked={student.isStepParent === true}
                    onSelect={() =>
                      updateStudent(student.id, "isStepParent", true)
                    }
                  />
                  <StyledRadioOption
                    name={`step-${student.id}`}
                    label="No"
                    checked={student.isStepParent === false}
                    onSelect={() =>
                      updateStudent(student.id, "isStepParent", false)
                    }
                  />
                </div>
              </div>
            </div>

            <div className="border-t border-gray-100 my-8"></div>
            <SectionHeader
              icon={School}
              title="CURRENT SCHOOLING"
              rightContent={
                index > 0 ? (
                  <label className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-widest text-irongrey/40">
                    <input
                      type="checkbox"
                      checked={
                        studentSectionPrefill[student.id]?.currentSchooling ??
                        true
                      }
                      onChange={(event) =>
                        setStudentSectionPrefillState(
                          student.id,
                          "currentSchooling",
                          event.target.checked,
                        )
                      }
                      className="h-3.5 w-3.5 rounded border-gray-300 accent-primary focus:outline-none"
                    />
                    <span>Pre-Fill</span>
                  </label>
                ) : undefined
              }
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <InputLabel>School Currently Attending</InputLabel>
                <Input
                  value={student.currentSchool}
                  onChange={(e) =>
                    updateStudent(student.id, "currentSchool", e.target.value)
                  }
                />
              </div>
              <div>
                <InputLabel required>Current Grade</InputLabel>
                <div
                  data-testid={index === 0 ? 'intake-student-current-grade-select' : undefined}
                  data-validation-key={fieldKey(
                    "students",
                    index,
                    "currentGrade",
                  )}
                >
                  <Select
                    options={[...CURRENT_GRADE_OPTIONS]}
                    value={student.currentGrade}
                    onChange={(val) =>
                      updateStudent(student.id, "currentGrade", val)
                    }
                    onBlur={() =>
                      markFieldTouched(
                        fieldKey("students", student.id, "currentGrade"),
                      )
                    }
                    triggerClassName={getFieldIndicatorClassName(
                      fieldKey("students", student.id, "currentGrade"),
                      "Students",
                      student.currentGrade,
                    )}
                  />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
              <div>
                <InputLabel>Director/Principal</InputLabel>
                <Input
                  value={student.schoolPrincipal}
                  onChange={(e) =>
                    updateStudent(student.id, "schoolPrincipal", e.target.value)
                  }
                />
              </div>
              <div>
                <InputLabel>Phone Number</InputLabel>
                <Input
                  type="tel"
                  value={student.schoolPhone}
                  onChange={(e) =>
                    updateStudent(
                      student.id,
                      "schoolPhone",
                      sanitizePhoneInput(e.target.value),
                    )
                  }
                  onBlur={() =>
                    markFieldTouched(
                      fieldKey("students", student.id, "schoolPhone"),
                    )
                  }
                  inputMode="tel"
                  className={getFieldIndicatorClassName(
                    fieldKey("students", student.id, "schoolPhone"),
                    "Students",
                    student.schoolPhone,
                    { required: false, validator: isValidPhoneNumber },
                  )}
                />
              </div>
            </div>

            <div className="border-t border-gray-100 my-8"></div>
            <SectionHeader
              icon={Heart}
              title="MEDICAL CONTACTS"
              rightContent={
                index > 0 ? (
                  <label className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-widest text-irongrey/40">
                    <input
                      type="checkbox"
                      checked={
                        studentSectionPrefill[student.id]?.medicalContacts ??
                        true
                      }
                      onChange={(event) =>
                        setStudentSectionPrefillState(
                          student.id,
                          "medicalContacts",
                          event.target.checked,
                        )
                      }
                      className="h-3.5 w-3.5 rounded border-gray-300 accent-primary focus:outline-none"
                    />
                    <span>Pre-Fill</span>
                  </label>
                ) : undefined
              }
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <InputLabel>Pediatrician</InputLabel>
                <Input
                  value={student.pediatrician}
                  onChange={(e) =>
                    updateStudent(student.id, "pediatrician", e.target.value)
                  }
                />
              </div>
              <div>
                <InputLabel>Phone</InputLabel>
                <Input
                  type="tel"
                  value={student.pediatricianPhone}
                  onChange={(e) =>
                    updateStudent(
                      student.id,
                      "pediatricianPhone",
                      sanitizePhoneInput(e.target.value),
                    )
                  }
                  onBlur={() =>
                    markFieldTouched(
                      fieldKey("students", student.id, "pediatricianPhone"),
                    )
                  }
                  inputMode="tel"
                  className={getFieldIndicatorClassName(
                    fieldKey("students", student.id, "pediatricianPhone"),
                    "Students",
                    student.pediatricianPhone,
                    { required: false, validator: isValidPhoneNumber },
                  )}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <InputLabel>Dentist</InputLabel>
                <Input
                  value={student.dentist}
                  onChange={(e) =>
                    updateStudent(student.id, "dentist", e.target.value)
                  }
                />
              </div>
              <div>
                <InputLabel>Phone</InputLabel>
                <Input
                  type="tel"
                  value={student.dentistPhone}
                  onChange={(e) =>
                    updateStudent(
                      student.id,
                      "dentistPhone",
                      sanitizePhoneInput(e.target.value),
                    )
                  }
                  onBlur={() =>
                    markFieldTouched(
                      fieldKey("students", student.id, "dentistPhone"),
                    )
                  }
                  inputMode="tel"
                  className={getFieldIndicatorClassName(
                    fieldKey("students", student.id, "dentistPhone"),
                    "Students",
                    student.dentistPhone,
                    { required: false, validator: isValidPhoneNumber },
                  )}
                />
              </div>
            </div>

            <div className="mt-6 bg-gray-50 p-4 rounded-xl border border-gray-100">
              <p className="text-xs text-irongrey/60 leading-relaxed text-justify font-medium">
                Please provide any additional information the school should be
                aware of to properly service your child’s needs (i.e. speech and
                language, occupational therapy, medical, educational or
                psychological concerns or evaluations).
              </p>
            </div>
          </Card>
        ))}
      </div>

      <button
        onClick={addStudent}
        className="w-full py-4 border-2 border-dashed border-gray-200 rounded-xl text-irongrey/50 font-bold text-sm hover:border-primary hover:text-primary hover:bg-primary/5 transition-all flex items-center justify-center gap-2 mb-12"
      >
        <Plus size={18} />
        Add Sibling Applicant
      </button>
    </div>
  );

  const renderParentsStep = () => {
    const parentNameMissing =
      !readText(family.father.name) && !readText(family.mother.name);
    const parentPhoneMissing = ![
      family.homePhone,
      family.father.cellPhone,
      family.mother.cellPhone,
    ].some((value) => isValidPhoneNumber(value));
    const parentEmailMissing = ![family.father.email, family.mother.email].some(
      (value) => isValidEmailAddress(value),
    );
    const householdComposition = resolveParentGuardianHouseholdComposition(family);

    return (
      <>
        <StepHeader
          title="Family & Religious Information"
          description="Parental contacts and community affiliation."
        />

        <Card className="mb-8">
          <div className="mb-6 rounded-xl border border-primary/10 bg-primary/5 px-4 py-3">
            <p className="text-xs font-semibold text-irongrey/70 leading-relaxed">
              To continue, enter your family address plus at least one parent
              name, one working phone number, and one parent email address.
              These contact details can be split across parents.
            </p>
          </div>
          <div className="mb-8 grid grid-cols-1 gap-4 rounded-xl border border-gray-100 bg-gray-50 p-4 md:grid-cols-2">
            <div data-testid="parent-admissions-household-mode">
              <InputLabel>Household setup</InputLabel>
              <Select
                value={householdComposition.mode}
                onChange={(value) =>
                  applyParentGuardianHouseholdComposition(
                    value === "single_parent_guardian"
                      ? "single_parent_guardian"
                      : "two_parent",
                    householdComposition.primaryKey,
                  )
                }
                options={[
                  { label: "Two parents / guardians", value: "two_parent" },
                  {
                    label: "Single parent / guardian",
                    value: "single_parent_guardian",
                  },
                ]}
              />
            </div>
            {householdComposition.mode === "single_parent_guardian" ? (
              <div data-testid="parent-admissions-household-primary-key">
                <InputLabel>Active parent / guardian</InputLabel>
                <Select
                  value={householdComposition.primaryKey}
                  onChange={(value) =>
                    applyParentGuardianHouseholdComposition(
                      "single_parent_guardian",
                      value === "mother" ? "mother" : "father",
                    )
                  }
                  options={[
                    { label: "Father / guardian 1", value: "father" },
                    { label: "Mother / guardian 2", value: "mother" },
                  ]}
                />
              </div>
            ) : null}
          </div>
          <div className="mb-8">
            <InputLabel required>Family Surname</InputLabel>
            <Input
              data-validation-key="family.surname"
              placeholder="Gertler, Cohen, etc."
              value={family.surname}
              onChange={(e) => updateFamily("surname", e.target.value)}
              onBlur={() => markFieldTouched(fieldKey("family", "surname"))}
              className={getFieldIndicatorClassName(
                fieldKey("family", "surname"),
                "Parents",
                family.surname,
              )}
              data-testid="intake-family-surname-input"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <InputLabel>Home Phone</InputLabel>
              <Input
                data-validation-key="family.homePhone"
                type="tel"
                value={family.homePhone}
                onChange={(e) =>
                  updateFamily("homePhone", sanitizePhoneInput(e.target.value))
                }
                onBlur={() => markFieldTouched(fieldKey("family", "homePhone"))}
                inputMode="tel"
                className={getGroupedFieldIndicatorClassName(
                  fieldKey("family", "homePhone"),
                  "Parents",
                  family.homePhone,
                  parentPhoneMissing,
                  isValidPhoneNumber,
                )}
                data-testid="intake-family-home-phone-input"
              />
            </div>
            <div>
              <InputLabel required>Home Address</InputLabel>
              <Input
                data-validation-key="family.address"
                value={family.address}
                onChange={(e) => updateFamily("address", e.target.value)}
                onBlur={() => markFieldTouched(fieldKey("family", "address"))}
                className={getFieldIndicatorClassName(
                  fieldKey("family", "address"),
                  "Parents",
                  family.address,
                )}
                data-testid="intake-family-address-input"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
            <div>
              <InputLabel required>City</InputLabel>
              <Input
                data-validation-key="family.city"
                value={family.city}
                onChange={(e) => updateFamily("city", e.target.value)}
                onBlur={() => markFieldTouched(fieldKey("family", "city"))}
                className={getFieldIndicatorClassName(
                  fieldKey("family", "city"),
                  "Parents",
                  family.city,
                )}
                data-testid="intake-family-city-input"
              />
            </div>
            <div>
              <InputLabel required>State</InputLabel>
              <div
                data-testid="intake-family-state-select"
                data-validation-key="family.state"
              >
                <Select
                  options={US_STATES}
                  value={family.state}
                  onChange={(val) => updateFamily("state", val)}
                  onBlur={() => markFieldTouched(fieldKey("family", "state"))}
                  triggerClassName={getFieldIndicatorClassName(
                    fieldKey("family", "state"),
                    "Parents",
                    family.state,
                  )}
                  searchable
                />
              </div>
            </div>
            <div>
              <InputLabel required>Zip</InputLabel>
              <Input
                data-validation-key="family.zip"
                value={family.zip}
                onChange={(e) => updateFamily("zip", e.target.value)}
                onBlur={() => markFieldTouched(fieldKey("family", "zip"))}
                className={getFieldIndicatorClassName(
                  fieldKey("family", "zip"),
                  "Parents",
                  family.zip,
                  {
                    validator: (candidate) =>
                      readText(candidate).replace(/\D/g, "").length >= 5,
                  },
                )}
                data-testid="intake-family-zip-input"
              />
            </div>
          </div>

          <div className="border-t border-gray-100 my-8"></div>
          <SectionHeader title="Father's Information" />

          <div className="grid grid-cols-4 gap-4 mb-4">
            <div className="col-span-1">
              <InputLabel>Title</InputLabel>
              <Select
                options={["Mr.", "Rabbi", "Dr.", "Professor"]}
                value={family.father.title}
                onChange={(val) => updateParent("father", "title", val)}
              />
            </div>
            <div className="col-span-3">
              <InputLabel>Father's First Name</InputLabel>
              <Input
                data-validation-key="family.father.name"
                value={family.father.name}
                onChange={(e) => updateParent("father", "name", e.target.value)}
                onBlur={() =>
                  markFieldTouched(fieldKey("family", "father", "name"))
                }
                className={getGroupedFieldIndicatorClassName(
                  fieldKey("family", "father", "name"),
                  "Parents",
                  family.father.name,
                  parentNameMissing,
                )}
                data-testid="intake-father-first-name-input"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <InputLabel>Cell Phone</InputLabel>
              <Input
                data-validation-key="family.father.cellPhone"
                type="tel"
                value={family.father.cellPhone}
                onChange={(e) =>
                  updateParent(
                    "father",
                    "cellPhone",
                    sanitizePhoneInput(e.target.value),
                  )
                }
                onBlur={() =>
                  markFieldTouched(fieldKey("family", "father", "cellPhone"))
                }
                inputMode="tel"
                className={getGroupedFieldIndicatorClassName(
                  fieldKey("family", "father", "cellPhone"),
                  "Parents",
                  family.father.cellPhone,
                  parentPhoneMissing,
                  isValidPhoneNumber,
                )}
                data-testid="intake-father-cell-phone-input"
              />
            </div>
            <div>
              <InputLabel>Email address</InputLabel>
              <Input
                data-validation-key="family.father.email"
                value={family.father.email}
                onChange={(e) =>
                  updateParent("father", "email", e.target.value)
                }
                onBlur={() =>
                  markFieldTouched(fieldKey("family", "father", "email"))
                }
                type="email"
                className={getGroupedFieldIndicatorClassName(
                  fieldKey("family", "father", "email"),
                  "Parents",
                  family.father.email,
                  parentEmailMissing,
                  isValidEmailAddress,
                )}
                data-testid="intake-father-email-input"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
            <div>
              <InputLabel>Occupation</InputLabel>
              <Input
                value={family.father.occupation}
                onChange={(e) =>
                  updateParent("father", "occupation", e.target.value)
                }
              />
            </div>
            <div>
              <InputLabel>Name of Firm</InputLabel>
              <Input
                value={family.father.firmName}
                onChange={(e) =>
                  updateParent("father", "firmName", e.target.value)
                }
              />
            </div>
          </div>

          <div className="mb-4 grid gap-3 md:grid-cols-2">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={family.father.hasSeparateAddress}
                onChange={(e) =>
                  updateParent("father", "hasSeparateAddress", e.target.checked)
                }
                className="h-4 w-4 rounded border-gray-300 text-primary accent-primary focus:ring-primary focus:ring-offset-0 focus:outline-none cursor-pointer"
              />
              <span className="text-sm font-semibold text-irongrey">
                Father lives at a separate address
              </span>
            </label>
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={family.father.isDeceasedOrNoLongerLegalGuardian}
                onChange={(e) =>
                  updateParent(
                    "father",
                    "isDeceasedOrNoLongerLegalGuardian",
                    e.target.checked,
                  )
                }
                className="h-4 w-4 rounded border-gray-300 text-primary accent-primary focus:ring-primary focus:ring-offset-0 focus:outline-none cursor-pointer"
              />
              <span className="text-sm font-semibold text-irongrey">
                Father is deceased or no longer legal guardian
              </span>
            </label>
          </div>

          {family.father.hasSeparateAddress && (
            <div className="mb-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <InputLabel>Home phone</InputLabel>
                  <Input
                    type="tel"
                    value={family.father.sepHomePhone}
                    onChange={(e) =>
                      updateParent(
                        "father",
                        "sepHomePhone",
                        sanitizePhoneInput(e.target.value),
                      )
                    }
                    onBlur={() =>
                      markFieldTouched(
                        fieldKey("family", "father", "sepHomePhone"),
                      )
                    }
                    inputMode="tel"
                    className={getFieldIndicatorClassName(
                      fieldKey("family", "father", "sepHomePhone"),
                      "Parents",
                      family.father.sepHomePhone,
                      { required: false, validator: isValidPhoneNumber },
                    )}
                  />
                </div>
                <div>
                  <InputLabel>Home address</InputLabel>
                  <Input
                    value={family.father.sepAddress}
                    onChange={(e) =>
                      updateParent("father", "sepAddress", e.target.value)
                    }
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <InputLabel>City</InputLabel>
                  <Input
                    value={family.father.sepCity}
                    onChange={(e) =>
                      updateParent("father", "sepCity", e.target.value)
                    }
                  />
                </div>
                <div>
                  <InputLabel>State</InputLabel>
                  <Select
                    options={US_STATES}
                    value={family.father.sepState}
                    onChange={(val) => updateParent("father", "sepState", val)}
                    searchable
                  />
                </div>
                <div>
                  <InputLabel>Zip</InputLabel>
                  <Input
                    value={family.father.sepZip}
                    onChange={(e) =>
                      updateParent("father", "sepZip", e.target.value)
                    }
                  />
                </div>
              </div>
            </div>
          )}

          <div className="border-t border-gray-100 my-8"></div>
          <SectionHeader title="Mother's Information" />

          <div className="grid grid-cols-3 gap-4 mb-4">
            <div className="col-span-1">
              <InputLabel>Title</InputLabel>
              <Select
                options={["Mrs.", "Dr.", "Professor", "Ms."]}
                value={family.mother.title}
                onChange={(val) => updateParent("mother", "title", val)}
              />
            </div>
            <div className="col-span-2">
              <InputLabel>Mother's First Name</InputLabel>
              <Input
                value={family.mother.name}
                onChange={(e) => updateParent("mother", "name", e.target.value)}
                onBlur={() =>
                  markFieldTouched(fieldKey("family", "mother", "name"))
                }
                className={getGroupedFieldIndicatorClassName(
                  fieldKey("family", "mother", "name"),
                  "Parents",
                  family.mother.name,
                  parentNameMissing,
                )}
                data-testid="intake-mother-first-name-input"
              />
            </div>
          </div>

          <div className="mb-4">
            <InputLabel>Maiden Name</InputLabel>
            <Input
              value={family.mother.maidenName}
              onChange={(e) =>
                updateParent("mother", "maidenName", e.target.value)
              }
              data-testid="intake-mother-maiden-name-input"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <InputLabel>Cell Phone</InputLabel>
              <Input
                type="tel"
                value={family.mother.cellPhone}
                onChange={(e) =>
                  updateParent(
                    "mother",
                    "cellPhone",
                    sanitizePhoneInput(e.target.value),
                  )
                }
                onBlur={() =>
                  markFieldTouched(fieldKey("family", "mother", "cellPhone"))
                }
                inputMode="tel"
                className={getGroupedFieldIndicatorClassName(
                  fieldKey("family", "mother", "cellPhone"),
                  "Parents",
                  family.mother.cellPhone,
                  parentPhoneMissing,
                  isValidPhoneNumber,
                )}
                data-testid="intake-mother-cell-phone-input"
              />
            </div>
            <div>
              <InputLabel>Email address</InputLabel>
              <Input
                value={family.mother.email}
                onChange={(e) =>
                  updateParent("mother", "email", e.target.value)
                }
                onBlur={() =>
                  markFieldTouched(fieldKey("family", "mother", "email"))
                }
                type="email"
                className={getGroupedFieldIndicatorClassName(
                  fieldKey("family", "mother", "email"),
                  "Parents",
                  family.mother.email,
                  parentEmailMissing,
                  isValidEmailAddress,
                )}
                data-testid="intake-mother-email-input"
              />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <InputLabel>Occupation</InputLabel>
              <Input
                value={family.mother.occupation}
                onChange={(e) =>
                  updateParent("mother", "occupation", e.target.value)
                }
              />
            </div>
            <div>
              <InputLabel>Name of Firm</InputLabel>
              <Input
                value={family.mother.firmName}
                onChange={(e) =>
                  updateParent("mother", "firmName", e.target.value)
                }
              />
            </div>
          </div>

          <div className="mb-4 grid gap-3 md:grid-cols-2">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={family.mother.hasSeparateAddress}
                onChange={(e) =>
                  updateParent("mother", "hasSeparateAddress", e.target.checked)
                }
                className="h-4 w-4 rounded border-gray-300 text-primary accent-primary focus:ring-primary focus:ring-offset-0 focus:outline-none cursor-pointer"
              />
              <span className="text-sm font-semibold text-irongrey">
                Mother lives at a separate address
              </span>
            </label>
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={family.mother.isDeceasedOrNoLongerLegalGuardian}
                onChange={(e) =>
                  updateParent(
                    "mother",
                    "isDeceasedOrNoLongerLegalGuardian",
                    e.target.checked,
                  )
                }
                className="h-4 w-4 rounded border-gray-300 text-primary accent-primary focus:ring-primary focus:ring-offset-0 focus:outline-none cursor-pointer"
              />
              <span className="text-sm font-semibold text-irongrey">
                Mother is deceased or no longer legal guardian
              </span>
            </label>
          </div>

          {family.mother.hasSeparateAddress && (
            <div className="mb-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <InputLabel>Home phone</InputLabel>
                  <Input
                    type="tel"
                    value={family.mother.sepHomePhone}
                    onChange={(e) =>
                      updateParent(
                        "mother",
                        "sepHomePhone",
                        sanitizePhoneInput(e.target.value),
                      )
                    }
                    onBlur={() =>
                      markFieldTouched(
                        fieldKey("family", "mother", "sepHomePhone"),
                      )
                    }
                    inputMode="tel"
                    className={getFieldIndicatorClassName(
                      fieldKey("family", "mother", "sepHomePhone"),
                      "Parents",
                      family.mother.sepHomePhone,
                      { required: false, validator: isValidPhoneNumber },
                    )}
                  />
                </div>
                <div>
                  <InputLabel>Home address</InputLabel>
                  <Input
                    value={family.mother.sepAddress}
                    onChange={(e) =>
                      updateParent("mother", "sepAddress", e.target.value)
                    }
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <InputLabel>City</InputLabel>
                  <Input
                    value={family.mother.sepCity}
                    onChange={(e) =>
                      updateParent("mother", "sepCity", e.target.value)
                    }
                  />
                </div>
                <div>
                  <InputLabel>State</InputLabel>
                  <Select
                    options={US_STATES}
                    value={family.mother.sepState}
                    onChange={(val) => updateParent("mother", "sepState", val)}
                    searchable
                  />
                </div>
                <div>
                  <InputLabel>Zip</InputLabel>
                  <Input
                    value={family.mother.sepZip}
                    onChange={(e) =>
                      updateParent("mother", "sepZip", e.target.value)
                    }
                  />
                </div>
              </div>
            </div>
          )}

          <div className="border-t border-gray-100 my-8"></div>
          <SectionHeader title="Status & Affiliation" />

          <div className="mb-4">
            <InputLabel>Marital Status</InputLabel>
            <Input
              value={family.maritalStatus}
              onChange={(e) => updateFamily("maritalStatus", e.target.value)}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <InputLabel>Current shul affiliation</InputLabel>
              <Input
                value={family.shulAffiliation}
                onChange={(e) =>
                  updateFamily("shulAffiliation", e.target.value)
                }
              />
            </div>
            <div>
              <InputLabel>Rav</InputLabel>
              <Input
                value={family.rav}
                onChange={(e) => updateFamily("rav", e.target.value)}
              />
            </div>
          </div>
        </Card>
      </>
    );
  };

  const renderGrandparentsStep = () => (
    <>
      <StepHeader
        title="Grandparent Information"
        description="Extended family information as requested in the enrollment form."
      />

      {/* Paternal */}
      <Card className="mb-8">
        <SectionHeader title="Paternal Grandparents" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div className="md:col-span-2">
            <InputLabel>Grandparents - Last Name</InputLabel>
            <Input
              value={paternalGrandparents.lastName}
              onChange={(e) =>
                updateGrandparents("paternal", "lastName", e.target.value)
              }
            />
          </div>
          <div>
            <InputLabel>Marital Status</InputLabel>
            <Select
              options={GRANDPARENT_MARITAL_STATUS_OPTIONS}
              value={paternalGrandparents.maritalStatus}
              onChange={(val) =>
                updateGrandparents("paternal", "maritalStatus", val)
              }
            />
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
          <div className="col-span-1">
            <InputLabel>Title</InputLabel>
            <Input
              value={paternalGrandparents.grandfatherTitle}
              onChange={(e) =>
                updateGrandparents(
                  "paternal",
                  "grandfatherTitle",
                  e.target.value,
                )
              }
            />
          </div>
          <div className="col-span-1 md:col-span-3">
            <InputLabel>Grandfather's Name</InputLabel>
            <Input
              value={paternalGrandparents.grandfatherName}
              onChange={(e) =>
                updateGrandparents(
                  "paternal",
                  "grandfatherName",
                  e.target.value,
                )
              }
            />
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
          <div className="col-span-1">
            <InputLabel>Title</InputLabel>
            <Input
              value={paternalGrandparents.grandmotherTitle}
              onChange={(e) =>
                updateGrandparents(
                  "paternal",
                  "grandmotherTitle",
                  e.target.value,
                )
              }
            />
          </div>
          <div className="col-span-1 md:col-span-3">
            <InputLabel>Grandmother's Name</InputLabel>
            <Input
              value={paternalGrandparents.grandmotherName}
              onChange={(e) =>
                updateGrandparents(
                  "paternal",
                  "grandmotherName",
                  e.target.value,
                )
              }
            />
          </div>
        </div>

        <div className="mb-4">
          <InputLabel>Home Address</InputLabel>
          <Input
            value={paternalGrandparents.address}
            onChange={(e) =>
              updateGrandparents("paternal", "address", e.target.value)
            }
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div>
            <InputLabel>City</InputLabel>
            <Input
              value={paternalGrandparents.city}
              onChange={(e) =>
                updateGrandparents("paternal", "city", e.target.value)
              }
            />
          </div>
          <div>
            <InputLabel>State</InputLabel>
            <Select
              options={US_STATES}
              value={paternalGrandparents.state}
              onChange={(e) => updateGrandparents("paternal", "state", e)}
              searchable
            />
          </div>
          <div>
            <InputLabel>Zip</InputLabel>
            <Input
              value={paternalGrandparents.zip}
              onChange={(e) =>
                updateGrandparents("paternal", "zip", e.target.value)
              }
            />
          </div>
        </div>

        <div className="mb-4">
          <InputLabel>Phone</InputLabel>
          <Input
            type="tel"
            value={paternalGrandparents.phone}
            onChange={(e) =>
              updateGrandparents(
                "paternal",
                "phone",
                sanitizePhoneInput(e.target.value),
              )
            }
            onBlur={() =>
              markFieldTouched(fieldKey("paternalGrandparents", "phone"))
            }
            inputMode="tel"
            className={getFieldIndicatorClassName(
              fieldKey("paternalGrandparents", "phone"),
              "Extended Family",
              paternalGrandparents.phone,
              { required: false, validator: isValidPhoneNumber },
            )}
          />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <InputLabel>His email</InputLabel>
            <Input
              value={paternalGrandparents.grandfatherEmail}
              onChange={(e) =>
                updateGrandparents(
                  "paternal",
                  "grandfatherEmail",
                  e.target.value,
                )
              }
              onBlur={() =>
                markFieldTouched(
                  fieldKey("paternalGrandparents", "grandfatherEmail"),
                )
              }
              type="email"
              className={getFieldIndicatorClassName(
                fieldKey("paternalGrandparents", "grandfatherEmail"),
                "Extended Family",
                paternalGrandparents.grandfatherEmail,
                { required: false, validator: isValidEmailAddress },
              )}
            />
          </div>
          <div>
            <InputLabel>Her email</InputLabel>
            <Input
              value={paternalGrandparents.grandmotherEmail}
              onChange={(e) =>
                updateGrandparents(
                  "paternal",
                  "grandmotherEmail",
                  e.target.value,
                )
              }
              onBlur={() =>
                markFieldTouched(
                  fieldKey("paternalGrandparents", "grandmotherEmail"),
                )
              }
              type="email"
              className={getFieldIndicatorClassName(
                fieldKey("paternalGrandparents", "grandmotherEmail"),
                "Extended Family",
                paternalGrandparents.grandmotherEmail,
                { required: false, validator: isValidEmailAddress },
              )}
            />
          </div>
        </div>
      </Card>

      {/* Maternal */}
      <Card className="mb-8">
        <SectionHeader title="Maternal Grandparents" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div className="md:col-span-2">
            <InputLabel>Grandparents - Last Name</InputLabel>
            <Input
              value={maternalGrandparents.lastName}
              onChange={(e) =>
                updateGrandparents("maternal", "lastName", e.target.value)
              }
            />
          </div>
          <div>
            <InputLabel>Marital Status</InputLabel>
            <Select
              options={GRANDPARENT_MARITAL_STATUS_OPTIONS}
              value={maternalGrandparents.maritalStatus}
              onChange={(val) =>
                updateGrandparents("maternal", "maritalStatus", val)
              }
            />
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
          <div className="col-span-1">
            <InputLabel>Title</InputLabel>
            <Input
              value={maternalGrandparents.grandfatherTitle}
              onChange={(e) =>
                updateGrandparents(
                  "maternal",
                  "grandfatherTitle",
                  e.target.value,
                )
              }
            />
          </div>
          <div className="col-span-1 md:col-span-3">
            <InputLabel>Grandfather's Name</InputLabel>
            <Input
              value={maternalGrandparents.grandfatherName}
              onChange={(e) =>
                updateGrandparents(
                  "maternal",
                  "grandfatherName",
                  e.target.value,
                )
              }
            />
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
          <div className="col-span-1">
            <InputLabel>Title</InputLabel>
            <Input
              value={maternalGrandparents.grandmotherTitle}
              onChange={(e) =>
                updateGrandparents(
                  "maternal",
                  "grandmotherTitle",
                  e.target.value,
                )
              }
            />
          </div>
          <div className="col-span-1 md:col-span-3">
            <InputLabel>Grandmother's Name</InputLabel>
            <Input
              value={maternalGrandparents.grandmotherName}
              onChange={(e) =>
                updateGrandparents(
                  "maternal",
                  "grandmotherName",
                  e.target.value,
                )
              }
            />
          </div>
        </div>

        <div className="mb-4">
          <InputLabel>Home Address</InputLabel>
          <Input
            value={maternalGrandparents.address}
            onChange={(e) =>
              updateGrandparents("maternal", "address", e.target.value)
            }
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div>
            <InputLabel>City</InputLabel>
            <Input
              value={maternalGrandparents.city}
              onChange={(e) =>
                updateGrandparents("maternal", "city", e.target.value)
              }
            />
          </div>
          <div>
            <InputLabel>State</InputLabel>
            <Select
              options={US_STATES}
              value={maternalGrandparents.state}
              onChange={(e) => updateGrandparents("maternal", "state", e)}
              searchable
            />
          </div>
          <div>
            <InputLabel>Zip</InputLabel>
            <Input
              value={maternalGrandparents.zip}
              onChange={(e) =>
                updateGrandparents("maternal", "zip", e.target.value)
              }
            />
          </div>
        </div>

        <div className="mb-4">
          <InputLabel>Phone</InputLabel>
          <Input
            type="tel"
            value={maternalGrandparents.phone}
            onChange={(e) =>
              updateGrandparents(
                "maternal",
                "phone",
                sanitizePhoneInput(e.target.value),
              )
            }
            onBlur={() =>
              markFieldTouched(fieldKey("maternalGrandparents", "phone"))
            }
            inputMode="tel"
            className={getFieldIndicatorClassName(
              fieldKey("maternalGrandparents", "phone"),
              "Extended Family",
              maternalGrandparents.phone,
              { required: false, validator: isValidPhoneNumber },
            )}
          />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <InputLabel>His email</InputLabel>
            <Input
              value={maternalGrandparents.grandfatherEmail}
              onChange={(e) =>
                updateGrandparents(
                  "maternal",
                  "grandfatherEmail",
                  e.target.value,
                )
              }
              onBlur={() =>
                markFieldTouched(
                  fieldKey("maternalGrandparents", "grandfatherEmail"),
                )
              }
              type="email"
              className={getFieldIndicatorClassName(
                fieldKey("maternalGrandparents", "grandfatherEmail"),
                "Extended Family",
                maternalGrandparents.grandfatherEmail,
                { required: false, validator: isValidEmailAddress },
              )}
            />
          </div>
          <div>
            <InputLabel>Her email</InputLabel>
            <Input
              value={maternalGrandparents.grandmotherEmail}
              onChange={(e) =>
                updateGrandparents(
                  "maternal",
                  "grandmotherEmail",
                  e.target.value,
                )
              }
              onBlur={() =>
                markFieldTouched(
                  fieldKey("maternalGrandparents", "grandmotherEmail"),
                )
              }
              type="email"
              className={getFieldIndicatorClassName(
                fieldKey("maternalGrandparents", "grandmotherEmail"),
                "Extended Family",
                maternalGrandparents.grandmotherEmail,
                { required: false, validator: isValidEmailAddress },
              )}
            />
          </div>
        </div>
      </Card>
    </>
  );

  const renderDocumentsStep = () => (
    <>
      <StepHeader
        title="Documents"
        description="Please upload necessary documentation."
      />

      <Card className="mb-8">
        <SectionHeader icon={Upload} title="Uploads" />

        {!admissionsRequirementsReady ? (
          <div
            className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-4 text-amber-950"
            data-validation-key="documents.requirementsReady"
          >
            <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
              <div className="flex items-start gap-3">
                <AlertCircle
                  size={18}
                  className="mt-0.5 shrink-0 text-amber-800"
                />
                <div>
                  <p className="text-sm font-bold">
                    Application requirements are not ready yet.
                  </p>
                  <p className="mt-1 text-sm font-medium leading-relaxed text-amber-900/80">
                    {admissionsRequirementsLoading
                      ? "Loading this school's current document and agreement requirements."
                      : admissionsRequirementsErrorMessage ||
                        "We could not load this school's current document and agreement requirements. Please try again before continuing."}
                  </p>
                </div>
              </div>
              <Button
                type="button"
                variant="secondary"
                className="self-start whitespace-nowrap !px-5 !py-2"
                onClick={() => {
                  void retryAdmissionsRequirements();
                }}
                disabled={admissionsRequirementsLoading}
              >
                {admissionsRequirementsLoading ? "Loading..." : "Try Again"}
              </Button>
            </div>
          </div>
        ) : (
          <>
        <div className="space-y-8 mb-8">
          {documentUploadTargets.map(
            ({
              key,
              labelIndex,
              showStudentHeader,
              student: targetStudent,
              uploadStudentId,
            }) => (
              <div
                key={key}
                className="space-y-4"
              >
                {showStudentHeader && targetStudent ? (
                  <div className="rounded-xl border border-gray-100 bg-gray-50 px-4 py-3">
                    <p className="text-xs font-bold uppercase tracking-wide text-irongrey/40">
                      Student {labelIndex + 1}
                    </p>
                    <p className="text-sm font-semibold text-irongrey">
                      {joinName(
                        targetStudent.firstName,
                        targetStudent.lastName,
                      ) || `Sibling ${labelIndex + 1}`}
                    </p>
                  </div>
                ) : null}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {admissionsDocumentRows
                    .filter((slot) =>
                      isAdmissionsDocumentRowVisibleForStudent(
                        slot,
                        targetStudent,
                      ),
                    )
                    .map((slotRow) => {
                      const slot = { ...slotRow, id: slotRow.slotId };
                      const uploadKey = buildDocumentSlotKey(
                        slot.id,
                        uploadStudentId,
                      );
                      const uploadedFile = resolveAdmissionsDocumentUploadFileName({
                        documentUploads,
                        slotId: slot.id,
                        studentId: uploadStudentId,
                        studentIndex: labelIndex,
                        studentCount: documentUploadTargets.length,
                      });
                    const isRequiredUpload = slot.required === true;
                    const uploadValidationKey = buildDocumentValidationKey(
                      uploadKey,
                    );
                    const showRequiredUploadMissing =
                      isRequiredUpload &&
                      Boolean(attemptedSteps.Documents) &&
                      !uploadedFile;
                    const testIdToken = uploadKey.replace(
                      /[^a-zA-Z0-9_-]/g,
                      "-",
                    );
                    const Icon = slot.icon === "photo" ? Camera : FileText;
                    return (
                      <div
                        key={uploadKey}
                        role="button"
                        tabIndex={0}
                        data-testid={`parent-portal-upload-slot-${slot.id}`}
                        data-upload-slot-instance={testIdToken}
                        data-validation-key={
                          isRequiredUpload ? uploadValidationKey : undefined
                        }
                        className={`bg-gray-50 border-2 border-dashed rounded-xl p-6 text-center transition-all group ${
                          showRequiredUploadMissing
                            ? "border-amber-600 bg-amber-50"
                            : uploadedFile
                              ? "border-primary/35 bg-primary/5"
                              : "border-gray-200 hover:border-primary/30 hover:bg-primary/5"
                        }`}
                        onClick={() => openDocumentPicker(uploadKey)}
                        onKeyDown={(event) => {
                          if (event.key === "Enter" || event.key === " ") {
                            event.preventDefault();
                            openDocumentPicker(uploadKey);
                          }
                        }}
                        onDragOver={(event) => {
                          event.preventDefault();
                          event.stopPropagation();
                        }}
                        onDrop={(event) => {
                          event.preventDefault();
                          event.stopPropagation();
                          handleDocumentFiles(
                            uploadKey,
                            event.dataTransfer?.files ?? null,
                          );
                        }}
                      >
                        <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-sm group-hover:scale-110 transition-transform">
                          <Icon size={24} className="text-primary/60" />
                        </div>
                        <h4 className="text-sm font-bold text-irongrey mb-1">
                          {slot.label}
                        </h4>
                        <p className="text-xs text-irongrey/40 mb-2">
                          {slot.description}
                        </p>
                        {isRequiredUpload ? (
                          <p className="mb-2 text-[11px] font-bold uppercase tracking-wide text-amber-700">
                            Required
                          </p>
                        ) : null}
                        <p
                          data-testid={`parent-portal-upload-file-name-${slot.id}`}
                          data-upload-slot-instance={testIdToken}
                          className="text-[11px] font-semibold text-irongrey/55 mb-4 truncate"
                        >
                          {uploadedFile ||
                            "Drag and drop a file or choose one."}
                        </p>
                        <input
                          ref={(node) => {
                            fileInputRefs.current[uploadKey] = node;
                          }}
                          data-testid={`parent-portal-upload-input-${slot.id}`}
                          data-upload-slot-instance={testIdToken}
                          className="absolute -left-[9999px] top-auto h-px w-px opacity-0"
                          type="file"
                          accept={slot.accept}
                          onChange={(event) =>
                            handleDocumentFiles(uploadKey, event.target.files)
                          }
                        />
                        <Button
                          variant="secondary"
                          type="button"
                          data-testid={`parent-portal-upload-choose-${slot.id}`}
                          data-upload-slot-instance={testIdToken}
                          className="w-full !py-2 !text-xs"
                          onClick={(event) => {
                            event.stopPropagation();
                            openDocumentPicker(uploadKey);
                          }}
                        >
                          Choose File
                        </Button>
                        {showRequiredUploadMissing ? (
                          <p className="mt-3 text-xs font-semibold text-amber-800">
                            Upload {slot.label} before submitting
                            {familySelfSchedulingVisible ? " or scheduling" : ""}.
                          </p>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              </div>
            ),
          )}
        </div>

        {tuitionPolicyAgreement?.visible ? (
          <>
            <div className="border-t border-gray-100 my-8"></div>

            <SectionHeader icon={CheckSquare} title="Policies" />

            <div className="bg-primary/5 rounded-xl p-6 border border-primary/10">
              <h4 className="text-sm font-bold text-irongrey mb-2">
                {tuitionPolicyAgreement.label}
              </h4>
              <p className="text-xs text-irongrey/60 mb-4 leading-relaxed">
                {tuitionPolicyAgreement.text}
              </p>
              <p className="text-xs font-semibold text-irongrey/70 mb-4">
                {tuitionPolicyAgreement.required
                  ? "Acknowledgment is required before you can continue."
                  : "Acknowledgment is optional for this application."}
              </p>
          <div className="flex flex-col sm:flex-row gap-6 mt-4">
            <label
              data-testid="parent-portal-intake-documents-father-policy-checkbox"
              data-validation-key="documents.fatherPolicyAware"
              className={`flex items-center gap-3 rounded-lg px-2 py-1 transition-colors ${fatherPolicyAcknowledgementDisabled ? "cursor-not-allowed opacity-50 grayscale" : "cursor-pointer group"} ${fatherPolicyAcknowledgementDisabled ? "border-gray-200 bg-gray-100" : tuitionPolicyAgreement.required ? getCheckboxIndicatorClassName("Documents", documents.fatherPolicyAware) : ""}`}
              aria-disabled={fatherPolicyAcknowledgementDisabled}
            >
              <div
                className={`w-5 h-5 rounded border flex items-center justify-center transition-colors ${fatherPolicyAcknowledgementDisabled ? "bg-gray-100 border-gray-300" : documents.fatherPolicyAware ? "bg-primary border-primary" : "bg-white border-gray-300 group-hover:border-primary"}`}
              >
                {!fatherPolicyAcknowledgementDisabled && documents.fatherPolicyAware && (
                  <Check size={14} className="text-white" />
                )}
              </div>
              <input
                type="checkbox"
                className="hidden"
                checked={!fatherPolicyAcknowledgementDisabled && documents.fatherPolicyAware}
                onChange={(e) =>
                  setDocuments((prev) => ({
                    ...prev,
                    fatherPolicyAware: e.target.checked,
                  }))
                }
                disabled={fatherPolicyAcknowledgementDisabled}
              />
              <span
                className={`text-xs font-bold uppercase tracking-wide ${fatherPolicyAcknowledgementDisabled ? "text-irongrey/30" : documents.fatherPolicyAware ? "text-primary" : "text-irongrey/40"}`}
              >
                Father's Signature{fatherPolicyAcknowledgementDisabled || !tuitionPolicyAgreement.required ? "" : " *"}
              </span>
            </label>

            <label
              data-testid="parent-portal-intake-documents-mother-policy-checkbox"
              data-validation-key="documents.motherPolicyAware"
              className={`flex items-center gap-3 rounded-lg px-2 py-1 transition-colors ${motherPolicyAcknowledgementDisabled ? "cursor-not-allowed opacity-50 grayscale" : "cursor-pointer group"} ${motherPolicyAcknowledgementDisabled ? "border-gray-200 bg-gray-100" : tuitionPolicyAgreement.required ? getCheckboxIndicatorClassName("Documents", documents.motherPolicyAware) : ""}`}
              aria-disabled={motherPolicyAcknowledgementDisabled}
            >
              <div
                className={`w-5 h-5 rounded border flex items-center justify-center transition-colors ${motherPolicyAcknowledgementDisabled ? "bg-gray-100 border-gray-300" : documents.motherPolicyAware ? "bg-primary border-primary" : "bg-white border-gray-300 group-hover:border-primary"}`}
              >
                {!motherPolicyAcknowledgementDisabled && documents.motherPolicyAware && (
                  <Check size={14} className="text-white" />
                )}
              </div>
              <input
                type="checkbox"
                className="hidden"
                checked={!motherPolicyAcknowledgementDisabled && documents.motherPolicyAware}
                onChange={(e) =>
                  setDocuments((prev) => ({
                    ...prev,
                    motherPolicyAware: e.target.checked,
                  }))
                }
                disabled={motherPolicyAcknowledgementDisabled}
              />
              <span
                className={`text-xs font-bold uppercase tracking-wide ${motherPolicyAcknowledgementDisabled ? "text-irongrey/30" : documents.motherPolicyAware ? "text-primary" : "text-irongrey/40"}`}
              >
                Mother's Signature{motherPolicyAcknowledgementDisabled || !tuitionPolicyAgreement.required ? "" : " *"}
              </span>
            </label>
          </div>
            </div>
          </>
        ) : null}
          </>
        )}
      </Card>
    </>
  );

  const renderReviewStep = () => (
    <>
      <StepHeader
        title="Review & Submit"
        description="Please verify all information before final submission."
      />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        <Card>
          <SectionHeader icon={Heart} title="Family Summary" />
          <div className="space-y-2 text-sm">
            <p>
              <strong className="text-irongrey font-semibold">Surname:</strong>{" "}
              <span className="text-irongrey/70 transition-colors duration-500 group-hover:text-irongrey/90">
                {family.surname || "Not entered"}
              </span>
            </p>
            <p>
              <strong className="text-irongrey font-semibold">Father:</strong>{" "}
              <span className="text-irongrey/70 transition-colors duration-500 group-hover:text-irongrey/90">
                {family.father.title} {family.father.name}
              </span>
            </p>
            <p>
              <strong className="text-irongrey font-semibold">Mother:</strong>{" "}
              <span className="text-irongrey/70 transition-colors duration-500 group-hover:text-irongrey/90">
                {family.mother.title} {family.mother.name}
              </span>
            </p>
            <p>
              <strong className="text-irongrey font-semibold">Email:</strong>{" "}
              <span className="text-irongrey/70 transition-colors duration-500 group-hover:text-irongrey/90">
                {family.father.email}
              </span>
            </p>
            <p>
              <strong className="text-irongrey font-semibold">Phone:</strong>{" "}
              <span className="text-irongrey/70 transition-colors duration-500 group-hover:text-irongrey/90">
                {family.homePhone}
              </span>
            </p>
          </div>
        </Card>

        <Card>
          <SectionHeader
            icon={School}
            title={`Students (${students.length})`}
          />
          <div className="space-y-4">
            {students.map((s, i) => (
              <div
                key={s.id}
                className="flex justify-between items-center border-b border-gray-50 last:border-0 pb-2 last:pb-0"
              >
                <div>
                  <p className="font-bold text-irongrey text-sm">
                    {s.firstName} {s.lastName}
                  </p>
                  <p className="text-xs text-irongrey/50">
                    {s.hebrewFirst} {s.hebrewLast}
                  </p>
                </div>
                <span className="text-xs font-bold bg-gray-100 text-irongrey/60 px-2 py-1 rounded-lg">
                  {s.grade || "Grade"}
                </span>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {gradeDiscrepancyWarnings.length > 0 ? (
        <div
          data-testid="parent-portal-intake-grade-discrepancy-warning"
          className={`bg-amber-50 border rounded-2xl p-6 mb-8 ${
            attemptedSteps.Review && !gradeDiscrepancyAcknowledged
              ? "border-amber-700"
              : "border-amber-300"
          }`}
        >
          <div className="flex items-start gap-3">
            <AlertCircle size={20} className="mt-0.5 shrink-0 text-amber-700" />
            <div className="space-y-3">
              <div>
                <p className="text-sm font-bold text-amber-950">
                  Please review the grade information
                </p>
                <p className="mt-1 text-sm font-medium leading-relaxed text-amber-900">
                  The applying grade and current grade look farther apart than
                  expected. Correct the student details or acknowledge that the
                  grades are intentional before continuing.
                </p>
              </div>
              <ul className="space-y-2">
                {gradeDiscrepancyWarnings.map((warning) => (
                  <li
                    key={warning.key}
                    className="rounded-xl border border-amber-200 bg-white/70 px-3 py-2 text-xs font-semibold text-amber-950"
                  >
                    {warning.studentLabel}: applying for {warning.applyingGrade};
                    current grade is {warning.currentGrade}.
                  </li>
                ))}
              </ul>
              <label
                data-validation-key="review.gradeDiscrepancyAcknowledged"
                className="flex cursor-pointer items-start gap-3 rounded-xl bg-white/70 p-3"
              >
                <input
                  data-testid="parent-portal-intake-grade-discrepancy-acknowledgement"
                  type="checkbox"
                  checked={gradeDiscrepancyAcknowledged}
                  onChange={(event) =>
                    setGradeDiscrepancyAcknowledged(event.target.checked)
                  }
                  className="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary accent-primary focus:outline-none focus:shadow-none focus-visible:shadow-none cursor-pointer"
                />
                <span className="text-sm font-semibold leading-relaxed text-amber-950">
                  I reviewed this grade discrepancy and want to continue.
                </span>
              </label>
            </div>
          </div>
        </div>
      ) : null}

      {certificationAgreement?.visible ? (
        <div
          className={`bg-white border rounded-2xl p-6 mb-8 ${
            attemptedSteps.Review &&
            certificationAgreement.required &&
            !consentChecked
              ? "border-amber-700 bg-amber-50/40"
              : "border-[#28E2A7]"
          }`}
        >
          <div className="mb-4">
            <InputLabel required={certificationAgreement.required}>
              {certificationAgreement.label}
            </InputLabel>
          </div>
          <label className="flex items-start gap-4 cursor-pointer">
            <input
              data-testid="parent-portal-intake-review-consent-checkbox"
              data-validation-key="review.consentChecked"
              type="checkbox"
              checked={consentChecked}
              onChange={(e) => setConsentChecked(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-primary accent-primary focus:outline-none focus:shadow-none focus-visible:shadow-none cursor-pointer"
            />
            <span className="text-sm text-irongrey leading-relaxed">
              {certificationAgreement.text}
            </span>
          </label>
        </div>
      ) : null}
    </>
  );

  const renderScheduleStep = () => {
    const daysInMonth = new Date(
      currentCalendarDate.getFullYear(),
      currentCalendarDate.getMonth() + 1,
      0,
    ).getDate();
    const startDay = new Date(
      currentCalendarDate.getFullYear(),
      currentCalendarDate.getMonth(),
      1,
    ).getDay(); // 0 = Sun
    // Adjust grid for Mon start: Sun(0)->6, Mon(1)->0, Tue(2)->1
    const paddingDays = startDay === 0 ? 6 : startDay - 1;

    if (scheduleView === "success") {
      return (
        <div className="flex flex-col items-center justify-center min-h-[500px] animate-in fade-in zoom-in-95 duration-500">
          <Card className="max-w-md w-full text-center p-12 shadow-lg shadow-primary/20">
            <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <Check size={40} className="text-primary" />
            </div>
            <h2 className="text-2xl font-bold text-irongrey font-heading mb-2">
              You are scheduled
            </h2>
            <p className="text-irongrey/60 mb-8">
              Admissions interviews have been scheduled for each child below.
            </p>
            <div className="bg-gray-50 rounded-xl p-6 mb-8 text-left space-y-4">
              {schedulableStudents.map((student, index) => {
                const studentLabel =
                  readText(student.firstName) ||
                  readText(student.calledBy) ||
                  `Child ${index + 1}`;
                const scheduledUnix = interviewSlotsByStudentId[student.id];
                const scheduledDate = Number.isFinite(scheduledUnix)
                  ? new Date(scheduledUnix * 1000)
                  : null;
                const hasDate = Boolean(
                  scheduledDate && !Number.isNaN(scheduledDate.getTime()),
                );
                return (
                  <div
                    key={student.id}
                    className="rounded-xl border border-gray-100 bg-white px-4 py-3 space-y-2"
                  >
                    <p className="text-xs font-bold uppercase tracking-widest text-irongrey/40">
                      {`Admissions Interview for ${studentLabel}`}
                    </p>
                    <div className="flex items-center gap-2 text-sm font-medium text-irongrey/80">
                      <Calendar size={16} className="text-irongrey/40" />
                      <span>
                        {hasDate
                          ? scheduledDate!.toLocaleDateString("en-US", {
                              weekday: "long",
                              month: "long",
                              day: "numeric",
                            })
                          : "Pending"}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-sm font-medium text-irongrey/80">
                      <Clock size={16} className="text-irongrey/40" />
                      <span>
                        {hasDate ? toScheduleTime(scheduledDate!) : "Pending"}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-sm font-medium text-irongrey/80">
                      <MapPin size={16} className="text-irongrey/40" />
                      <span>In-person visit</span>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="flex flex-col gap-3">
              <Button
                data-testid="parent-portal-intake-finish-registration"
                className="w-full"
                onClick={() => {
                  void nextStep();
                }}
                disabled={
                  isSubmittingApplication ||
                  !intakeActionState.canFinishScheduledSubmission
                }
              >
                {isSubmittingApplication
                  ? "Submitting…"
                  : "Finish Registration"}
              </Button>
            </div>
          </Card>
        </div>
      );
    }

    return (
      <div
        data-testid="parent-portal-intake-schedule-step"
        className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500"
      >
        <div className="text-center">
          <h2
            data-testid="parent-portal-intake-schedule-heading"
            className="text-3xl font-bold text-irongrey font-heading"
          >
            One more step
          </h2>
          <p className="text-lg text-irongrey/80 mt-2">{`Admissions Interview for ${activeScheduleStudentName}`}</p>
          {schedulableStudents.length > 1 ? (
            <p className="text-xs font-semibold uppercase tracking-widest text-irongrey/40 mt-3">
              Student{" "}
              {Math.min(scheduleStudentIndex + 1, schedulableStudents.length)}{" "}
              of {schedulableStudents.length}
            </p>
          ) : null}
          <p className="max-w-2xl mx-auto text-irongrey/60 mt-4 text-sm leading-relaxed">
            All applicants must visit us for an in-person interview to be
            considered for enrollment. Please use the scheduler below to select
            a convenient time.
          </p>
        </div>

        {schedulableStudents.length > 1 ? (
          <div className="flex flex-wrap items-center justify-center gap-2">
            {schedulableStudents.map((student, index) => {
              const studentName =
                readText(student.firstName) ||
                readText(student.calledBy) ||
                `Child ${index + 1}`;
              const isActive = index === scheduleStudentIndex;
              const isScheduled = Boolean(
                interviewSlotsByStudentId[student.id],
              );
              return (
                <button
                  key={student.id}
                  onClick={() => openSchedulerForStudent(index)}
                  className={`px-4 py-2 rounded-full border text-xs font-bold tracking-wide transition-colors ${
                    isActive
                      ? "border-primary bg-primary/10 text-primary"
                      : isScheduled
                        ? "border-primary/40 bg-white text-primary"
                        : "border-gray-200 bg-white text-irongrey/70"
                  }`}
                >
                  {`Admissions Interview for ${studentName}`}
                </button>
              );
            })}
          </div>
        ) : null}

        <div className="bg-white rounded-2xl shadow-xl overflow-hidden max-w-6xl mx-auto flex flex-col md:flex-row min-h-[600px] border border-gray-100 transition-all duration-300 group">
          {/* Left Panel */}
          <div className="w-full md:w-[30%] bg-white border-r border-gray-100 p-8 flex flex-col relative transition-all duration-300">
            <div className="mt-4 mb-6">
              <div
                className="w-20 h-20 rounded-full bg-gray-200 mb-6 overflow-hidden border-2 border-white shadow-sm"
                style={{ display: "none" }}
              >
                <img
                  src="https://images.unsplash.com/photo-1560250097-0b93528c311a?w=400&h=400&fit=crop"
                  alt="Staff"
                  className="w-full h-full object-cover"
                />
              </div>
              <p
                className="text-irongrey/60 font-semibold tracking-wide text-sm mb-1 transition-colors duration-500 group-hover:text-irongrey/90"
                style={{ display: "none" }}
              >
                Rabbi Shragi Herskowitz
              </p>
              <h3 className="text-2xl font-bold text-irongrey font-heading">{`Admissions Interview for ${activeScheduleStudentName}`}</h3>
            </div>

            <div className="space-y-4 text-irongrey/60 text-sm mb-8 transition-colors duration-500 group-hover:text-irongrey/90">
              <div className="flex items-center gap-3 font-medium">
                <Clock
                  size={18}
                  className="text-irongrey/40 transition-colors duration-500 group-hover:text-irongrey/90"
                />
                <span>30 min</span>
              </div>
              <div className="flex items-center gap-3 font-medium">
                <MapPin
                  size={18}
                  className="text-irongrey/40 transition-colors duration-500 group-hover:text-irongrey/90"
                />
                <span>In-person visit</span>
              </div>
            </div>

            <div className="pt-8 border-t border-gray-100 text-xs leading-relaxed text-irongrey/60 transition-colors duration-500 group-hover:text-irongrey/90">
              <p>
                Please bring a copy of the student's most recent report card if
                you haven't uploaded it yet. We look forward to meeting your
                family!
              </p>
            </div>
          </div>

          {/* Right Panel */}
          <div className="flex-1 bg-white p-8 md:p-10 flex flex-col">
            {scheduleView === "calendar" ? (
              <>
                <h3 className="text-xl font-bold text-irongrey font-heading mb-8 text-center transition-all duration-300">
                  {selectedDate ? "Select a time" : "Select a date & time"}
                </h3>

                <div
                  className={`flex flex-col md:flex-row h-full gap-8 transition-all duration-500 ${selectedDate ? "justify-center" : ""}`}
                >
                  {/* Calendar Section */}
                  <div
                    className={`flex flex-col transition-all duration-500 ${selectedDate ? "w-full md:w-1/2 border-r border-gray-50 pr-8" : "w-full max-w-md mx-auto"}`}
                  >
                    {/* Calendar Header */}
                    <div className="flex items-center justify-center gap-8 mb-8">
                      <button
                        onClick={() => navigateMonth(-1)}
                        disabled={isSameAdmissionsCalendarMonth(
                          currentCalendarDate,
                          createDefaultCalendarDate(),
                        )}
                        className="p-2 text-irongrey/30 hover:text-irongrey hover:bg-gray-50 rounded-full transition-colors disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-irongrey/30"
                      >
                        <ChevronLeft size={20} />
                      </button>
                      <span className="text-lg font-semibold text-irongrey min-w-[140px] text-center">
                        {currentCalendarDate.toLocaleDateString("en-US", {
                          month: "long",
                          year: "numeric",
                        })}
                      </span>
                      <button
                        onClick={() => navigateMonth(1)}
                        className="p-2 text-irongrey/30 hover:text-irongrey hover:bg-gray-50 rounded-full transition-colors"
                      >
                        <ChevronRight size={20} />
                      </button>
                    </div>

                    {/* Calendar Grid */}
                    <div className="w-full">
                      <div className="grid grid-cols-7 gap-y-4 gap-x-2 mb-2">
                        {["MON", "TUE", "WED", "THU", "FRI", "SAT", "SUN"].map(
                          (day) => (
                            <div
                              key={day}
                              className="text-[10px] font-bold text-irongrey/30 text-center tracking-widest"
                            >
                              {day}
                            </div>
                          ),
                        )}
                      </div>
                      <div className="grid grid-cols-7 gap-y-2 gap-x-2">
                        {/* Padding days */}
                        {Array.from({ length: paddingDays }).map((_, i) => (
                          <div key={`pad-${i}`} />
                        ))}
                        {/* Days */}
                        {Array.from(
                          { length: daysInMonth },
                          (_, i) => i + 1,
                        ).map((day) => {
                          const isSelected = selectedDate === day;
                          const isAvailable =
                            isSelectableAdmissionsScheduleDate(
                              currentCalendarDate,
                              day,
                            );

                          return (
                            <button
                              key={day}
                              data-testid="parent-portal-intake-schedule-date"
                              data-schedule-day={day}
                              data-schedule-month={
                                currentCalendarDate.getMonth() + 1
                              }
                              data-schedule-year={
                                currentCalendarDate.getFullYear()
                              }
                              data-schedule-state={
                                isSelected
                                  ? "selected"
                                  : isAvailable
                                    ? "available"
                                    : "disabled"
                              }
                              onClick={() =>
                                (isAvailable || isSelected) &&
                                setSelectedDate(day)
                              }
                              className={`h-10 w-10 md:h-12 md:w-12 mx-auto rounded-full flex items-center justify-center text-sm font-medium transition-all duration-500 relative ${
                                isSelected
                                  ? "bg-primary text-white shadow-lg shadow-primary/20"
                                  : isAvailable
                                    ? selectedDate
                                      ? "text-primary bg-transparent font-bold"
                                      : "text-primary bg-primary/10 hover:bg-primary/20 font-bold"
                                    : "text-irongrey/30 cursor-default"
                              }`}
                              disabled={!isAvailable && !isSelected}
                            >
                              {day}
                              {day === 24 &&
                                !isSelected &&
                                currentCalendarDate.getMonth() === 11 && (
                                  <div className="absolute -bottom-1 w-1 h-1 bg-irongrey/30 rounded-full"></div>
                                )}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    {/* Timezone - Only visible when full calendar or bottom of calendar col */}
                    <div
                      ref={timezoneRef}
                      className="relative mt-auto pt-6 border-t border-gray-50"
                    >
                      <button
                        onClick={() => setTimezoneOpen(!timezoneOpen)}
                        className="flex items-center gap-2 text-sm text-irongrey/60 font-medium cursor-pointer hover:text-irongrey transition-colors w-full"
                      >
                        <Globe size={16} className="text-irongrey/40" />
                        <span>{selectedTimezone} (09:29)</span>
                        <ChevronDown
                          size={14}
                          className={`ml-auto opacity-50 transition-transform ${timezoneOpen ? "rotate-180" : ""}`}
                        />
                      </button>

                      {timezoneOpen && (
                        <div className="absolute bottom-full left-0 w-full mb-2 bg-white rounded-xl shadow-xl border border-gray-100 overflow-hidden animate-in fade-in zoom-in-95 duration-200 z-10">
                          {[
                            "Eastern Time",
                            "Central Time",
                            "Mountain Time",
                            "Pacific Time",
                            "Greenwich Mean Time",
                            "Israel Standard Time",
                          ].map((tz) => (
                            <button
                              key={tz}
                              onClick={() => {
                                setSelectedTimezone(tz);
                                setTimezoneOpen(false);
                              }}
                              className={`w-full text-left px-4 py-3 text-sm hover:bg-gray-50 transition-colors ${selectedTimezone === tz ? "text-primary font-bold bg-primary/5" : "text-irongrey/80"}`}
                            >
                              {tz}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Time Slots Section (Conditionally Rendered) */}
                  {selectedDate && (
                    <div className="w-full md:w-1/2 animate-in slide-in-from-right-4 fade-in duration-300 flex flex-col">
                      <p className="text-sm font-semibold text-irongrey mb-4 sticky top-0 bg-white py-2">
                        {new Date(
                          currentCalendarDate.getFullYear(),
                          currentCalendarDate.getMonth(),
                          selectedDate,
                        ).toLocaleDateString("en-US", {
                          weekday: "long",
                          month: "long",
                          day: "numeric",
                        })}
                      </p>
                      <div className="overflow-y-auto pr-2 scrollbar-thin flex-1 max-h-[400px]">
                        <div className="grid grid-cols-1 gap-3">
                          {[
                            "9:00am",
                            "9:30am",
                            "10:00am",
                            "10:30am",
                            "11:00am",
                            "11:30am",
                            "1:00pm",
                            "1:30pm",
                            "2:00pm",
                            "2:30pm",
                            "3:00pm",
                            "3:30pm",
                            "4:00pm",
                          ].map((time) => (
                            <button
                              key={time}
                              onClick={() => {
                                setSelectedTime(time);
                                setScheduleView("form");
                              }}
                              className="w-full py-3 border border-primary text-primary font-bold rounded-xl hover:bg-primary hover:text-white transition-all shadow-sm hover:shadow-md"
                            >
                              {time}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </>
            ) : (
              // Form View
              <div className="max-w-lg mx-auto w-full animate-in slide-in-from-right-8 fade-in duration-300">
                <button
                  onClick={() => setScheduleView("calendar")}
                  className="mb-6 flex items-center gap-2 text-irongrey/40 hover:text-irongrey transition-colors text-sm font-bold"
                >
                  <div className="w-8 h-8 rounded-full border border-gray-200 flex items-center justify-center bg-white shadow-sm">
                    <ArrowLeft size={16} />
                  </div>
                  Back
                </button>

                <h3 className="text-2xl font-bold text-irongrey font-heading mb-6">
                  Enter Details
                </h3>

                <div className="space-y-4">
                  <div data-validation-key={fieldKey("scheduleForm", "name")}>
                    <InputLabel required>Name</InputLabel>
                    <Input
                      value={scheduleForm.name}
                      onChange={(e) =>
                        setScheduleForm({
                          ...scheduleForm,
                          name: e.target.value,
                        })
                      }
                      onBlur={() =>
                        markFieldTouched(fieldKey("scheduleForm", "name"))
                      }
                      className={getFieldIndicatorClassName(
                        fieldKey("scheduleForm", "name"),
                        "Schedule",
                        scheduleForm.name,
                      )}
                    />
                  </div>
                  <div data-validation-key={fieldKey("scheduleForm", "email")}>
                    <InputLabel required>Email</InputLabel>
                    <Input
                      type="email"
                      value={scheduleForm.email}
                      onChange={(e) =>
                        setScheduleForm({
                          ...scheduleForm,
                          email: e.target.value,
                        })
                      }
                      onBlur={() =>
                        markFieldTouched(fieldKey("scheduleForm", "email"))
                      }
                      className={getFieldIndicatorClassName(
                        fieldKey("scheduleForm", "email"),
                        "Schedule",
                        scheduleForm.email,
                        { validator: isValidEmailAddress },
                      )}
                    />
                  </div>
                  <div data-validation-key={fieldKey("scheduleForm", "phone")}>
                    <InputLabel required>Phone</InputLabel>
                    <Input
                      type="tel"
                      value={scheduleForm.phone}
                      onChange={(e) =>
                        setScheduleForm({
                          ...scheduleForm,
                          phone: sanitizePhoneInput(e.target.value),
                        })
                      }
                      onBlur={() =>
                        markFieldTouched(fieldKey("scheduleForm", "phone"))
                      }
                      inputMode="tel"
                      className={getFieldIndicatorClassName(
                        fieldKey("scheduleForm", "phone"),
                        "Schedule",
                        scheduleForm.phone,
                        { validator: isValidPhoneNumber },
                      )}
                    />
                  </div>
                  <div>
                    <InputLabel>
                      Please share anything that will help prepare for our
                      meeting.
                    </InputLabel>
                    <textarea
                      className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-sm font-semibold text-irongrey focus:ring-primary focus:border-primary min-h-[100px]"
                      value={scheduleForm.notes}
                      onChange={(e) =>
                        setScheduleForm({
                          ...scheduleForm,
                          notes: e.target.value,
                        })
                      }
                    />
                  </div>
                  {firstSubmissionBlockingIssue ? (
                    <div
                      data-testid="parent-portal-intake-schedule-blocking-alert"
                      className="rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-left text-sm font-semibold text-amber-900"
                    >
                      {firstSubmissionBlockingIssue.label} is required before
                      scheduling an interview.
                    </div>
                  ) : null}
                  <Button
                    data-testid="parent-portal-intake-schedule-event"
                    className="w-full mt-4"
                    onClick={handleScheduleEvent}
                    disabled={
                      isSubmittingApplication || Boolean(firstSubmissionBlockingIssue)
                    }
                  >
                    Schedule Event
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="rounded-2xl border border-gray-100 bg-gray-50 px-5 py-4">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div className="space-y-1">
              <p className="text-sm font-bold text-irongrey">
                Need more time to pick an interview?
              </p>
              <p className="text-sm text-irongrey/60">
                Submit your application now and schedule the interview later.
              </p>
            </div>
            <Button
              data-testid="parent-portal-intake-submit-without-scheduling"
              variant="secondary"
              className="w-full md:w-auto"
              onClick={() => {
                void submitWithoutScheduling();
              }}
              disabled={
                isSubmittingApplication ||
                !intakeActionState.canSubmitWithoutScheduling
              }
            >
              {isSubmittingApplication
                ? "Submitting…"
                : "Submit Without Scheduling"}
            </Button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="max-w-5xl mx-auto p-0 md:px-6 md:pt-10 md:pb-32">
      <Modal
        isOpen={browserBackGuard.isPromptOpen}
        onClose={browserBackGuard.cancelNavigation}
        title="Leave this application form?"
        description="Browser back can clear unsaved form data."
        footer={(
          <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
            <Button variant="secondary" onClick={browserBackGuard.cancelNavigation}>
              Stay on form
            </Button>
            <Button variant="danger" onClick={browserBackGuard.confirmNavigation}>
              Leave page
            </Button>
          </div>
        )}
      >
        <p className="text-sm font-semibold leading-6 text-irongrey/70">
          Using the browser back button can clear the information you have entered.
          Use the form Back button when you want to move to an earlier step safely.
        </p>
      </Modal>
      {selectedApplicationReadOnly ? (
        <div
          data-testid="intake-selected-application-readonly-badge"
          className="mb-6 rounded-2xl border border-primary/20 bg-primary/5 px-4 py-3"
        >
          <p className="text-sm font-bold text-primary">
            {selectedApplicationCode
              ? `Viewing application ${selectedApplicationCode} (read-only)`
              : "Viewing selected application (read-only)"}
          </p>
        </div>
      ) : null}

      <fieldset
        disabled={selectedApplicationReadOnly}
        className={`border-0 p-0 m-0 min-w-0 ${selectedApplicationReadOnly ? "pointer-events-none select-none opacity-90" : ""}`}
      >
        <ProgressBar
          currentStep={currentStep}
          onStepClick={setCurrentStep}
          steps={steps}
        />

        {steps[currentStep] === "Students" && renderStudentsStep()}
        {steps[currentStep] === "Parents" && renderParentsStep()}
        {steps[currentStep] === "Extended Family" && renderGrandparentsStep()}
        {steps[currentStep] === "Documents" && renderDocumentsStep()}
        {steps[currentStep] === "Review" && renderReviewStep()}
        {steps[currentStep] === "Schedule" && renderScheduleStep()}

        {currentStep === steps.length && (
          <div className="text-center py-20 animate-in fade-in zoom-in-95 duration-500">
            <div className="w-24 h-24 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle size={48} className="text-primary" />
            </div>
            <h2 className="text-3xl font-bold text-irongrey mb-4 font-heading">
              Application Submitted!
            </h2>
            <p className="text-irongrey/60 max-w-md mx-auto">
              {pendingSubmission?.pendingPhase === "schedule"
                ? "Your application has been received, but we could not save the requested interview schedule yet."
                : pendingSubmission?.pendingPhase === "documents"
                  ? "Your application has been received, but one or more uploaded documents still need attention."
                  : submissionCompletionMode === "unscheduled"
                    ? familySelfSchedulingVisible
                      ? "Thank you for enrolling with us. Your application has been received and no interview has been scheduled yet."
                      : "Application submitted. The admissions team will follow up with next steps."
                    : "Thank you for enrolling with us. Your interview has been scheduled and a confirmation email has been sent."}
            </p>
            <div className="mt-8 flex flex-col items-center gap-3">
              <a
                href="/parent/portal/applications"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-primary text-white font-bold text-sm hover:opacity-90 transition-opacity"
                data-testid="intake-view-applications-link"
              >
                <ArrowLeft size={16} />
                View My Applications
              </a>
              {pendingSubmission ? (
                <div className="flex flex-wrap items-center justify-center gap-3">
                  <Button
                    type="button"
                    variant="secondary"
                    data-testid="parent-portal-intake-retry-follow-up"
                    onClick={() => {
                      void retryPendingSubmission();
                    }}
                    disabled={isSubmittingApplication}
                  >
                    {isSubmittingApplication
                      ? "Retrying…"
                      : pendingSubmission.pendingPhase === "schedule"
                        ? "Retry Interview Scheduling"
                        : "Retry Document Upload"}
                  </Button>
                  {submissionAlert?.applicationId ? (
                    <a
                      href={buildSubmittedApplicationPath(
                        submissionAlert.applicationId,
                      )}
                      className="inline-flex items-center gap-2 px-6 py-3 rounded-xl border border-primary/20 bg-white text-primary font-bold text-sm hover:border-primary/40 hover:bg-primary/5 transition-colors"
                      data-testid="parent-portal-intake-view-submitted-application"
                    >
                      View Submitted Application
                    </a>
                  ) : null}
                </div>
              ) : null}
            </div>
          </div>
        )}
      </fieldset>

      {showAutofillFab ? (
        <button
          type="button"
          data-testid="parent-portal-intake-autofill-fab"
          onClick={applyAutofillDraft}
          title="Fill the admissions intake with synthetic test data"
          className="fixed bottom-5 right-5 z-[60] inline-flex items-center gap-3 rounded-full border border-[#1D5D50]/15 bg-[#1D5D50] px-4 py-3 text-white shadow-[0_18px_40px_rgba(29,93,80,0.32)] transition-all duration-200 hover:-translate-y-0.5 hover:bg-[#184B41] focus:outline-none focus:ring-4 focus:ring-[#1D5D50]/20 md:bottom-8 md:right-8"
        >
          <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-white/12">
            <CheckSquare size={18} />
          </span>
          <span className="text-left">
            <span className="block text-[10px] font-bold uppercase tracking-[0.24em] text-white/70">
              Dev
            </span>
            <span className="block text-sm font-bold leading-tight">
              Fill Test Draft
            </span>
          </span>
        </button>
      ) : null}

      <div className="border-t border-gray-100 mb-8 mt-12"></div>
      {submissionAlert ? (
        <div
          className={`mb-6 rounded-xl border px-4 py-3 ${
            submissionAlert.tone === "warning"
              ? "border-amber-300 bg-amber-50 text-amber-900"
              : "border-strawberry/30 bg-strawberry/5 text-strawberry"
          }`}
          data-testid="parent-portal-intake-submission-alert"
        >
          {submissionAlert.tone === "warning" ? null : (
            <EngineeringSupportBanner compact className="mb-3" />
          )}
          <p className="text-sm font-bold">{submissionAlert.title}</p>
          <p className="mt-1 text-sm font-medium">{submissionAlert.detail}</p>
          {submissionAlert.applicationCode ? (
            <p className="mt-2 text-xs font-semibold uppercase tracking-wide opacity-80">
              Application code: {submissionAlert.applicationCode}
            </p>
          ) : null}
        </div>
      ) : null}

      {selectedApplicationReadOnly ? (
        <div className="flex items-center justify-between">
          <button
            className="text-irongrey font-semibold text-sm hover:text-irongrey/70 transition-colors"
            onClick={() => {
              window.location.assign("/parent/portal/applications");
            }}
          >
            Cancel & Exit
          </button>
          <span className="text-xs font-bold text-irongrey/40 uppercase tracking-widest">
            Read-only application view
          </span>
        </div>
      ) : (
        <>
          {showStepValidationAlert ? (
            <div
              data-testid="parent-portal-intake-step-validation-alert"
              id="parent-portal-intake-step-validation-alert"
              role="alert"
              aria-live="assertive"
              className="mb-6 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-amber-950"
            >
              <div className="flex items-start gap-3">
                <AlertCircle
                  size={18}
                  className="mt-0.5 shrink-0 text-amber-800"
                />
                <div className="min-w-0">
                  <p className="text-sm font-bold">
                    Complete the highlighted fields to continue.
                  </p>
                  <ul className="mt-2 space-y-1 text-sm font-medium">
                    {visibleAdvanceIssues.map((issue) => (
                      <li key={issue.key}>{issue.label}</li>
                    ))}
                    {hiddenAdvanceIssueCount > 0 ? (
                      <li>+{hiddenAdvanceIssueCount} more</li>
                    ) : null}
                  </ul>
                </div>
              </div>
            </div>
          ) : null}

          <div className="flex flex-col items-stretch gap-4 md:flex-row md:items-center md:justify-between">
            {currentStep > 0 &&
            currentStep < steps.length &&
            showFooterNavigation ? (
              <button
                onClick={prevStep}
                className="flex w-full items-center justify-center gap-2 px-6 py-3 border border-gray-200 rounded-xl text-irongrey font-semibold text-sm hover:bg-gray-50 transition-colors md:w-auto"
              >
                <ChevronLeft size={16} />
                Back
              </button>
            ) : (
              currentStep < steps.length &&
              showFooterNavigation && (
                <button
                  className="order-2 w-full text-irongrey font-semibold text-sm hover:text-irongrey/70 transition-colors md:order-none md:w-auto"
                  onClick={() => {
                    window.location.assign("/parent/portal/applications");
                  }}
                >
                  Cancel & Exit
                </button>
              )
            )}

            {currentStep < steps.length && showFooterNavigation && (
              <div className="order-1 flex flex-col items-stretch gap-4 md:order-none md:flex-row md:items-center md:gap-6">
                {currentStep < steps.length - 1 && (
                  <span
                    className={`text-xs font-medium hidden sm:inline ${
                      draftFeedback.state === "error"
                        ? "text-strawberry"
                        : draftFeedback.state === "saving" ||
                            isSavingParentAdmissionsDraft
                          ? "text-irongrey/55"
                          : "text-irongrey/30"
                    }`}
                    data-testid="parent-portal-intake-draft-status"
                  >
                    {draftFeedback.message}
                  </span>
                )}
                <Button
                  type="button"
                  variant="secondary"
                  data-testid="parent-portal-intake-discard-draft"
                  className="w-full justify-center !px-5 !py-3 !shadow-none md:w-auto"
                  onClick={() => {
                    void discardParentAdmissionsDraft();
                  }}
                  disabled={
                    isSubmittingApplication ||
                    isDeletingParentAdmissionsDraft ||
                    isSavingParentAdmissionsDraft
                  }
                >
                  {isDeletingParentAdmissionsDraft ||
                  draftFeedback.state === "discarding"
                    ? "Discarding..."
                    : "Discard Draft"}
                </Button>
                {steps[currentStep] === "Schedule" ? (
                  <Button
                    variant="primary"
                    className="!px-8 !py-3 shadow-xl !bg-[#237062] hover:!bg-[#28E2A7] !text-[1.1rem] !font-bold transition-all duration-300 flex w-full items-center justify-center gap-3 opacity-0 pointer-events-none md:w-auto"
                    onClick={() => {
                      void nextStep();
                    }}
                  >
                    {/* Hidden on Step 5 until success */}
                    Finish Registration
                    <CheckCircle size={20} />
                  </Button>
                ) : (
                  <Button
                    data-testid="parent-portal-intake-save-continue"
                    variant="primary"
                    className={`w-full justify-center !px-8 !py-3 !shadow-none md:w-auto ${
                      primaryActionBlocked
                        ? "!cursor-not-allowed !bg-gray-200 !text-irongrey/45 hover:!bg-gray-200 hover:!text-irongrey/45"
                        : ""
                    }`}
                    onClick={() => {
                      void nextStep();
                    }}
                    aria-disabled={primaryActionBlocked}
                    aria-describedby={
                      showStepValidationAlert
                        ? "parent-portal-intake-step-validation-alert"
                        : undefined
                    }
                    data-validation-blocked={primaryActionBlocked}
                    title={
                      primaryActionBlocked
                        ? "Complete the highlighted fields to continue"
                        : undefined
                    }
                    disabled={isSubmittingApplication}
                  >
                    {isDisabledSchedulingReviewSubmit
                      ? isSubmittingApplication
                        ? "Submitting…"
                        : "Submit Application"
                      : "Save & Continue"}
                    {isDisabledSchedulingReviewSubmit ? (
                      <CheckCircle size={16} />
                    ) : (
                      <ChevronRight size={16} />
                    )}
                  </Button>
                )}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default StudentForm;
